import FutureTimeLabel from './FutureTimeLabel.jsx'
import { useWorkspaceDailySource } from './WorkspaceDailySource.jsx'
import ChartDrawings from './relativeStrength/ChartDrawings.jsx'
import {ALERT_APPEARANCE} from '../../lib/priceAlerts/appearance.js'
import PriceAlertLines from '../priceAlerts/PriceAlertLines.jsx'
import { coordinateToChartTime } from '../../lib/chart/chartTimeProjection.js'
import { resolveAnchoredVwapAppearances } from '../../lib/chart/sharedTimeframeProfiles.js'
import { chartTimeLabelFormatter } from '../../lib/chart/chartTimeLabel.js'
import { useSharedTimeframeProfiles } from '../../hooks/useSharedTimeframeProfiles.js'
import { useAuth } from '../../hooks/useAuth.jsx'
import { usePrivacy } from '../../hooks/usePrivacy.jsx'
import { CurrentPriceLabelPrimitive } from '../../lib/chart/CurrentPriceLabelPrimitive.js'
import { usePriceAlertCrosshair } from '../priceAlerts/usePriceAlertCrosshair.js'
import { useChartNavigationCommands } from './useChartNavigationCommands.js'
import { useDynamicCanvas } from './useDynamicCanvas.js'
import { DYNAMIC_CANVAS_TIMEFRAMES } from '../../lib/chart/dynamicCanvas.js'
import { CHART_MARGIN_TIMEFRAME_LABELS, DEFAULT_CHART_MARGINS_BY_TIMEFRAME, normalizeChartMarginSlot, normalizeChartMarginsByTimeframe, chartMarginsForTimeframe, applyDefaultVisibleLogicalRange } from '../../lib/chart/chartMargins.js'
import { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { applySyncedCrosshair, clearSyncedCrosshairOverlay } from '../../lib/chart/crosshairSync.js'
import { RotateCcw, WifiOff } from 'lucide-react'
// CE-04c-2-fix-3: LWC v5 import via compat shim. Real multi-pane support
// (chart.addPane() + positional paneIndex on addSeries) ships with v5; the
// shim re-attaches the v4 convenience method names (addLineSeries / etc.)
// that v5 removed, so this chassis can keep calling addHistogramSeries /
// addLineSeries / addAreaSeries while opting into v5's positional paneIndex.
import { createChart, createSeriesMarkers, CrosshairMode, LineStyle } from '../../lib/chart/createChartCompat'
// CE-04g-UI-chrome-1 — workspace-theme design tokens (single source of
// truth for chart visual styling). See chartTokens.js for token surface.
import {
  CHART_LAYOUT_WORKSPACE,
  CHART_GRID_WORKSPACE,
  CHART_CROSSHAIR_WORKSPACE,
  CHART_RIGHT_PRICE_SCALE_WORKSPACE,
  CHART_RIGHT_PRICE_SCALE_MIN_WIDTH,
  CHART_TIME_SCALE_WORKSPACE,
  SERIES_COLORS_WORKSPACE,
  BORDER_COLOR_RGBA,
  BORDER_COLOR_RGBA_HOVER,
} from './chartTokens.js'
// JSDoc-only import for typedef discoverability in IDEs.
import './types.js'
import { attachVolumeReadout } from '../../lib/chart/volumeReadout.js'
import { observeChartPaneLayout } from '../../lib/chart/chartPaneLayout.js'
import { stablePriceScaleWidth } from '../../lib/chart/stablePriceScaleWidth.js'
import IndicatorAccordion, { AVAILABLE_INDICATORS } from './IndicatorAccordion.jsx'
import { MULTI_INSTANCE_KINDS } from './indicatorRules.js'
import IndicatorPopover from './IndicatorPopover.jsx'
import AnchoredVwapLayer from './AnchoredVwapLayer.jsx'
import { useIndicatorTemplateLibrary } from '../../hooks/useIndicatorTemplateLibrary.js'
import ChartSettingsModal from './ChartSettingsModal.jsx'
import EarningsLayer from './EarningsLayer.jsx'
import { useEarningsVwapAnchor } from '../../hooks/useEarningsVwapAnchor.js'
import { computeEarningsVwap } from '../../lib/chart/earningsVwap.js'
import {
  createIndicatorFromTemplate,
} from '../../lib/chart/indicatorTemplates.js'
import {
  CHART_PREFERENCE_KEYS,
  readServerChartPreference,
  resolvePreferenceUserId,
  writeServerChartPreference,
} from '../../lib/chart/chartPreferenceStore.js'
import {
  computeSMA,
  computeEMA,
  computeKeltner,
  computeBollinger,
  computeDonchian,
  computeVolumeBarColors,
  applyOpacityToColor,
} from '../../lib/chart/computeIndicator.js'
import { computeSessionVWAP } from '../../lib/chart/computeSessionVWAP.js'
import {
  computeWindowVWAP,
  filterBarsTo1DayWindow,
  filterBarsTo2DayWindow,
  filterBarsToWtdWindow,
  filterBarsToMtdWindow,
  filterBarsToYtdWindow,
} from '../../lib/chart/vwapWindow.js'
import { computePivotLevels } from '../../lib/chart/computePivots.js'
// CE-04g-UI-pivots-popover — pure helpers for the prior-trading-day daily
// OHLC source and axis-label dedupe pass.
import {
  pickPriorTradingDayDailyBar,
  pickPriorTradingDayDailyBarCandidate,
} from '../../lib/chart/dailyPivotSource.js'
import { pickVisibleAxisLabels } from '../../lib/chart/pivotAxisDedupe.js'
// CE-04g-UI-pivots-iseriesprimitive-refactor — pivots render through a
// single PivotPrimitive attached to the candle series via attachPrimitive.
// React-side helpers below are only needed by Effect 2.5 to compute the
// per-pass payload (session anchor, segment endpoint, RTH close anchor).
import {
  findCurrentSessionStartTime,
  nextRthOpenChartTime,
  rthCloseChartTime,
} from '../../lib/chart/pivotOverlayRenderer.js'
import { BandFillPrimitive } from '../../lib/chart/BandFillPrimitive.js'
import { PivotPrimitive } from '../../lib/chart/PivotPrimitive.js'
import { updateChartSeries } from '../../lib/marketData/marketHealthSeries.js'
import { CarsRegimePrimitive, buildCarsRegimeSpans, buildSignalRegimeSpans } from '../../lib/chart/CarsRegimePrimitive.js'
import { SessionBackgroundPrimitive, buildSessionBackgroundSpans } from '../../lib/chart/SessionBackgroundPrimitive.js'
import { FixedOhlcBarPrimitive } from '../../lib/chart/FixedOhlcBarPrimitive.js'
import { ExecutionMarkerPrimitive } from '../../lib/chart/ExecutionMarkerPrimitive.js'
import { QuickMeasurePrimitive } from '../../lib/chart/QuickMeasurePrimitive.js'
import {
  alignCarsPrimaryBarsToChartBars,
  buildCarsIndicatorPayload,
} from '../../lib/chart/carsEnginePayload.js'
// CE-04g-UI-pivots-parity-diagnostic — read-only debug readout. Silent
// unless `localStorage.erevis_pivot_debug === '1'`.
import {
  backCalcPriorOhlcFromPivots,
  emitPivotDebug,
} from '../../lib/chart/pivotDebug.js'
// CE-04g-UI-indicators-live-tick — pure helpers consumed by Effect 3
// (the live-tick data pump) to bridge in-memory bar state into the
// indicator compute layer without a settled-bar boundary fire.
import { synthesizeFormingBar, composeEffectiveBars, reconcileRuntimeCandle } from '../../lib/chart/formingBar.js'
import {
  buildChartExportSnapshot,
  createChartExportId,
  isChartExportDebugEnabled,
  registerChartExportSnapshot,
} from '../../lib/chart/chartExportDebug.js'
// CE-04g-UI-a — live-mode data layer.
import {
  IBKR_TF,
  toCandles,
} from '../../lib/candleStore.js'
import { useCandleStore } from '../../hooks/useCandleStore.js'
import { useTradingCalendar } from '../../hooks/useTradingCalendar.js'
import { useDailyPivotSource, needsDailyPivotSource } from '../../hooks/useDailyPivotSource.js'
import { useCarsAdjustedDailySource } from '../../hooks/useCarsAdjustedDailySource.js'
import { ibkrStream } from '../../lib/ibkrStream.js'
import {
  EQUITY_STREAM_TIMEFRAMES,
  equityStreamBarSizeForTimeframe,
  subscribeEquityStream,
} from '../../lib/marketData/equityStream.js'
import { subscribeIbkrChartStream } from '../../lib/marketData/ibkrChartAdapter.js'
import { getMarketSession } from '../../lib/marketSession.js'
import { buildQuoteSessionPriceLine } from '../../lib/marketData/quoteSessionPresentation.js'
import { utcToChartTimestamp, floorToBarPeriod } from '../../lib/timezone.js'
import {
  getEtSessionDateKey,
  getPreviousEtTradingDayKey,
} from '../../lib/chart/sessionBoundary.js'
import { parseOCC, isOCCSymbol } from '../../lib/occParser.js'
import { getTickInstant } from '../../lib/chart/getTickInstant.js'
import { supabase } from '../../lib/supabase.js'
import { executionMatchesStopPrice } from '../../lib/trancheStopMatcher.js'
import AccountName from '../AccountName.jsx'
import PrivateValue from '../PrivateValue.jsx'

const DEFAULT_CHART_STORAGE_SCOPE = 'position'
const MIN_VISIBLE_LOGICAL_BARS = 10
const MAX_VISIBLE_LOGICAL_RIGHT_OFFSET = 300
const VISIBLE_RANGE_PREFERENCE_VERSION = 2
const MAX_STORED_LOGICAL_RANGE_BARS = 1200
const STORED_LOGICAL_RANGE_RIGHT_OFFSET_TOLERANCE = 30
const LIVE_VIEWPORT_SETTLE_MS = 750

const CHART_SETTINGS_BASE = Object.freeze({
  ...ALERT_APPEARANCE,
  indicatorDropdownVisible: true,
  scaleControlsVisible: true,
  volumeReadoutVisible: true,
  vwapLabelsVisible: true,
  preMarketPriceVisible: true,
  postMarketPriceVisible: true,
  overnightPriceVisible: true,
  crosshairPriceLabelColor: null,
  dateLabelColor: null,
  currentPriceLabelCustom: false,
  currentPriceLabelColor: '#D1D5DB',
  crosshairVisible: true,
  crosshairVerticalVisible: true,
  crosshairHorizontalVisible: true,
  crosshairVerticalColor: '#A2A2A2',
  crosshairHorizontalColor: '#A2A2A2',
  crosshairVerticalOpacity: 32,
  crosshairHorizontalOpacity: 32,
  crosshairVerticalWidth: 1,
  crosshairHorizontalWidth: 1,
  crosshairVerticalStyle: 2,
  crosshairHorizontalStyle: 2,
  thickOhlcBars: true,
  colorBarsBasedOnPreviousClose: false,
  matchAppTheme: true,
  canvasBackgroundColor: '#111318',
  canvasBackgroundOpacity: 100,
  canvasTextColor: '#A2A2A2',
  canvasTextOpacity: 65,
  canvasGridVisible: false,
  canvasGridColor: '#424242',
  canvasGridOpacity: 45,
  extendedHoursBackgroundColor: '#64748B',
  extendedHoursBackgroundOpacity: 18,
  quickMeasureUpColor: '#2962FF',
  quickMeasureUpOpacity: 18,
  quickMeasureDownColor: '#F23645',
  quickMeasureDownOpacity: 18,
  barUpOpacity: 100,
  barDownOpacity: 100,
  candleBodyVisible: true,
  candleBodyUpColor: SERIES_COLORS_WORKSPACE.up,
  candleBodyDownColor: SERIES_COLORS_WORKSPACE.down,
  candleBodyUpOpacity: 100,
  candleBodyDownOpacity: 100,
  candleBorderVisible: true,
  candleBorderUpColor: SERIES_COLORS_WORKSPACE.up,
  candleBorderDownColor: SERIES_COLORS_WORKSPACE.down,
  candleBorderUpOpacity: 100,
  candleBorderDownOpacity: 100,
  candleWickVisible: true,
  candleWickUpColor: SERIES_COLORS_WORKSPACE.up,
  candleWickDownColor: SERIES_COLORS_WORKSPACE.down,
  candleWickUpOpacity: 100,
  candleWickDownOpacity: 100,
  positionEntryColor: '#468278',
  positionEntryOpacity: 70,
  positionStopColor: '#E05A4A',
  positionStopOpacity: 90,
  positionTargetColor: '#E05A4A',
  positionTargetOpacity: 85,
  positionAvgColor: '#FFFDE7',
  positionAvgOpacity: 70,
})

const DEFAULT_CHART_GEOMETRY_SETTINGS = Object.freeze({
  marginsByTimeframe: DEFAULT_CHART_MARGINS_BY_TIMEFRAME,
})

const DEFAULT_CHART_SETTINGS_BY_THEME = Object.freeze({
  dark: Object.freeze({
    ...CHART_SETTINGS_BASE,
    barUpColor: '#D1D5DB',
    barDownColor: '#D1D5DB',
  }),
  light: Object.freeze({
    ...CHART_SETTINGS_BASE,
    currentPriceLabelColor: '#000000',
    canvasBackgroundColor: '#FFFFFF',
    canvasTextColor: '#4B5563',
    canvasTextOpacity: 80,
    canvasGridColor: '#CBD5E1',
    extendedHoursBackgroundColor: '#CBD5E1',
    extendedHoursBackgroundOpacity: 35,
    barUpColor: '#000000',
    barDownColor: '#000000',
    positionEntryColor: '#227060',
    positionEntryOpacity: 78,
    positionStopColor: '#CD2D26',
    positionStopOpacity: 88,
    positionTargetColor: '#CD2D26',
    positionTargetOpacity: 88,
    positionAvgColor: '#374151',
    positionAvgOpacity: 82,
  }),
})

const DEFAULT_CHART_SETTINGS = DEFAULT_CHART_SETTINGS_BY_THEME.dark

function currentVisualTheme() {
  if (typeof document === 'undefined') return 'dark'
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

function clampNumber(value, fallback, min, max) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return fallback
  return Math.min(max, Math.max(min, numeric))
}

function chartScaleMargins(margins) {
  return { top: margins.top, bottom: margins.bottom }
}

// Presentation only: retain session-price authority even while its overlay is hidden.
function sessionPriceVisibility(session, settings) {
  const key = { pre: 'preMarketPriceVisible', post: 'postMarketPriceVisible', overnight: 'overnightPriceVisible' }[session]
  const visible = settings[key] !== false
  return { lineVisible: visible, axisLabelVisible: visible }
}

function normalizeChartSettings(value, theme = 'dark') {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : null
  const merged = {
    ...DEFAULT_CHART_SETTINGS_BY_THEME[theme],
    ...(source || null),
  }
  const { marginsByTimeframe: _migratedGeometry, ...themeSettings } = merged
  return themeSettings
}

function normalizeChartGeometrySettings(value) {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : DEFAULT_CHART_GEOMETRY_SETTINGS
  return {
    marginsByTimeframe: normalizeChartMarginsByTimeframe(source?.marginsByTimeframe),
    markerRange: ['30d', '6m', '1y', 'all'].includes(source?.markerRange) ? source.markerRange : null,
    earningsVisible: source?.earningsVisible !== false,
    executionMarkersVisible: source?.executionMarkersVisible !== false,
    autoScale: source?.autoScale !== false,
    logScale: source?.logScale !== false,
  }
}

function pickStoredChartGeometrySource(parsed) {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  if (parsed.marginsByTimeframe || 'executionMarkersVisible' in parsed || 'markerRange' in parsed || 'autoScale' in parsed || 'logScale' in parsed) return parsed

  const activeTheme = currentVisualTheme()
  if (parsed[activeTheme]?.marginsByTimeframe) return parsed[activeTheme]
  if (parsed.dark?.marginsByTimeframe) return parsed.dark
  if (parsed.light?.marginsByTimeframe) return parsed.light
  return parsed
}

function readStoredChartPreferences() {
  const defaults = {
    settingsByTheme: {
      dark: normalizeChartSettings(null, 'dark'),
      light: normalizeChartSettings(null, 'light'),
    },
    geometrySettings: normalizeChartGeometrySettings(null),
  }
  return defaults
}

function normalizeChartPreferencesPayload(value) {
  const defaults = readStoredChartPreferences()
  const parsed = value && typeof value === 'object' && !Array.isArray(value) ? value : null
  if (!parsed) return defaults
  if (parsed.dark || parsed.light) {
    return {
      settingsByTheme: {
        dark: normalizeChartSettings(parsed.dark, 'dark'),
        light: normalizeChartSettings(parsed.light, 'light'),
      },
      geometrySettings: normalizeChartGeometrySettings(pickStoredChartGeometrySource(parsed)),
    }
  }
  return {
    settingsByTheme: {
      dark: normalizeChartSettings(parsed, 'dark'),
      light: normalizeChartSettings(parsed, 'light'),
    },
    geometrySettings: normalizeChartGeometrySettings(parsed),
  }
}

function chartSettingsPreferencePayload(chartGeometrySettings, chartSettingsByTheme) {
  return {
    version: 1,
    settings: {
      ...chartGeometrySettings,
      ...chartSettingsByTheme,
    },
  }
}

function paneRatioPreferencePayload(paneRatio) {
  return {
    version: 1,
    value: paneRatio,
  }
}

function chartSettingsColor(settings, colorKey, opacityKey, fallback) {
  return applyOpacityToColor(
    settings?.[colorKey] || fallback,
    settings?.[opacityKey] ?? 100
  )
}

function quickMeasureStyle(settings) {
  const upColor = settings?.quickMeasureUpColor || '#2962FF'
  const downColor = settings?.quickMeasureDownColor || '#F23645'
  return {
    upColor,
    downColor,
    upFillColor: chartSettingsColor(settings, 'quickMeasureUpColor', 'quickMeasureUpOpacity', '#2962FF'),
    downFillColor: chartSettingsColor(settings, 'quickMeasureDownColor', 'quickMeasureDownOpacity', '#F23645'),
  }
}

export function chartCanvasOptions(settings) {
  if (settings?.matchAppTheme !== false) {
    return {
      layout: CHART_LAYOUT_WORKSPACE,
      grid: CHART_GRID_WORKSPACE,
      scaleTextColor: CHART_RIGHT_PRICE_SCALE_WORKSPACE.textColor,
    }
  }

  const backgroundColor = chartSettingsColor(
    settings,
    'canvasBackgroundColor',
    'canvasBackgroundOpacity',
    '#111318'
  )
  const textColor = chartSettingsColor(
    settings,
    'canvasTextColor',
    'canvasTextOpacity',
    '#A2A2A2'
  )
  const gridColor = chartSettingsColor(
    settings,
    'canvasGridColor',
    'canvasGridOpacity',
    '#424242'
  )
  const gridVisible = settings?.canvasGridVisible === true

  return {
    layout: {
      ...CHART_LAYOUT_WORKSPACE,
      background: { type: 'solid', color: backgroundColor },
      textColor,
    },
    grid: {
      vertLines: { visible: gridVisible, color: gridColor },
      horzLines: { visible: gridVisible, color: gridColor },
    },
    scaleTextColor: textColor,
  }
}

const TRANSPARENT_PRICE_COLOR = 'rgba(0,0,0,0)'

function isUpPriceBar(bar, previousBar, colorBasedOnPreviousClose) {
  if (!bar) return true
  const close = Number(bar.close)
  const open = Number(bar.open)
  const previousClose = Number(previousBar?.close)
  if (colorBasedOnPreviousClose && Number.isFinite(close) && Number.isFinite(previousClose)) {
    return close >= previousClose
  }
  if (Number.isFinite(close) && Number.isFinite(open)) return close >= open
  return true
}

function barSeriesOptions(chartSettings) {
  return {
    upColor: chartSettingsColor(chartSettings, 'barUpColor', 'barUpOpacity', SERIES_COLORS_WORKSPACE.barUp),
    downColor: chartSettingsColor(chartSettings, 'barDownColor', 'barDownOpacity', SERIES_COLORS_WORKSPACE.barDown),
    thinBars: false,
    openVisible: true,
  }
}

function candleSeriesOptions(chartSettings) {
  return {
    upColor: chartSettings.candleBodyVisible
      ? chartSettingsColor(chartSettings, 'candleBodyUpColor', 'candleBodyUpOpacity', SERIES_COLORS_WORKSPACE.up)
      : TRANSPARENT_PRICE_COLOR,
    downColor: chartSettings.candleBodyVisible
      ? chartSettingsColor(chartSettings, 'candleBodyDownColor', 'candleBodyDownOpacity', SERIES_COLORS_WORKSPACE.down)
      : TRANSPARENT_PRICE_COLOR,
    borderVisible: chartSettings.candleBorderVisible,
    borderUpColor: chartSettingsColor(chartSettings, 'candleBorderUpColor', 'candleBorderUpOpacity', SERIES_COLORS_WORKSPACE.up),
    borderDownColor: chartSettingsColor(chartSettings, 'candleBorderDownColor', 'candleBorderDownOpacity', SERIES_COLORS_WORKSPACE.down),
    wickVisible: chartSettings.candleWickVisible,
    wickUpColor: chartSettingsColor(chartSettings, 'candleWickUpColor', 'candleWickUpOpacity', SERIES_COLORS_WORKSPACE.up),
    wickDownColor: chartSettingsColor(chartSettings, 'candleWickDownColor', 'candleWickDownOpacity', SERIES_COLORS_WORKSPACE.down),
  }
}

function stylePriceBar(bar, previousBar, chartType, chartSettings) {
  if (!bar) return bar
  if (chartType === 'line') return { ...bar, value: bar.close }
  if (!chartSettings.colorBarsBasedOnPreviousClose) return bar
  const up = isUpPriceBar(bar, previousBar, chartSettings.colorBarsBasedOnPreviousClose)
  if (chartType === 'bar') {
    return {
      ...bar,
      color: up
        ? chartSettingsColor(chartSettings, 'barUpColor', 'barUpOpacity', SERIES_COLORS_WORKSPACE.barUp)
        : chartSettingsColor(chartSettings, 'barDownColor', 'barDownOpacity', SERIES_COLORS_WORKSPACE.barDown),
    }
  }
  return {
    ...bar,
    color: chartSettings.candleBodyVisible
      ? (up
          ? chartSettingsColor(chartSettings, 'candleBodyUpColor', 'candleBodyUpOpacity', SERIES_COLORS_WORKSPACE.up)
          : chartSettingsColor(chartSettings, 'candleBodyDownColor', 'candleBodyDownOpacity', SERIES_COLORS_WORKSPACE.down))
      : TRANSPARENT_PRICE_COLOR,
    borderColor: chartSettings.candleBorderVisible
      ? (up
          ? chartSettingsColor(chartSettings, 'candleBorderUpColor', 'candleBorderUpOpacity', SERIES_COLORS_WORKSPACE.up)
          : chartSettingsColor(chartSettings, 'candleBorderDownColor', 'candleBorderDownOpacity', SERIES_COLORS_WORKSPACE.down))
      : TRANSPARENT_PRICE_COLOR,
    wickColor: chartSettings.candleWickVisible
      ? (up
          ? chartSettingsColor(chartSettings, 'candleWickUpColor', 'candleWickUpOpacity', SERIES_COLORS_WORKSPACE.up)
          : chartSettingsColor(chartSettings, 'candleWickDownColor', 'candleWickDownOpacity', SERIES_COLORS_WORKSPACE.down))
      : TRANSPARENT_PRICE_COLOR,
  }
}

function closeLineOptions(chartSettings) {
  return { color: chartSettingsColor(chartSettings, 'barUpColor', 'barUpOpacity', SERIES_COLORS_WORKSPACE.barUp),
    lineWidth: 1, pointMarkersVisible: true, pointMarkersRadius: 3, crosshairMarkerVisible: true }
}

function stylePriceBars(bars, chartType, chartSettings) {
  if (!Array.isArray(bars)) return bars
  return bars.map((bar, idx) => stylePriceBar(bar, bars[idx - 1], chartType, chartSettings))
}

function previousRenderedBarForUpdate(bars, barUpdate) {
  if (!Array.isArray(bars) || !bars.length || !barUpdate) return null
  const last = bars[bars.length - 1]
  if (last?.time === barUpdate.time) return bars[bars.length - 2] || null
  return last
}

function visibleLogicalRangesEqual(a, b) {
  if (!a || !b) return false
  return Number(a.from) === Number(b.from) && Number(a.to) === Number(b.to)
}

function readVisibleLogicalRange(chart) {
  const timeScale = chart?.timeScale?.()
  if (!timeScale || typeof timeScale.getVisibleLogicalRange !== 'function') return null
  try { return timeScale.getVisibleLogicalRange() } catch { return null }
}

function restoreVisibleLogicalRange(chart, range, { force = false } = {}) {
  if (!range) return false
  const timeScale = chart?.timeScale?.()
  if (!timeScale || typeof timeScale.setVisibleLogicalRange !== 'function') return false
  if (!force && visibleLogicalRangesEqual(readVisibleLogicalRange(chart), range)) return false
  try {
    timeScale.setVisibleLogicalRange(range)
    return true
  } catch {
    return false
  }
}

function updateLivePriceBar(chart, series, bar, preserveVisibleRange = false) {
  if (!series || !bar) return
  const visibleLogicalRange = preserveVisibleRange ? readVisibleLogicalRange(chart) : null
  series.update(bar)
  restoreVisibleLogicalRange(chart, visibleLogicalRange, { force: true })
}

function priceBarStyleKey(chartSettings) {
  if (!chartSettings?.colorBarsBasedOnPreviousClose) return 'series-options'
  return JSON.stringify({
    chartPreviousClose: true,
    barUpColor: chartSettings.barUpColor,
    barDownColor: chartSettings.barDownColor,
    barUpOpacity: chartSettings.barUpOpacity,
    barDownOpacity: chartSettings.barDownOpacity,
    candleBodyVisible: chartSettings.candleBodyVisible,
    candleBodyUpColor: chartSettings.candleBodyUpColor,
    candleBodyDownColor: chartSettings.candleBodyDownColor,
    candleBodyUpOpacity: chartSettings.candleBodyUpOpacity,
    candleBodyDownOpacity: chartSettings.candleBodyDownOpacity,
    candleBorderVisible: chartSettings.candleBorderVisible,
    candleBorderUpColor: chartSettings.candleBorderUpColor,
    candleBorderDownColor: chartSettings.candleBorderDownColor,
    candleBorderUpOpacity: chartSettings.candleBorderUpOpacity,
    candleBorderDownOpacity: chartSettings.candleBorderDownOpacity,
    candleWickVisible: chartSettings.candleWickVisible,
    candleWickUpColor: chartSettings.candleWickUpColor,
    candleWickDownColor: chartSettings.candleWickDownColor,
    candleWickUpOpacity: chartSettings.candleWickUpOpacity,
    candleWickDownOpacity: chartSettings.candleWickDownOpacity,
  })
}

function mergeThemeStyle(base, themePatch) {
  if (!themePatch || typeof themePatch !== 'object' || Array.isArray(themePatch)) {
    return base || {}
  }
  const next = { ...(base || {}) }
  for (const [key, value] of Object.entries(themePatch)) {
    if (
      value
      && typeof value === 'object'
      && !Array.isArray(value)
      && next[key]
      && typeof next[key] === 'object'
      && !Array.isArray(next[key])
    ) {
      next[key] = mergeThemeStyle(next[key], value)
    } else {
      next[key] = value
    }
  }
  return next
}

function indicatorSectionStyle(ind, sectionKey, visualTheme) {
  return mergeThemeStyle(
    ind?.style?.[sectionKey] || {},
    ind?.style?.themeStyles?.[visualTheme]?.[sectionKey]
  )
}

function useVisualTheme() {
  const [visualTheme, setVisualTheme] = useState(currentVisualTheme)

  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return undefined
    const sync = () => setVisualTheme(currentVisualTheme())
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  return visualTheme
}

function normalizeVisibleTimeRange(range) {
  if (!range) return null
  const from = Number(range.from)
  const to = Number(range.to)
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null
  return { from, to }
}

function normalizeVisibleTimeRanges(value) {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {}
  const next = {}
  for (const [timeframe, range] of Object.entries(source)) {
    const normalized = normalizeVisibleTimeRange(range)
    if (normalized) next[timeframe] = normalized
  }
  return next
}

function readStoredVisibleTimeRange(timeframe, ranges = {}) {
  return normalizeVisibleTimeRange(ranges?.[timeframe])
}

function normalizeVisibleLogicalRange(range) {
  if (!range) return null
  const from = Number(range.from)
  const to = Number(range.to)
  const bars = Number(range.bars)
  const rightOffset = Number(range.rightOffset)
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null
  if ((to - from) < MIN_VISIBLE_LOGICAL_BARS) return null
  if (!Number.isFinite(bars) || bars <= 0 || !Number.isFinite(rightOffset)) return { from, to }
  if (bars < MIN_VISIBLE_LOGICAL_BARS) return null
  return { from, to, bars, rightOffset }
}

function normalizeVisibleLogicalRanges(value) {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {}
  const next = {}
  for (const [timeframe, range] of Object.entries(source)) {
    const normalized = normalizeVisibleLogicalRange(range)
    if (normalized) next[timeframe] = normalized
  }
  return next
}

function readStoredVisibleLogicalRange(timeframe, ranges = {}) {
  return normalizeVisibleLogicalRange(ranges?.[timeframe])
}

function visibleTimeRangesPreferencePayload(ranges) {
  return {
    version: VISIBLE_RANGE_PREFERENCE_VERSION,
    ranges: normalizeVisibleTimeRanges(ranges),
  }
}

function visibleLogicalRangesPreferencePayload(ranges) {
  return {
    version: VISIBLE_RANGE_PREFERENCE_VERSION,
    ranges: normalizeVisibleLogicalRanges(ranges),
  }
}

function nextVisibleTimeRangeMap(existingRanges, timeframe, range) {
  if (!timeframe || !range) return null
  const normalized = normalizeVisibleTimeRange(range)
  if (!normalized) return null
  return {
    ...(existingRanges || {}),
    [timeframe]: normalized,
  }
}

function nextVisibleLogicalRangeMap(existingRanges, timeframe, range, totalBars) {
  if (!timeframe || !range) return null
  const from = Number(range.from)
  const to = Number(range.to)
  const count = Number(totalBars)
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return
  if (!Number.isFinite(count) || count <= 0) return
  const bars = to - from
  if (bars < MIN_VISIBLE_LOGICAL_BARS) return
  const rightOffset = to - count
  if (rightOffset > MAX_VISIBLE_LOGICAL_RIGHT_OFFSET) return
  return {
    ...(existingRanges || {}),
    [timeframe]: { from, to, bars, rightOffset },
  }
}

function clearVisibleRangeMapTimeframe(existingRanges, timeframe) {
  if (!timeframe) return existingRanges || {}
  const next = { ...(existingRanges || {}) }
  delete next[timeframe]
  return next
}

function applyStoredVisibleTimeRange(chart, timeframe, ranges = {}) {
  const stored = readStoredVisibleTimeRange(timeframe, ranges)
  if (!stored) return false
  try {
    chart.timeScale().setVisibleRange(stored)
    return true
  } catch {
    return false
  }
}

function applyStoredVisibleLogicalRange(chart, timeframe, totalBars, ranges = {}, options = {}) {
  const stored = readStoredVisibleLogicalRange(timeframe, ranges)
  if (!stored) return false
  const count = Number(totalBars)
  let from = Number(stored.from)
  let to = Number(stored.to)
  if (Number.isFinite(stored.bars) && Number.isFinite(stored.rightOffset) && Number.isFinite(count) && count > 0) {
    const bars = Math.max(1, Number(stored.bars))
    const preferredVisibleBars = Math.round(clampNumber(options.visibleBars, 0, 0, MAX_STORED_LOGICAL_RANGE_BARS))
    const maxReasonableBars = Math.max(
      MIN_VISIBLE_LOGICAL_BARS,
      Math.min(
        count,
        MAX_STORED_LOGICAL_RANGE_BARS,
        preferredVisibleBars > 0 ? preferredVisibleBars * 3 : MAX_STORED_LOGICAL_RANGE_BARS,
      ),
    )
    if (bars > maxReasonableBars || bars > count) return false
    const preferredRightOffset = clampNumber(options.rightOffset, 0, 0, MAX_VISIBLE_LOGICAL_RIGHT_OFFSET)
    const maxReasonableRightOffset = Math.min(
      MAX_VISIBLE_LOGICAL_RIGHT_OFFSET,
      preferredRightOffset + STORED_LOGICAL_RANGE_RIGHT_OFFSET_TOLERANCE,
    )
    if (stored.rightOffset > maxReasonableRightOffset) return false
    const minRightOffset = -Math.max(0, count - 1)
    const maxRightOffset = Math.min(MAX_VISIBLE_LOGICAL_RIGHT_OFFSET, Math.max(0, bars - 1))
    const rightOffset = clampNumber(stored.rightOffset, 0, minRightOffset, maxRightOffset)
    to = count + rightOffset
    if (!Number.isFinite(to) || to <= 0) return false
    from = Math.max(0, to - bars)
  }
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return false
  try {
    chart.timeScale().setVisibleLogicalRange({ from, to })
    return true
  } catch {
    return false
  }
}

function isReplacingSparseLiveHistory(previousKey, nextKey, previousTotalBars, nextTotalBars) {
  if (!previousKey || previousKey !== nextKey) return false
  const previous = Number(previousTotalBars)
  const next = Number(nextTotalBars)
  return Number.isFinite(previous)
    && Number.isFinite(next)
    && previous > 0
    && previous < MIN_VISIBLE_LOGICAL_BARS
    && next >= MIN_VISIBLE_LOGICAL_BARS
}

// CE-04g-UI-live-bar-continuity — pure live-tick reducer extracted from
// the chart-build effect so the boundary-seeding logic can be tested
// directly without a full LWC mock. Contract:
//   - `deps.tickInstant` MUST be supplied by the call site (captured ONCE
//     per tick — multiple `new Date()` calls at boundary seconds can
//     disagree by ms and route session / date / floor to different bars).
//   - The helper MUST NOT call `new Date()`.
//   - The helper MUST NOT mutate `barState` — returns a fresh
//     `nextBarState`.
//   - The same-bar branch also writes `sessionDateKey` so the bootstrap
//     path (chart load → mid-bar live ticks → first boundary) gets
//     same-session continuity instead of falling through to seed-from-
//     tick. Without this, the first boundary after every chart load
//     would visibly gap.
//   - Same-session new-bar `open` falls back to the incoming `price` if
//     `barState.close` is null. This shouldn't fire in practice — by the
//     time a same-session boundary lands, at least one same-bar tick has
//     populated `close` — but it keeps the OHLC invariant correct in
//     edge cases (manual tests, history-seed-with-null-close, etc.).
export function reduceLiveTick(barState, tick, deps) {
  const {
    tickInstant,
    getMarketSession: getSession,
    getEtSessionDateKey: getDateKey,
    floorToBarPeriod: floorBar,
    timeframe,
    timezone,
  } = deps

  if (!tickInstant) throw new Error('reduceLiveTick requires tickInstant')

  if (tick?.field !== 'last') return { kind: 'no-op', nextBarState: barState }
  if (tick?.source === 'ibkr-snapshot') return { kind: 'no-op', nextBarState: barState }
  const price = tick.value
  if (typeof price !== 'number' || !Number.isFinite(price) || price <= 0) {
    return { kind: 'no-op', nextBarState: barState }
  }

  const session = getSession(tickInstant)
  if (session !== 'regular') {
    if (session === 'closed') return { kind: 'no-op', nextBarState: barState }
    return { kind: 'extended', session, price, nextBarState: barState }
  }

  const tickDateKey = getDateKey(tickInstant)
  const barTime = floorBar(tickInstant, timeframe, timezone)
  // CE-04g-UI-vwap-anchor: stamp rawEpochMs (real-instant epoch ms) from
  // the authoritative tick instant so the updated/merged bar carries the
  // field downstream into stored candle arrays. Additive — LWC ignores it.
  const rawEpochMs = tickInstant instanceof Date
    ? tickInstant.getTime()
    : (typeof tickInstant === 'number' ? tickInstant : null)

  if (barTime !== barState.time) {
    const sameSession = barState.sessionDateKey !== null && barState.sessionDateKey === tickDateKey
    const open = sameSession ? (barState.close ?? price) : price
    const nextBarState = {
      ...barState,
      time: barTime,
      rawEpochMs,
      open,
      high: Math.max(open, price),
      low: Math.min(open, price),
      close: price,
      sessionDateKey: tickDateKey,
      hasCorrection: false,
    }
    return {
      kind: 'update',
      barUpdate: { time: barTime, rawEpochMs, open, high: nextBarState.high, low: nextBarState.low, close: price },
      nextBarState,
    }
  }

  const nextBarState = {
    ...barState,
    rawEpochMs: rawEpochMs ?? barState.rawEpochMs ?? null,
    high: Math.max(barState.high, price),
    low: Math.min(barState.low, price),
    close: price,
    sessionDateKey: tickDateKey,
  }
  return {
    kind: 'update',
    barUpdate: {
      time: barTime,
      rawEpochMs: nextBarState.rawEpochMs,
      open: nextBarState.open,
      high: nextBarState.high,
      low: nextBarState.low,
      close: nextBarState.close,
    },
    nextBarState,
  }
}

export function buildLiveBoundaryPlaceholder(barState, deps) {
  const {
    nowMs = Date.now(),
    timeframe,
    timezone,
    getMarketSession: getSession,
    getEtSessionDateKey: getDateKey,
    floorToBarPeriod: floorBar,
  } = deps || {}

  const intervalSeconds = LIVE_TIMEFRAME_SECONDS[timeframe]
  if (!intervalSeconds || !barState || barState.time == null) return null
  if (!Number.isFinite(barState.close) || barState.close <= 0) return null
  if (typeof getSession !== 'function' || typeof getDateKey !== 'function' || typeof floorBar !== 'function') return null

  const nowDate = new Date(nowMs)
  if (Number.isNaN(nowDate.getTime())) return null
  if (getSession(nowDate) !== 'regular') return null

  const nextTime = floorBar(nowDate, timeframe, timezone)
  if (!Number.isFinite(nextTime) || nextTime <= barState.time) return null

  const previousRawEpochMs = Number(barState.rawEpochMs)
  if (!Number.isFinite(previousRawEpochMs) || previousRawEpochMs <= 0) return null
  const previousDateKey = getDateKey(previousRawEpochMs)
  const nextDateKey = getDateKey(nowMs)
  if (!previousDateKey || previousDateKey !== nextDateKey) return null

  const timeDeltaSeconds = nextTime - barState.time
  if (!Number.isFinite(timeDeltaSeconds) || timeDeltaSeconds <= 0) return null

  const close = barState.close
  return {
    time: nextTime,
    rawEpochMs: previousRawEpochMs + timeDeltaSeconds * 1000,
    open: close,
    high: close,
    low: close,
    close,
    volume: 0,
    clockBoundary: true,
  }
}

// CE-04g-UI-live-bar-continuity — pure helper that merges a corrected
// forming bar into the in-memory bar state. Required because
// the previous inline onFormingBarUpdate callback never tracked `close`;
// without close-tracking the next same-session boundary would seed
// `open` from a stale value.
export function applyFormingBarCorrection(barState, correctedBar) {
  const matchesCurrent = barState?.time === correctedBar?.time
  if (!matchesCurrent) {
    return { matchesCurrent: false, merged: correctedBar, nextBarState: barState }
  }

  // The official Python runtime has already reconciled RT Volume and the
  // provider's forming-bar checkpoint.  Preserve that exact snapshot instead
  // of retaining browser-local extremes from an earlier message.
  if (correctedBar.authoritativeForming) {
    const rawEpochMs = correctedBar.rawEpochMs ?? barState.rawEpochMs ?? null
    const nextBarState = {
      ...barState,
      time: correctedBar.time,
      rawEpochMs,
      open: correctedBar.open,
      high: correctedBar.high,
      low: correctedBar.low,
      close: correctedBar.close,
      volume: correctedBar.volume,
      lastPriceRawEpochMs: null,
      clockBoundary: false,
      hasCorrection: true,
    }
    return {
      matchesCurrent: true,
      nextBarState,
      merged: {
        time: correctedBar.time,
        rawEpochMs,
        open: correctedBar.open,
        high: correctedBar.high,
        low: correctedBar.low,
        close: correctedBar.close,
        volume: correctedBar.volume,
      },
    }
  }

  // CE-04g-UI-vwap-anchor: prefer the corrected bar's rawEpochMs (sourced
  // from the provider payload); fall back to the existing
  // barState's rawEpochMs so we never regress the field.
  const rawEpochMs = correctedBar.rawEpochMs ?? barState.rawEpochMs ?? null
  const correctedEventEpochMs = correctedBar.eventEpochMs ?? correctedBar.rawEpochMs ?? null
  if (correctedBar.lastPriceOnly) {
    const close = correctedBar.close
    const high = Math.max(barState.high, correctedBar.high ?? close)
    const low = Math.min(barState.low, correctedBar.low ?? close)
    const nextBarState = {
      ...barState,
      rawEpochMs,
      high,
      low,
      close,
      lastPriceRawEpochMs: correctedEventEpochMs ?? barState.lastPriceRawEpochMs ?? null,
      hasCorrection: true,
    }
    return {
      matchesCurrent: true,
      nextBarState,
      merged: {
        time: correctedBar.time,
        rawEpochMs,
        open: barState.open,
        high,
        low,
        close,
        lastPriceOnly: true,
      },
    }
  }
  const lastPriceRawEpochMs = barState.lastPriceRawEpochMs ?? null
  const correctedRawEpochMs = correctedBar.eventEpochMs ?? correctedBar.rawEpochMs ?? null
  const hasNewerLastPrice = Number.isFinite(lastPriceRawEpochMs)
    && Number.isFinite(correctedRawEpochMs)
    && lastPriceRawEpochMs > correctedRawEpochMs
  const nextRawEpochMs = hasNewerLastPrice ? lastPriceRawEpochMs : rawEpochMs
  const close = hasNewerLastPrice ? barState.close : correctedBar.close
  const keepClockBoundaryOpen = barState.clockBoundary === true && Number.isFinite(barState.open)
  const open = keepClockBoundaryOpen ? barState.open : correctedBar.open
  const high = Math.max(open, barState.high, correctedBar.high, close)
  const low = Math.min(open, barState.low, correctedBar.low, close)
  const nextBarState = {
    ...barState,
    rawEpochMs: nextRawEpochMs,
    open,
    high,
    low,
    close,
    lastPriceRawEpochMs: hasNewerLastPrice ? lastPriceRawEpochMs : null,
    hasCorrection: true,
  }

  return {
    matchesCurrent: true,
    nextBarState,
    merged: {
      time: correctedBar.time,
      rawEpochMs: nextRawEpochMs,
      open: nextBarState.open,
      high: nextBarState.high,
      low: nextBarState.low,
      close: nextBarState.close,
      // CE-04g-UI-indicators-live-tick: forward volume from the
      // forming-bar update so it lands on liveCandlesRef[last] and is
      // available to synthesizeFormingBar in the live-tick data pump.
      volume: correctedBar.volume,
    },
  }
}

// Keep changing latency callbacks outside the chart render scope: V8 can otherwise
// retain earlier render contexts (and their candle arrays) through memoized handlers.
function createMarkPendingLatencySpan(symbol, activeTimeframe, marketSession, mode, pendingLatencyStartRef) {
  return (reason, {
    nextSymbol = symbol,
    nextTimeframe = activeTimeframe,
    nextSession = marketSession,
  } = {}) => {
    const key = `${mode}:${nextSymbol || ''}:${nextTimeframe || ''}:${nextSession || ''}`
    pendingLatencyStartRef.current = {
      key,
      reason,
      startedAt: marketDataLatencyNow(),
      startedWallTs: Date.now(),
    }
  }
}

function createRecordFirstLiveUpdateAfterPaint(latencySpanRef, latencyKey) {
  return (payload, corrected) => {
    const span = latencySpanRef.current
    if (!span || span.key !== latencyKey || span.firstLiveUpdateAt != null || span.firstPaintAt == null) return
    const now = marketDataLatencyNow()
    span.firstLiveUpdateAt = now
    emitMarketDataLatencyEvent({
      kind: 'first-live-forming-update',
      reason: span.reason,
      symbol: span.symbol,
      timeframe: span.timeframe,
      session: span.session,
      barSize: payload?.barSize,
      barTime: payload?.barTime,
      chartTime: corrected?.time,
      firstPaintToFirstLiveUpdateMs: roundLatencyMs(now - span.firstPaintAt),
    })
  }
}

function createHandleTimeframeChange({
  markPendingLatencySpan, setActiveTimeframe, mode, resetLiveViewportForTimeframe,
  committedTfRef, setIndicators, serverIndicatorMapRef, chartStorageScope, onTimeframeChange,
}) {
  return (tf) => {
    markPendingLatencySpan('timeframe-switch', { nextTimeframe: tf })
    setActiveTimeframe(tf)
    if (mode === 'live') {
      resetLiveViewportForTimeframe(tf)
      committedTfRef.current = tf
      setIndicators(
        readServerIndicatorsForTimeframe(tf, mode, serverIndicatorMapRef.current)
          ?? initialIndicatorsForMode(mode, chartStorageScope)
      )
    }
    if (onTimeframeChange) onTimeframeChange(tf)
  }
}

function marketDataLatencyDebugEnabled() {
  if (typeof window === 'undefined') return false
  try {
    const params = new URLSearchParams(window.location?.search || '')
    if (params.get('latency') === '1') return true
    return window.localStorage?.getItem('erevis.marketDataLatencyDebug') === '1'
  } catch {
    return false
  }
}

function marketDataChartParityModeEnabled() {
  if (typeof window === 'undefined') return false
  try {
    const params = new URLSearchParams(window.location?.search || '')
    if (params.get('chartParity') === '1' || params.get('parity') === '1') return true
    return false
  } catch {
    return false
  }
}

function marketDataLatencyNow() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now()
  }
  return Date.now()
}

function roundLatencyMs(value) {
  return Number.isFinite(value) ? Math.round(value) : null
}

export function emitMarketDataLatencyEvent(event) {
  if (!marketDataLatencyDebugEnabled()) return null
  const record = {
    schema: 'erevis-market-data-latency.v1',
    wallTs: Date.now(),
    ...event,
  }
  try {
    const buffer = Array.isArray(window.__EREVIS_MARKET_DATA_LATENCY__)
      ? window.__EREVIS_MARKET_DATA_LATENCY__
      : []
    buffer.push(record)
    window.__EREVIS_MARKET_DATA_LATENCY__ = buffer.slice(-200)
  } catch {
    // Non-fatal; console output is still useful during manual debugging.
  }
  console.debug('[market-data-latency]', record)
  return record
}

function emitMarketDataLatencyDebug({ symbol, timeframe, session, payload, corrected }) {
  if (!marketDataLatencyDebugEnabled()) return
  const latency = payload?.latency && typeof payload.latency === 'object' ? payload.latency : {}
  const chartApplyTs = Date.now()
  const browserReceiveTs = Number.isInteger(Number(latency.browserReceiveTs)) ? Number(latency.browserReceiveTs) : null
  const providerEventTs = Number.isInteger(Number(latency.providerEventTs)) ? Number(latency.providerEventTs) : null
  emitMarketDataLatencyEvent({
    kind: 'live-forming-update',
    symbol,
    timeframe,
    session,
    barSize: payload?.barSize,
    barTime: payload?.barTime,
    isFinal: Boolean(payload?.isFinal),
    source: payload?.source,
    close: payload?.close,
    chartTime: corrected?.time,
    latency: {
      ...latency,
      chartApplyTs,
      browserToChartMs: browserReceiveTs == null ? null : chartApplyTs - browserReceiveTs,
      providerToChartMs: providerEventTs == null ? null : chartApplyTs - providerEventTs,
    },
  })
}

// CE-04g-UI-vwap-anchor — display-time filter that keeps only pivot rows
// whose underlying bar falls on the most-recent ET session. `calcPivotLevels`
// itself stays pure (strategy/snapshot callers keep byte-for-byte output);
// the chart owns the today-only render policy. Generalizes to "today's
// session plus prior N sessions" by widening `matchedDateKeys` — that's the
// hook for the deferred `num_pivots_back > 1` polish slice.
export function filterPivotsToTodayEtSession(raw, bars) {
  if (!raw || !Array.isArray(bars) || bars.length === 0) return raw
  // Build the {time → ET date key} map from the input bars. Prefer
  // rawEpochMs (real-instant). Bars missing it (legacy seed) fall back to
  // chart-`time` * 1000 — the historical behavior — so the filter never
  // throws away every row.
  const dateKeyByTime = new Map()
  let lastNonNullKey = null
  for (const b of bars) {
    const ms = (typeof b.rawEpochMs === 'number' && Number.isFinite(b.rawEpochMs))
      ? b.rawEpochMs
      : (typeof b.time === 'number' ? b.time * 1000 : null)
    const key = ms == null ? null : getEtSessionDateKey(ms)
    if (key) {
      dateKeyByTime.set(b.time, key)
      lastNonNullKey = key
    }
  }
  if (!lastNonNullKey) return raw
  const matchedDateKeys = new Set([lastNonNullKey])
  const out = {}
  for (const levelKey of ['pivot', 'r1', 'r2', 'r3', 's1', 's2', 's3']) {
    const arr = raw[levelKey] || []
    out[levelKey] = arr.filter(p => matchedDateKeys.has(dateKeyByTime.get(p.time)))
  }
  return out
}

// MD-00 + CE-04g-UI-tick-fallback (v2-extended): canonical helper lives
// in src/lib/chart/getTickInstant.js (imported at the top of this file).
// Re-exported as a local binding so the shared matrix
// (src/components/__shared__/liveTickReducerMatrix.js) keeps
// destructuring it off this module's exports. MD-00b: a bare `export {
// x } from '...'` does NOT create a local binding — the
// `ibkrStream.subscribe` callback below references getTickInstant
// directly, so the import + local-binding export are both required.
export { getTickInstant }

const TIMEFRAMES = Object.freeze([
  { key: '5min',  label: '5m' },
  { key: '15min', label: '15m' },
  { key: '30min', label: '30m' },
  { key: '65min', label: '65m' },
  { key: 'daily', label: 'D' },
  { key: 'weekly', label: 'W' },
])

/**
 * Shared chart React component. Single import path for PositionWorkspace
 * (CE-05), Strategy Builder (CE-10), Scanner thumbnails (CE-09), and
 * AAR/postmortem replay (future epic, composes around `mode='static'`).
 *
 * **CE-04a fills in the `mode='static'` render body**: real Lightweight
 * Charts v4 candle rendering, indicator legend, SMA reference indicator,
 * and the popover settings UX. Other modes keep the CE-02 placeholder body.
 *
 * **Frozen contract.** See vault:
 * `_System/epics/chart-experience-foundation/chart-component-contract.md`.
 * Single-variable-commit discipline applies: every prop addition or rename
 * in CE-03 through CE-15 requires a fresh Grok validate_prompt pass and an
 * atomic commit on the contract doc + this file.
 *
 * **CE-04g-UI-chrome-1.** Wrapped in `forwardRef` and exposes an
 * `addIndicator(kind)` imperative handle so parent-owned top-bar chrome
 * (ChartTopBar, future PositionWorkspace top bar) can append indicators
 * without lifting the chassis's indicator-list state. See
 * `chart-component-contract.md` §15.
 *
 * @param {import('./types.js').TradeChartProps} props
 * @returns {JSX.Element}
 */

// Hardcoded indicator defaults per CE-04a scope rule §13 + CE-04b §13.
// Initial popover state in this slice — Supabase-backed asset-class-aware
// presets arrive in CE-04g. EMA shares the SMA field shape verbatim
// (indicator-settings-spec.md §1+§2); only the `length` and `style.ma.color`
// differ at the bare-add default level.
const SMA_DEFAULT_PARAMS = Object.freeze({
  length: 5,
  source: 'close',
  offset: 0,
  smoothing: { type: 'None', length: 14, bb_std_dev: 2.0 },
  timeframe: null,
  wait_for_timeframe_closes: true,
})

const SMA_DEFAULT_STYLE = Object.freeze({
  ma: { visible: true, color: '#2962FF', opacity: 100, thickness: 2, line_style: 'solid' },
  precision: 'default',
  labels_on_price_scale: true,
  values_in_status_line: true,
  inputs_in_status_line: true,
})

// EMA defaults: TV default length 9 (= first entry of equity 9/21/50 +
// crypto 9/21/55 seed lists per indicator-settings-spec.md §2), turquoise
// from the existing 14-entry palette, otherwise mirrors SMA. The "21 EMA =
// turquoise dashed" preset convention is preset-level (CE-04g) — the
// bare-add line_style stays 'solid'.
const EMA_DEFAULT_PARAMS = Object.freeze({
  length: 9,
  source: 'close',
  offset: 0,
  smoothing: { type: 'None', length: 14, bb_std_dev: 2.0 },
  timeframe: null,
  wait_for_timeframe_closes: true,
})

const EMA_DEFAULT_STYLE = Object.freeze({
  ma: { visible: true, color: '#00BCD4', opacity: 100, thickness: 2, line_style: 'solid' },
  precision: 'default',
  labels_on_price_scale: true,
  values_in_status_line: true,
  inputs_in_status_line: true,
})

const KELTNER_DEFAULT_PARAMS = Object.freeze({
  length: 21,
  multiplier: 1.5,
  source: 'ohlc4',
  use_exponential_ma: true,
  bands_style: 'average_true_range',
  atr_length: 10,
  timeframe: null,
  wait_for_timeframe_closes: true,
})

const KELTNER_DEFAULT_STYLE = Object.freeze({
  basis: { visible: true, color: '#FF9800', opacity: 100, thickness: 2, line_style: 'solid' },
  upper: { visible: true, color: '#2962FF', opacity: 100, thickness: 2, line_style: 'solid' },
  lower: { visible: true, color: '#2962FF', opacity: 100, thickness: 2, line_style: 'solid' },
  background: { visible: true, color: '#2962FF', opacity: 5 },
  precision: 'default',
  labels_on_price_scale: true,
  values_in_status_line: true,
  inputs_in_status_line: true,
})

// CE-04c — Bollinger Bands defaults per indicator-settings-spec.md §7
// (TradingView factory: length=20, std_mult=2.0). `ma_type` greyed via
// EI-03 (smoothing chain — same engine ticket family that gates SMA/EMA's
// `smoothing.type`). Background color matches upper/lower (TV blue) at
// low opacity to render a translucent band fill.
const BOLLINGER_DEFAULT_PARAMS = Object.freeze({
  length: 20,
  std_mult: 2.0,
  source: 'close',
  offset: 0,
  ma_type: 'SMA',
  timeframe: null,
  wait_for_timeframe_closes: true,
})

const BOLLINGER_DEFAULT_STYLE = Object.freeze({
  basis: { visible: true, color: '#FF9800', opacity: 100, thickness: 2, line_style: 'solid' },
  upper: { visible: true, color: '#2962FF', opacity: 100, thickness: 2, line_style: 'solid' },
  lower: { visible: true, color: '#2962FF', opacity: 100, thickness: 2, line_style: 'solid' },
  background: { visible: true, color: '#2962FF', opacity: 10 },
  precision: 'default',
  labels_on_price_scale: true,
  values_in_status_line: true,
  inputs_in_status_line: true,
})

// CE-04c — Donchian Channels defaults per indicator-settings-spec.md §9
// (TradingView factory: length=20). Donchian has no `source` parameter
// (the math walks high/low directly) and no smoothing chain. Background
// opacity is 5 — dimmer than Bollinger's 10 — per TV factory.
const DONCHIAN_DEFAULT_PARAMS = Object.freeze({
  length: 20,
  offset: 0,
  timeframe: null,
  wait_for_timeframe_closes: true,
})

const DONCHIAN_DEFAULT_STYLE = Object.freeze({
  upper: { visible: true, color: '#2962FF', opacity: 100, thickness: 2, line_style: 'solid' },
  basis: { visible: true, color: '#FF9800', opacity: 100, thickness: 2, line_style: 'solid' },
  lower: { visible: true, color: '#2962FF', opacity: 100, thickness: 2, line_style: 'solid' },
  background: { visible: true, color: '#2962FF', opacity: 5 },
  precision: 'default',
  labels_on_price_scale: true,
  values_in_status_line: true,
  inputs_in_status_line: true,
})

// CE-04c-2 — Volume defaults per indicator-settings-spec.md §5 (TradingView
// factory). `color_based_on_previous_close=false` colors bars by close-vs-
// current-open. Growing/falling colors are members of the existing 14-entry
// COLOR_SWATCHES palette in IndicatorPopover.jsx (TV green #089981 / red
// #F23645). Volume MA + outlier overlay are intentionally omitted — per
// scope rules §10 (no Volume MA) and §9 (no outlier overlay until EI-08).
const VOLUME_DEFAULT_PARAMS = Object.freeze({
  color_based_on_previous_close: false,
})

const VOLUME_DEFAULT_STYLE = Object.freeze({
  bars: {
    visible: true,
    growing: { color: '#089981', opacity: 100 },
    falling: { color: '#F23645', opacity: 100 },
  },
  precision: 'default',
  labels_on_price_scale: true,
  values_in_status_line: true,
  inputs_in_status_line: true,
})

// CE-04g-UI-b — Session VWAP defaults. Each VWAP variant is its own kind
// (vwap_1d, vwap_2d, vwap_wtd, vwap_mtd, vwap_ytd) rather than a single
// kind with a resetType param. This avoids multi-instance support changes
// in the indicator accordion and keeps the one-instance-per-type cap clean.
const VWAP_DEFAULT_PARAMS = Object.freeze({})

const VWAP_1D_DEFAULT_STYLE = Object.freeze({
  vwap: { visible: true, color: '#FF9800', opacity: 100, thickness: 2, line_style: 'solid' },
  precision: 'default',
  labels_on_price_scale: true,
  values_in_status_line: true,
  inputs_in_status_line: true,
})
const VWAP_2D_DEFAULT_STYLE = Object.freeze({
  vwap: { visible: true, color: '#E91E63', opacity: 100, thickness: 2, line_style: 'solid' },
  precision: 'default',
  labels_on_price_scale: true,
  values_in_status_line: true,
  inputs_in_status_line: true,
})
const VWAP_WTD_DEFAULT_STYLE = Object.freeze({
  vwap: { visible: true, color: '#9C27B0', opacity: 100, thickness: 2, line_style: 'solid' },
  precision: 'default',
  labels_on_price_scale: true,
  values_in_status_line: true,
  inputs_in_status_line: true,
})
const VWAP_MTD_DEFAULT_STYLE = Object.freeze({
  vwap: { visible: true, color: '#2962FF', opacity: 100, thickness: 2, line_style: 'solid' },
  precision: 'default',
  labels_on_price_scale: true,
  values_in_status_line: true,
  inputs_in_status_line: true,
})
const VWAP_YTD_DEFAULT_STYLE = Object.freeze({
  vwap: { visible: true, color: '#00BCD4', opacity: 100, thickness: 2, line_style: 'solid' },
  precision: 'default',
  labels_on_price_scale: true,
  values_in_status_line: true,
  inputs_in_status_line: true,
})

// CE-04g-UI-b — Pivot Points defaults. 7 horizontal-line components.
// `bar_size` tells calcPivotLevels how to group bars.
const PIVOTS_DEFAULT_PARAMS = Object.freeze({
  bar_size: '1 day',
})

const PIVOTS_DEFAULT_STYLE = Object.freeze({
  pivot: { visible: true, color: '#FF9800', opacity: 100, thickness: 1, line_style: 'dashed' },
  r1:    { visible: true, color: '#089981', opacity: 100, thickness: 1, line_style: 'dotted' },
  r2:    { visible: true, color: '#089981', opacity: 80, thickness: 1, line_style: 'dotted' },
  r3:    { visible: true, color: '#089981', opacity: 60, thickness: 1, line_style: 'dotted' },
  s1:    { visible: true, color: '#F23645', opacity: 100, thickness: 1, line_style: 'dotted' },
  s2:    { visible: true, color: '#F23645', opacity: 80, thickness: 1, line_style: 'dotted' },
  s3:    { visible: true, color: '#F23645', opacity: 60, thickness: 1, line_style: 'dotted' },
  precision: 'default',
  labels_on_price_scale: true,
  values_in_status_line: true,
  inputs_in_status_line: true,
})

const CARS_DEFAULT_PARAMS = Object.freeze({
  bar_size: '1 day',
  benchmark: 'SPY',
})

const CARS_DEFAULT_STYLE = Object.freeze({
  background: { color: '#2962FF', opacity: 12 },
})

const DEFAULT_PARAMS_BY_KIND = Object.freeze({
  sma: SMA_DEFAULT_PARAMS,
  ema: EMA_DEFAULT_PARAMS,
  keltner: KELTNER_DEFAULT_PARAMS,
  bollinger: BOLLINGER_DEFAULT_PARAMS,
  donchian: DONCHIAN_DEFAULT_PARAMS,
  volume: VOLUME_DEFAULT_PARAMS,
  vwap_1d: VWAP_DEFAULT_PARAMS,
  vwap_2d: VWAP_DEFAULT_PARAMS,
  vwap_wtd: VWAP_DEFAULT_PARAMS,
  vwap_mtd: VWAP_DEFAULT_PARAMS,
  vwap_ytd: VWAP_DEFAULT_PARAMS,
  vwap_earnings: { ...VWAP_DEFAULT_PARAMS, anchor_timing: 'earnings-date' },
  vwap_earnings_2: { ...VWAP_DEFAULT_PARAMS, anchor_timing: 'earnings-date' },
  pivots: PIVOTS_DEFAULT_PARAMS,
  cars_v1: CARS_DEFAULT_PARAMS,
})

const DEFAULT_STYLE_BY_KIND = Object.freeze({
  sma: SMA_DEFAULT_STYLE,
  ema: EMA_DEFAULT_STYLE,
  keltner: KELTNER_DEFAULT_STYLE,
  bollinger: BOLLINGER_DEFAULT_STYLE,
  donchian: DONCHIAN_DEFAULT_STYLE,
  volume: VOLUME_DEFAULT_STYLE,
  vwap_1d: VWAP_1D_DEFAULT_STYLE,
  vwap_2d: VWAP_2D_DEFAULT_STYLE,
  vwap_wtd: VWAP_WTD_DEFAULT_STYLE,
  vwap_mtd: VWAP_MTD_DEFAULT_STYLE,
  vwap_ytd: VWAP_YTD_DEFAULT_STYLE,
  vwap_earnings: { ...VWAP_YTD_DEFAULT_STYLE, vwap: { ...VWAP_YTD_DEFAULT_STYLE.vwap, color: '#FACC15' } },
  vwap_earnings_2: { ...VWAP_YTD_DEFAULT_STYLE, vwap: { ...VWAP_YTD_DEFAULT_STYLE.vwap, color: '#A78BFA' } },
  pivots: PIVOTS_DEFAULT_STYLE,
  cars_v1: CARS_DEFAULT_STYLE,
})

// CE-04g-UI-chrome-2 — multi-instance policy lives in indicatorRules.js so
// IndicatorAccordion can share the same set without a circular import.
// Re-exported here for any historical call sites that read it off the
// chassis module.
export { MULTI_INSTANCE_KINDS } from './indicatorRules.js'

const COMPUTE_BY_KIND = Object.freeze({
  sma: computeSMA,
  ema: computeEMA,
  keltner: computeKeltner,
  bollinger: computeBollinger,
  donchian: computeDonchian,
  volume: computeVolumeBarColors,
  vwap_1d: computeSessionVWAP,
  vwap_2d: computeSessionVWAP,
  vwap_wtd: computeSessionVWAP,
  vwap_mtd: computeSessionVWAP,
  vwap_ytd: computeSessionVWAP,
  vwap_earnings: computeEarningsVwap,
  vwap_earnings_2: computeEarningsVwap,
  pivots: computePivotLevels,
})

// CE-04g-UI-chart-smoke-fixes — pivot display ↔ compute-key mapping.
// Lowercase keys (`pivot`, `r1`...) match the math layer / style sections.
// Uppercase display keys (`P`, `R1`...) match TradingView's Classic Daily
// preset and become the price-line `title` shown next to the axis label.
const PIVOT_DISPLAY_KEYS = ['P', 'R1', 'R2', 'R3', 'S1', 'S2', 'S3']
const PIVOT_KEY_TO_COMPUTE = Object.freeze({
  P: 'pivot', R1: 'r1', R2: 'r2', R3: 'r3', S1: 's1', S2: 's2', S3: 's3',
})

// CE-04g-UI-chart-smoke-fixes — picks the latest non-null level per
// lowercase compute key from the filtered output of
// `filterPivotsToTodayEtSession`. Returned as a flat `{ pivot, r1, ... }`
// map suitable for direct `createPriceLine({ price })` / `applyOptions({ price })`
// calls. Exported only for inline test coverage; the math layer stays
// untouched (this adapter lives in the chart layer per slice scope).
export function extractLatestPivotLevels(filtered) {
  const out = {}
  if (!filtered || typeof filtered !== 'object') return out
  for (const key of ['pivot', 'r1', 'r2', 'r3', 's1', 's2', 's3']) {
    const arr = filtered[key]
    if (!Array.isArray(arr) || arr.length === 0) continue
    // Walk backward to pick the latest non-null finite value per spec —
    // a tail null/NaN must not strand a still-valid earlier level.
    for (let i = arr.length - 1; i >= 0; i--) {
      const point = arr[i]
      if (point && Number.isFinite(point.value)) {
        out[key] = point.value
        break
      }
    }
  }
  return out
}

// CE-04g-UI-pivots-popover — read the candle series' display step
// (minMove) for the axis-label dedupe pass. Lightweight Charts exposes
// minMove on `series.options().priceFormat`, NOT on
// `priceScale().options()` — the latter's PriceScaleOptions interface
// has no minMove field. Falls back to 0.01 (standard equity tick) when
// the series shape doesn't expose priceFormat.minMove, which covers
// every current TradeChart caller.
function resolveAxisMinMove(candleSeries) {
  try {
    const seriesMinMove = candleSeries?.options?.()?.priceFormat?.minMove
    if (Number.isFinite(seriesMinMove) && seriesMinMove > 0) return seriesMinMove
  } catch { /* noop */ }
  return 0.01
}

// Per-indicator-kind component layout. Matches the descriptor's
// `styleSections` in IndicatorPopover.FIELD_SHAPE_BY_KIND. Listed here
// rather than imported so the chart layer never touches presentational
// concerns; both maps must move in lockstep when a new indicator is added.
const COMPONENTS_BY_KIND = Object.freeze({
  sma: [{ key: 'ma', shape: 'line', dataKey: 'value' }],
  ema: [{ key: 'ma', shape: 'line', dataKey: 'value' }],
  keltner: [
    { key: 'basis',      shape: 'line', dataKey: 'basis' },
    { key: 'upper',      shape: 'line', dataKey: 'upper' },
    { key: 'lower',      shape: 'line', dataKey: 'lower' },
    { key: 'background', shape: 'fill', dataKey: 'upper' },
  ],
  bollinger: [
    { key: 'basis',      shape: 'line', dataKey: 'basis' },
    { key: 'upper',      shape: 'line', dataKey: 'upper' },
    { key: 'lower',      shape: 'line', dataKey: 'lower' },
    { key: 'background', shape: 'fill', dataKey: 'upper' },
  ],
  donchian: [
    { key: 'upper',      shape: 'line', dataKey: 'upper' },
    { key: 'basis',      shape: 'line', dataKey: 'basis' },
    { key: 'lower',      shape: 'line', dataKey: 'lower' },
    { key: 'background', shape: 'fill', dataKey: 'upper' },
  ],
  volume: [
    // Single histogram component. `dataKey: 'volume'` is informational for
    // Volume since computeVolumeBarColors emits {time,value,color} directly
    // — the histogram branch in the reconcile loop bypasses the
    // line/fill projection step. Convention kept uniform across kinds.
    { key: 'bars', shape: 'histogram', dataKey: 'volume' },
  ],
  vwap_1d:  [{ key: 'vwap', shape: 'line', dataKey: 'value' }],
  vwap_2d:  [{ key: 'vwap', shape: 'line', dataKey: 'value' }],
  vwap_wtd: [{ key: 'vwap', shape: 'line', dataKey: 'value' }],
  vwap_mtd: [{ key: 'vwap', shape: 'line', dataKey: 'value' }],
  vwap_ytd: [{ key: 'vwap', shape: 'line', dataKey: 'value' }],
  vwap_earnings: [{ key: 'vwap', shape: 'line', dataKey: 'value' }],
  vwap_earnings_2: [{ key: 'vwap', shape: 'line', dataKey: 'value' }],
  // CE-04g-UI-chart-smoke-fixes — `pivots` intentionally absent. Pivots
  // render via candleSeries.createPriceLine() (horizontal axis levels),
  // not the generic line-series pipeline. Effects 1+2 branch on
  // `ind.kind === 'pivots'` before this lookup; Effect 3 still skips.
})

const OVERLAY_AXIS_LABEL_TEXT_COLORS = Object.freeze({
  dark: Object.freeze({
    entry: '#FFFFFF',
    stop: '#FFFFFF',
    target: '#FFFFFF',
    avg: '#111827',
  }),
  light: Object.freeze({
    entry: '#FFFFFF',
    stop: '#FFFFFF',
    target: '#FFFFFF',
    avg: '#FFFFFF',
  }),
})

const OVERLAY_STYLE_KEYS = Object.freeze({
  entry: Object.freeze({ color: 'positionEntryColor', opacity: 'positionEntryOpacity' }),
  stop: Object.freeze({ color: 'positionStopColor', opacity: 'positionStopOpacity' }),
  target: Object.freeze({ color: 'positionTargetColor', opacity: 'positionTargetOpacity' }),
  avg: Object.freeze({ color: 'positionAvgColor', opacity: 'positionAvgOpacity' }),
})

function finiteOverlayPrice(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function overlayPriceLineStyle(chartSettings, role, visualTheme = 'dark') {
  const keys = OVERLAY_STYLE_KEYS[role] || OVERLAY_STYLE_KEYS.stop
  const fallbackSettings = DEFAULT_CHART_SETTINGS_BY_THEME[visualTheme] || DEFAULT_CHART_SETTINGS_BY_THEME.dark
  const color = chartSettingsColor(
    chartSettings,
    keys.color,
    keys.opacity,
    fallbackSettings[keys.color],
  )
  const labelTextColors = OVERLAY_AXIS_LABEL_TEXT_COLORS[visualTheme] || OVERLAY_AXIS_LABEL_TEXT_COLORS.dark
  return {
    color,
    axisLabelColor: color,
    axisLabelTextColor: labelTextColors[role] || '#FFFFFF',
  }
}

export function buildOverlayPriceLineSpecs({ setupLines, positionLines, visualTheme = 'dark', chartSettings } = {}) {
  const specs = []
  const effectiveSettings = chartSettings || DEFAULT_CHART_SETTINGS_BY_THEME[visualTheme] || DEFAULT_CHART_SETTINGS_BY_THEME.dark

  if (setupLines?.entry?.visible !== false) {
    const price = finiteOverlayPrice(setupLines?.entry?.price)
    if (price != null) {
      const style = overlayPriceLineStyle(effectiveSettings, 'entry', visualTheme)
      specs.push({
        key: 'setup-entry',
        price,
        ...style,
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: 'Trigger',
      })
    }
  }

  if (setupLines?.stop?.visible !== false) {
    const price = finiteOverlayPrice(setupLines?.stop?.price)
    if (price != null) {
      const style = overlayPriceLineStyle(effectiveSettings, 'stop', visualTheme)
      specs.push({
        key: 'setup-stop',
        price,
        ...style,
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: 'Stop',
      })
    }
  }

  if (setupLines?.target?.visible !== false) {
    const price = finiteOverlayPrice(setupLines?.target?.price)
    if (price != null) {
      const style = overlayPriceLineStyle(effectiveSettings, 'target', visualTheme)
      specs.push({
        key: 'setup-target',
        price,
        ...style,
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: '1/3 Target',
      })
    }
  }

  if (Array.isArray(setupLines?.trancheStops)) {
    for (const stop of setupLines.trancheStops) {
      const price = finiteOverlayPrice(stop?.price)
      if (price == null) continue
      const style = overlayPriceLineStyle(effectiveSettings, 'stop', visualTheme)
      specs.push({
        key: `tranche-stop:${stop.trancheId || price}`,
        price,
        ...style,
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: 'Stop',
      })
    }
  }

  const avgCost = finiteOverlayPrice(positionLines?.avgCost?.price)
  if (avgCost != null) {
    const style = overlayPriceLineStyle(effectiveSettings, 'avg', visualTheme)
    specs.push({
      key: 'position-avg-cost',
      price: avgCost,
      ...style,
      lineWidth: 1,
      lineStyle: LineStyle.Solid,
      axisLabelVisible: true,
      title: 'Avg',
    })
  }

  return specs
}

function markerColor(marker) {
  if (marker?.type === 'stop-hit') return 'rgba(255,100,120,0.95)'
  if (marker?.type === 'target-hit') return 'rgba(234,179,8,0.95)'
  if (marker?.side === 'buy') return 'rgba(46,204,113,0.95)'
  if (marker?.side === 'sell') return 'rgba(224,90,74,0.95)'
  return 'rgba(160,170,190,0.95)'
}

function markerShape(marker) {
  if (marker?.type === 'stop-hit') return 'square'
  if (marker?.type === 'target-hit') return 'circle'
  if (marker?.side === 'buy') return 'arrowUp'
  if (marker?.side === 'sell') return 'arrowDown'
  return 'circle'
}

function markerPosition(marker) {
  if (marker?.side === 'buy') return 'atPriceBottom'
  if (marker?.side === 'sell' || marker?.type === 'stop-hit' || marker?.type === 'target-hit') {
    return 'atPriceTop'
  }
  return 'atPriceMiddle'
}

export function buildSeriesMarkerSpecs(markers = []) {
  return (Array.isArray(markers) ? markers : [])
    .map((marker) => {
      if (markerIsExecutionAnnotation(marker)) return null
      const price = finiteOverlayPrice(marker?.price)
      const time = finiteOverlayPrice(marker?.bar_time_ref ?? marker?.time)
      if (price == null || time == null) return null
      if (marker?.type === 'engine-signal' && Number(marker?.intensity ?? 1) < 0.2) return null
      return {
        id: marker.id,
        time,
        price,
        position: markerPosition(marker),
        shape: markerShape(marker),
        color: markerColor(marker),
        text: marker.label,
        size: marker.state === 'active' ? 1.25 : 1,
      }
    })
    .filter(Boolean)
}

function markerIsExecutionAnnotation(marker) {
  if (!marker) return false
  if (marker.type === 'execution' || marker.type === 'stop-hit' || marker.type === 'target-hit') return true
  return marker.side === 'buy' || marker.side === 'sell'
}

function markerAsExecution(marker) {
  const children = Array.isArray(marker?.metadata?.children)
    ? marker.metadata.children.map((child, index) => ({
        id: child?.id || `${marker.id}:child:${index}`,
        trade_id: marker?.metadata?.trade_id,
        symbol: child?.symbol || marker?.symbol || marker?.metadata?.symbol,
        side: child?.side || marker?.side,
        price: child?.price ?? marker?.price,
        quantity: child?.quantity,
        account_number: child?.account_number || marker?.metadata?.account,
        executed_at: child?.executed_at || marker?.metadata?.executed_at,
      }))
    : null
  return {
    id: marker?.id,
    trade_id: marker?.metadata?.trade_id,
    symbol: marker?.symbol || marker?.metadata?.symbol,
    side: marker?.side,
    price: marker?.price,
    quantity: marker?.metadata?.qty,
    account_number: marker?.metadata?.account,
    executed_at: marker?.metadata?.executed_at,
    _children: children,
  }
}

function markerAnnotationSideLabel(marker) {
  if (marker?.side === 'buy') return 'BUY'
  if (marker?.side === 'sell') return 'SELL'
  if (marker?.type === 'stop-hit') return 'STOP'
  if (marker?.type === 'target-hit') return 'TARGET'
  return 'EXEC'
}

function markerTooltipValue(marker, key) {
  const row = marker?.tooltip?.rows?.find((item) => item?.left === key)
  return row?.right ?? null
}

function formatMarkerPrice(price) {
  const n = finiteOverlayPrice(price)
  if (n == null) return '-'
  return `$${n.toFixed(2)}`
}

function markerAggregateText(marker) {
  const qty = marker?.metadata?.qty ?? markerTooltipValue(marker, 'Qty')
  const price = marker?.price ?? markerTooltipValue(marker, 'Price')
  if (qty != null && price != null) return `${qty} shares @ ${formatMarkerPrice(price)}`
  if (qty != null) return `${qty} shares`
  if (price != null) return formatMarkerPrice(price)
  return marker?.tooltip?.headline || marker?.label || markerAnnotationSideLabel(marker)
}

function markerAccountText(marker) {
  return marker?.metadata?.account ?? markerTooltipValue(marker, 'Account') ?? null
}

function markerChildren(marker) {
  const children = Array.isArray(marker?.metadata?.children) ? marker.metadata.children.filter(Boolean) : []
  if (children.length > 0) return children
  const account = markerAccountText(marker)
  return account ? [{ account_number: account, quantity: marker?.metadata?.qty, price: marker?.price }] : []
}

function markerChildAccountKey(child) {
  return String(
    child?.account_name
      ?? child?.account_label
      ?? child?.account
      ?? child?.account_number
      ?? ''
  )
}

function markerChildPriceKey(child) {
  const price = finiteOverlayPrice(child?.price)
  return price == null ? null : String(price)
}

function displayMarkerChildren(marker) {
  const children = markerChildren(marker)
  const byAccountAndPrice = new Map()
  const merged = []

  children.forEach((child, index) => {
    const accountKey = markerChildAccountKey(child)
    const priceKey = markerChildPriceKey(child)
    const key = priceKey == null ? null : `${accountKey}\u0000${priceKey}`
    if (!key) {
      merged.push({ ...child, _sortIndex: index })
      return
    }
    const existing = byAccountAndPrice.get(key)
    if (existing) {
      existing.quantity = (Number(existing.quantity) || 0) + (Number(child?.quantity) || 0)
      return
    }
    const next = { ...child, _sortIndex: index }
    byAccountAndPrice.set(key, next)
    merged.push(next)
  })

  return merged.sort((a, b) => {
    const qtySort = (Number(b?.quantity) || 0) - (Number(a?.quantity) || 0)
    if (qtySort !== 0) return qtySort
    const accountSort = markerChildAccountKey(a).localeCompare(markerChildAccountKey(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    })
    if (accountSort !== 0) return accountSort
    const aPrice = finiteOverlayPrice(a?.price)
    const bPrice = finiteOverlayPrice(b?.price)
    if (aPrice != null && bPrice != null && aPrice !== bPrice) return aPrice - bPrice
    return (a._sortIndex || 0) - (b._sortIndex || 0)
  })
}

function markerConsolidatedCount(marker) {
  const count = Number(marker?.metadata?.consolidated_count)
  return Number.isFinite(count) && count > 1 ? count : 1
}

function markerTotalQty(marker) {
  const qty = Number(marker?.metadata?.qty)
  if (Number.isFinite(qty)) return qty
  return markerChildren(marker).reduce((sum, child) => sum + (Number(child?.quantity) || 0), 0)
}

function markerExecutedAt(marker) {
  const metadataTime = marker?.metadata?.executed_at
  if (metadataTime) return metadataTime
  const childTime = markerChildren(marker).find((child) => child?.executed_at)?.executed_at
  return childTime || null
}

function formatTimestampLabel(date, timezone) {
  if (!date || Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('en-US', {
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

function formatMarkerTimeLabel(marker, timezone) {
  const executedAt = markerExecutedAt(marker)
  const executionDate = executedAt ? new Date(executedAt) : null
  const executionLabel = formatTimestampLabel(executionDate, timezone)
  if (executionLabel) return executionLabel
  return formatTimestampLabel(marker?.time ? new Date(marker.time * 1000) : null, timezone)
}

function markerRangeCutoff(range) {
  if (range === '30d') return Date.now() / 1000 - 30 * 86400
  if (range === '6m') return Date.now() / 1000 - 182 * 86400
  if (range === '1y') return Date.now() / 1000 - 365 * 86400
  return 0
}

function resolveMarkerRangeDefault(timeframe, requested) {
  if (requested === '30d' || requested === '6m' || requested === '1y' || requested === 'all') return requested
  if (timeframe === 'weekly') return 'all'
  if (timeframe === 'daily') return '6m'
  return '30d'
}

// CE-04c-2-fix-3 — LWC v5 multi-pane lookup. Fifth chassis lookup-table
// family member. Maps each indicator kind to the logical pane name where
// its series render.
//
//   - `'price'`  → pane 0 (the candle pane, auto-created by createChart).
//   - `'volume'` → pane 1 (created explicitly via chart.addPane() at
//                  chart-init time).
//
// Future bottom-pane indicators (OBV = pane 2, etc.) extend
// this lookup. The resolvePane(paneRegistryRef, name) helper returns the
// numeric pane index for LWC v5's positional paneIndex argument on
// addLineSeries / addHistogramSeries / addAreaSeries (compat-shimmed via
// src/lib/chart/createChartCompat.js → addSeries(definition, opts, paneIdx)).
const DISPLAY_PANE_BY_KIND = Object.freeze({
  sma: 'price',
  ema: 'price',
  keltner: 'price',
  bollinger: 'price',
  donchian: 'price',
  volume: 'volume',
  vwap_1d: 'price',
  vwap_2d: 'price',
  vwap_wtd: 'price',
  vwap_mtd: 'price',
  vwap_ytd: 'price',
  vwap_earnings: 'price',
  vwap_earnings_2: 'price',
  pivots: 'price',
})

const LINE_STYLE_MAP = {
  solid: LineStyle.Solid,
  dashed: LineStyle.Dashed,
  dotted: LineStyle.Dotted,
}

function indicatorLineStyle(kind, configuredStyle) {
  if ((kind === 'sma' || kind === 'ema') && configuredStyle === 'dashed') {
    return LineStyle.LargeDashed
  }
  return LINE_STYLE_MAP[configuredStyle] ?? LineStyle.Solid
}

// CE-04g-UI-indicators-live-tick — Effect 3 (live-tick data pump) flushes
// at most once per browser paint. This keeps the active candle, volume, and
// existing indicator calculations on the same latest bar without an
// artificial fixed-delay window.
const LIVE_BOUNDARY_POLL_MS = 200
const LIVE_TIMEFRAME_SECONDS = Object.freeze({
  '1min': 60,
  '2min': 2 * 60,
  '5min': 5 * 60,
  '15min': 15 * 60,
  '30min': 30 * 60,
  '65min': 65 * 60,
})
const EXECUTION_TOOLTIP_HOVER_DELAY_MS = 140

export function shouldIgnoreClosedSessionLiveBar(correctedBar, latestSettledBar, {
  timeframe,
  marketSession = 'regular',
  nowMs = Date.now(),
  getMarketSessionForInstant = getMarketSession,
} = {}) {
  if (marketSession !== 'regular') return false
  if (!LIVE_TIMEFRAME_SECONDS[timeframe]) return false
  if (!correctedBar || correctedBar.time == null) return false
  if (!latestSettledBar || latestSettledBar.time == null) return false
  if (correctedBar.time > latestSettledBar.time) return false

  const session = getMarketSessionForInstant(new Date(nowMs))
  return session !== 'regular'
}

/**
 * CE-04c-2-fix-3 — Per-chart string-pane → numeric-pane index resolver.
 * Mints the next free index on first request, scoped to the per-chart-
 * instance `paneRegistryRef.current` Map. Mirrors the established
 * `src/hooks/useIndicator.js:resolvePaneForChart` pattern, specialized
 * here for per-instance ref scoping (no shared WeakMap — the new chassis
 * is self-contained per CE-04a rule §0). Throws on invalid input.
 *
 * **Live under LWC v5.** Returns the numeric pane index that the
 * reconcile loop passes positionally to `chart.addLineSeries(opts, idx)`
 * / `chart.addHistogramSeries(opts, idx)` / `chart.addAreaSeries(opts, idx)`
 * (compat-shimmed → v5's `chart.addSeries(SeriesDefinition, opts, idx)`).
 * Pane 0 is the price/candle pane (auto-created by createChart); pane 1
 * is the volume sub-pane (created explicitly via `chart.addPane()` at
 * chart-init time). New names mint the next free index in registration
 * order.
 *
 * @param {{ current: Map<string, number> | null }} paneRegistryRef
 * @param {string} paneName
 * @returns {number}
 */
function resolvePane(paneRegistryRef, paneName) {
  if (typeof paneName !== 'string' || paneName.length === 0) {
    throw new Error('resolvePane: invalid pane name')
  }
  const map = paneRegistryRef.current
  if (!map) throw new Error('resolvePane: registry not initialized')
  if (!map.has(paneName)) {
    map.set(paneName, map.size)
  }
  return map.get(paneName)
}

function removeChartComponent(chart, candleSeries, component) {
  if (!component) return
  if (component.__isBandFillPrimitive) {
    try { candleSeries?.detachPrimitive?.(component) } catch { /* noop */ }
    return
  }
  try { chart?.removeSeries?.(component) } catch { /* noop */ }
}

function setSeriesOrder(series, order) {
  if (!series || typeof series.setSeriesOrder !== 'function') return
  try { series.setSeriesOrder(order) } catch { /* noop */ }
}

function applyPricePaneVisualOrder({ candleSeries, seriesByPreset, indicators, visualTheme }) {
  let order = 0

  // LWC derives price-scale formatter precedence from the lowest series
  // order. Erevis price-pane indicators currently share the candle price
  // formatter shape, so this is acceptable while keeping OHLC visually on top.
  for (let i = indicators.length - 1; i >= 0; i--) {
    const ind = indicators[i]
    if (DISPLAY_PANE_BY_KIND[ind.kind] !== 'price') continue
    if (ind.style?.visible === false) continue
    const bucket = seriesByPreset.get(ind.presetId)
    if (!bucket) continue
    const components = COMPONENTS_BY_KIND[ind.kind] || []
    for (const component of components) {
      if (component.shape !== 'line') continue
      if (indicatorSectionStyle(ind, component.key, visualTheme).visible === false) continue
      setSeriesOrder(bucket[component.key], order)
      order += 1
    }
  }

  // Keep the OHLC series in front of every indicator line in the price pane.
  setSeriesOrder(candleSeries, order)
}

function projectComponentData(component, raw) {
  if (component.shape === 'histogram') return raw
  if (component.shape === 'fill') {
    return Array.isArray(raw)
      ? raw.map((p) => ({ time: p.time, upper: p.upper, lower: p.lower }))
      : []
  }
  return Array.isArray(raw)
    ? raw.map((p) => ({ time: p.time, value: p[component.dataKey] }))
    : (raw[component.key] || [])
}

function deepClonePlain(value) {
  if (value == null || typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map(deepClonePlain)
  const out = {}
  for (const k of Object.keys(value)) out[k] = deepClonePlain(value[k])
  return out
}

function makeIndicatorInstance(kind, presetId) {
  const params = DEFAULT_PARAMS_BY_KIND[kind]
  const style = DEFAULT_STYLE_BY_KIND[kind]
  return {
    presetId,
    kind,
    params: deepClonePlain(params),
    // `style.visible` (top-level) is the indicator-instance eye state set
    // by the legend. Per-component `style.<componentKey>.visible` is the
    // per-component checkbox in the popover Style tab. Reconcile honors
    // both: instance-hidden removes ALL component series; component-hidden
    // removes only that component's series. Per CE-04c rule §20.
    style: { visible: true, ...deepClonePlain(style) },
  }
}

// CE-04c-2 — Volume auto-seed for the demo route. In `mode='static'` the
// chart mounts with Volume already in the indicator collection, mirroring
// the eventual server-side seed pattern that will resolve from the
// `indicator_defaults` table in CE-04g (per chart-component-contract.md
// §9 "Default-volume seeding"). The static-mode default lives here as a
// transitional internal default rather than a new prop, to keep the
// frozen contract §14 unchanged. CE-04g moves the seed source from this
// init helper to the Supabase resolver chain.
function initialIndicatorsForMode(mode, storageScope = DEFAULT_CHART_STORAGE_SCOPE) {
  if (mode === 'static') return [
    makeIndicatorInstance('volume', 'volume-1'),
    makeIndicatorInstance('vwap_1d', 'vwap_1d-1'),
    makeIndicatorInstance('vwap_2d', 'vwap_2d-1'),
    makeIndicatorInstance('vwap_wtd', 'vwap_wtd-1'),
    makeIndicatorInstance('vwap_mtd', 'vwap_mtd-1'),
    makeIndicatorInstance('vwap_ytd', 'vwap_ytd-1'),
    makeIndicatorInstance('pivots', 'pivots-1'),
  ]
  if (mode === 'live') {
    return (['strategy', 'market-health'].includes(storageScope) || storageScope.startsWith('relative-strength-')) ? [] : [makeIndicatorInstance('volume', 'volume-1')]
  }
  return []
}

function initialCountersForMode(mode, storageScope = DEFAULT_CHART_STORAGE_SCOPE) {
  if (mode === 'static') return {
    volume: 1, vwap_1d: 1, vwap_2d: 1, vwap_wtd: 1,
    vwap_mtd: 1, vwap_ytd: 1, pivots: 1,
  }
  if (mode === 'live') return ['strategy', 'market-health'].includes(storageScope) ? {} : { volume: 1 }
  return {}
}

// Indicators are stored in the server template as a map keyed by timeframe.
// Each slot holds that TF's own indicator array, so a 5min SMA never bleeds
// onto daily. Entries are validated against `AVAILABLE_INDICATORS` so a
// future build that drops an indicator kind doesn't crash on stale server data.
const SERVER_INDICATOR_TEMPLATE_NAME = 'workspace-chart-indicators-v1'
const SERVER_INDICATOR_TEMPLATE_DESCRIPTION = 'Default workspace chart indicator map'
const SERVER_INDICATOR_SYNC_DELAY_MS = 250

function isValidIndicatorEntry(entry, allowedKinds) {
  if (!entry || typeof entry !== 'object') return false
  if (typeof entry.kind !== 'string' || !allowedKinds.has(entry.kind)) return false
  if (typeof entry.presetId !== 'string' || entry.presetId.length === 0) return false
  if (entry.params == null || typeof entry.params !== 'object') return false
  if (entry.style == null || typeof entry.style !== 'object') return false
  return true
}

// Filter a raw array to entries whose `kind` is still known. Shared by
// the mount read, the TF-change read, and the old-array migration path.
function validateIndicators(arr) {
  if (!Array.isArray(arr)) return []
  const allowed = new Set(AVAILABLE_INDICATORS.map((opt) => opt.kind))
  return arr.filter((e) => isValidIndicatorEntry(e, allowed))
}

function normalizeIndicatorMap(value) {
  const source = value?.timeframes && typeof value.timeframes === 'object' && !Array.isArray(value.timeframes)
    ? value.timeframes
    : value
  if (!source || typeof source !== 'object' || Array.isArray(source)) return null
  const map = {}
  for (const [tf, arr] of Object.entries(source)) {
    if (typeof tf !== 'string' || !Array.isArray(arr)) continue
    map[tf] = validateIndicators(arr)
  }
  return Object.keys(map).length > 0 ? map : null
}

function serverIndicatorTemplateName(storageScope = DEFAULT_CHART_STORAGE_SCOPE) {
  return storageScope === DEFAULT_CHART_STORAGE_SCOPE
    ? SERVER_INDICATOR_TEMPLATE_NAME
    : `${SERVER_INDICATOR_TEMPLATE_NAME}:${storageScope}`
}

function serverIndicatorTemplateDescription(storageScope = DEFAULT_CHART_STORAGE_SCOPE) {
  return storageScope === DEFAULT_CHART_STORAGE_SCOPE
    ? SERVER_INDICATOR_TEMPLATE_DESCRIPTION
    : `${SERVER_INDICATOR_TEMPLATE_DESCRIPTION} (${storageScope})`
}

function indicatorTemplatePayload(map, storageScope = DEFAULT_CHART_STORAGE_SCOPE) {
  return {
    version: 1,
    context: storageScope,
    timeframes: map,
  }
}

async function getAuthenticatedUserId() {
  return resolvePreferenceUserId()
}

function readServerIndicatorsForTimeframe(activeTimeframe, mode, map) {
  if (mode !== 'live') return null
  const arr = map?.[activeTimeframe]
  return Array.isArray(arr) ? validateIndicators(arr) : null
}

// Initial id counters — must reflect whatever was rehydrated so subsequent
// adds keep climbing instead of colliding with persisted preset ids.
function initialCountersWithPersistence(mode, indicatorList, storageScope = DEFAULT_CHART_STORAGE_SCOPE) {
  const base = initialCountersForMode(mode, storageScope)
  for (const ind of indicatorList) {
    const m = /^(.+)-(\d+)$/.exec(ind.presetId)
    if (!m) continue
    const [, kind, nStr] = m
    if (kind !== ind.kind) continue
    const n = parseInt(nStr, 10)
    if (!Number.isFinite(n)) continue
    base[kind] = Math.max(base[kind] || 0, n)
  }
  return base
}

function setNestedField(obj, path, value) {
  const parts = path.split('.')
  const next = { ...obj }
  let cursor = next
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i]
    cursor[k] = { ...(cursor[k] ?? {}) }
    cursor = cursor[k]
  }
  cursor[parts[parts.length - 1]] = value
  return next
}

function reorderIndicatorList(indicators, draggedPresetId, targetPresetId, position) {
  const fromIndex = indicators.findIndex((ind) => ind.presetId === draggedPresetId)
  const targetIndex = indicators.findIndex((ind) => ind.presetId === targetPresetId)
  if (fromIndex < 0 || targetIndex < 0 || fromIndex === targetIndex) return indicators

  const next = indicators.slice()
  const [moved] = next.splice(fromIndex, 1)
  const targetAfterRemoval = next.findIndex((ind) => ind.presetId === targetPresetId)
  if (targetAfterRemoval < 0) return indicators
  const insertIndex = position === 'after' ? targetAfterRemoval + 1 : targetAfterRemoval
  next.splice(insertIndex, 0, moved)
  return next
}

function TradeChart({
  symbol,
  mode,
  markers = [],
  avwapAnchors = [],
  setupLines = null,
  engineSignals = [],
  positionLines = null,
  bars,
  cursorTime,
  onCursorChange,
  readOnly,
  linkedToPositionId = null,
  onLinkChange,
  onSymbolChange,
  symbolSearchEnabled = true,
  onAvwapAnchorSave,
  // CE-04g-UI-a — live-mode props (all optional). `timeframe` arrives as
  // an initial seed; the chassis owns the active timeframe internally so
  // the embedded TF selector can drive refetches without the parent re-
  // rendering. `onTimeframeChange` lets the parent observe transitions.
  // CE-04g-UI-chrome-1 — when `composedChrome=true` the chassis also syncs
  // `activeTimeframe` from this prop on change, so a parent-owned top bar
  // can drive timeframe changes via setState.
  timeframe: initialTimeframe = 'daily',
  timezone = 'America/Chicago',
  entryDate = null,
  assetType = 'equity',
  onTimeframeChange,
  // CE-04g-UI-chrome-1 — chart-type toggle (matches production interface).
  // `'candlestick'` (default) renders addCandlestickSeries; `'bar'` renders
  // addBarSeries with the workspace bar colors.
  chartType: suppliedChartType = 'candlestick',
  marketSession: suppliedMarketSession = 'regular',
  // CE-04g-UI-chrome-1 — `'workspace'` (default) drives the production
  // light-on-dark token set extracted into `chartTokens.js`. Reserved for
  // future themes (e.g., a denser scanner thumbnail variant).
  theme = 'workspace',
  // CE-04g-UI-chrome-1 — when a parent renders its own ChartTopBar (or
  // future shared top bar), set this true to suppress the chassis's
  // internal live-mode timeframe selector and scale controls.
  composedChrome = false,
  compact = false,
  backgroundRegimeSpans = null,
  breadthIndicator = null,
  onVisibleTimeRangeChange = null,
  markerRangeDefault = null,
  storageScope = DEFAULT_CHART_STORAGE_SCOPE,
  fallbackStorageScope = null,
  preferenceUserId = null,
  keyboardActive = true,
  drawingTool = 'select',
  onDrawingToolChange = null,
  crosshairEnabled = false,
  onCrosshairMove = null,
  onChartReady = null,
  onPriceAlertCreate = null,
  priceAlertsVisible = true,
  candleAuthority = 'default',
  sessionPrice = null,
  sessionPriceSession = null,
  sessionPriceAuthoritative = false,
  quickMeasureEnabled = false,
  retainedVwapEnabled = false,
  retainedVwapToolbarTarget = null,
}, ref) {
  // Defaults rule per contract §2: `readOnly` defaults to `mode !== 'live'`.
  const effectiveReadOnly = readOnly ?? (mode !== 'live')

  // ── Ephemeral state (per chart-component-contract.md §6) ──────────────
  // `indicators` and `popoverPresetId` are CE-04a additions; both reset on
  // `symbol` change. The placeholder counter from CE-02 is retained because
  // existing tests lock the discriminator (`symbol`) via `data-reset-count`
  // and CE-04a doesn't change the discriminator.
  const [resetCount, setResetCount] = useState(0)
  // CE-04g-UI-a — the chassis owns the active timeframe internally. Declared
  // before `indicators` so the per-TF persistence read below can key off it;
  // on mount `activeTimeframe === initialTimeframe`.
  const [activeTimeframe, setActiveTimeframe] = useState(initialTimeframe)
  const activeTimeframeRef = useRef(activeTimeframe)
  const chartStorageScope = storageScope || DEFAULT_CHART_STORAGE_SCOPE
  const auth = useAuth()
  const { privacyMode } = usePrivacy()
  const sharedEligible = !compact && !chartStorageScope.startsWith('relative-strength') && chartStorageScope !== 'market-health'
  const shared = useSharedTimeframeProfiles(sharedEligible ? (preferenceUserId || auth?.user?.id) : null)
  const sharedEnabled = shared.status !== 'disabled'
  const sharedProfile = shared.profiles[activeTimeframe]
  const sharedAnchorAppearances = useMemo(() => sharedEnabled
    ? resolveAnchoredVwapAppearances(shared.profiles, activeTimeframe) : undefined,
  [sharedEnabled, shared.profiles, activeTimeframe])
  const sharedCurrent = useRef(null)
  sharedCurrent.current = { shared, timeframe: activeTimeframe, profile: sharedProfile }
  const sharedReady = !sharedEnabled || Boolean(sharedProfile)
  const visualTheme = useVisualTheme()
  const chartType = sharedProfile?.controls?.chartTypesByTheme?.[visualTheme] || suppliedChartType
  const marketSession = sharedProfile?.controls?.marketSession || suppliedMarketSession
  const visibleTimeRangesRef = useRef({})
  const visibleLogicalRangesRef = useRef({})
  const [visibleRangePreferenceNonce, setVisibleRangePreferenceNonce] = useState(0)
  const [serverChartPreferencesHydrated, setServerChartPreferencesHydrated] = useState(false)
  // CE-04c-2 — `mode='static'` mounts with Volume auto-seeded. Per scope
  // rule §8 + chart-component-contract.md §9 "Default-volume seeding".
  const [indicators, setIndicators] = useState(
    () => initialIndicatorsForMode(mode, chartStorageScope)
  )
  const { templates: indicatorTemplates, save: saveIndicatorTemplate } = useIndicatorTemplateLibrary(preferenceUserId || auth?.user?.id)
  const [serverIndicatorHydrated, setServerIndicatorHydrated] = useState(mode !== 'live')
  const chartBootstrapReady = sharedEnabled ? Boolean(sharedProfile) : mode !== 'live' || (serverChartPreferencesHydrated && serverIndicatorHydrated)
  const chartPreferencesReadyRef = useRef(chartBootstrapReady)
  chartPreferencesReadyRef.current = sharedEnabled ? sharedReady : chartBootstrapReady
  const [popoverPresetId, setPopoverPresetId] = useState(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settingsActiveTab, setSettingsActiveTab] = useState('symbol')
  const [chartPreferences, setChartPreferences] = useState(() => readStoredChartPreferences(chartStorageScope))
  const chartSettingsByTheme = chartPreferences.settingsByTheme
  const chartGeometrySettings = chartPreferences.geometrySettings
  const chartSettings = chartSettingsByTheme[visualTheme] || DEFAULT_CHART_SETTINGS_BY_THEME[visualTheme]
  const chartCanvasAppearance = useMemo(() => chartCanvasOptions(chartSettings), [chartSettings])
  const quickMeasureAppearance = useMemo(() => quickMeasureStyle(chartSettings), [chartSettings])
  const activeChartMargins = useMemo(
    () => ({ ...chartMarginsForTimeframe(chartGeometrySettings, activeTimeframe), ...(compact ? { visibleBars: 63, rightOffset: 21 } : {}) }),
    [chartGeometrySettings, activeTimeframe, compact]
  )
  const activeChartScaleMargins = useMemo(
    () => chartScaleMargins(activeChartMargins),
    [activeChartMargins]
  )
  const chartSettingsRef = useRef(chartSettings)
  const priceStyleKey = useMemo(() => priceBarStyleKey(chartSettings), [chartSettings])
  const [executionAnnotations, setExecutionAnnotations] = useState([])
  const [hoveredExecutionAnnotation, setHoveredExecutionAnnotation] = useState(null)
  const [selectedStopPrice, setSelectedStopPrice] = useState(null)
  const markerRange = chartGeometrySettings.markerRange || resolveMarkerRangeDefault(activeTimeframe, markerRangeDefault)
  // Per-kind monotonic counter. With CE-04b's one-instance-per-type cap the
  // first add for each kind yields `${kind}-1`; the counter only ever climbs
  // higher if a future multi-instance code path lands. Reset on `symbol`
  // change per chart-component-contract.md §6. CE-04c-2 — initialized
  // with `volume: 1` for `mode='static'` so the auto-seeded Volume's id
  // doesn't collide if it's removed and re-added.
  const idCountersByKindRef = useRef(initialCountersWithPersistence(mode, indicators, chartStorageScope))
  const isFirstRunRef = useRef(true)
  // CE-04g-UI-chrome-1-fix-3 — `committedTfRef` records which timeframe the
  // current `indicators` value belongs to. The write effect refuses to run
  // while it disagrees with `activeTimeframe`, so a TF switch can never
  // stamp the outgoing TF's collection onto the incoming TF's slot.
  // `tfWatchFirstRunRef` skips the prop-watcher's mount run — the `indicators`
  // useState initializer already performed the first read.
  const committedTfRef = useRef(activeTimeframe)
  const tfWatchFirstRunRef = useRef(true)
  const serverIndicatorSaveTimerRef = useRef(null)
  const serverIndicatorMapRef = useRef({})
  // CE-04g-UI-mobile-workspace-followup — explicit-action gate for the
  // server template upsert. Hydration, first render, TF switches, and
  // symbol switches must NEVER publish to `indicator_templates`; only a
  // deliberate indicator mutation (add / remove / reorder / visibility /
  // popover edit) flips this ref, after which saves flow for the rest of
  // the session. Without this gate, any device that merely opened the
  // page published its localStorage residue as the user-global canonical
  // template (the 2026-06-09 mobile-pollution incident).
  const userIndicatorActionRef = useRef(false)

  useEffect(() => { activeTimeframeRef.current = activeTimeframe }, [activeTimeframe])

  useEffect(() => {
    if (sharedEnabled) return
    if (mode !== 'live') {
      setServerIndicatorHydrated(true)
      return
    }

    let cancelled = false
    setServerIndicatorHydrated(false)

    async function hydrateServerIndicatorTemplate() {
      try {
        const userId = await getAuthenticatedUserId()
        if (!userId) return

        let { data, error } = await supabase
          .from('indicator_templates')
          .select('preset_snapshots')
          .eq('user_id', userId)
          .eq('name', serverIndicatorTemplateName(chartStorageScope))
          .eq('is_active', true)
          .maybeSingle()

        if (!error && !data && fallbackStorageScope) {
          const fallback = await supabase.from('indicator_templates').select('preset_snapshots')
            .eq('user_id', userId).eq('name', serverIndicatorTemplateName(fallbackStorageScope))
            .eq('is_active', true).maybeSingle()
          data = fallback.data
          error = fallback.error
        }
        if (cancelled || error) return

        const remoteMap = normalizeIndicatorMap(data?.preset_snapshots)
        if (!remoteMap) return

        serverIndicatorMapRef.current = remoteMap
        if (userIndicatorActionRef.current) return
        const currentTf = activeTimeframeRef.current
        const nextIndicators = Array.isArray(remoteMap[currentTf])
          ? validateIndicators(remoteMap[currentTf])
          : initialIndicatorsForMode(mode, chartStorageScope)
        committedTfRef.current = currentTf
        idCountersByKindRef.current = initialCountersWithPersistence(mode, nextIndicators, chartStorageScope)
        setIndicators(nextIndicators)
      } finally {
        if (!cancelled) setServerIndicatorHydrated(true)
      }
    }

    hydrateServerIndicatorTemplate()

    return () => {
      cancelled = true
    }
  }, [mode, chartStorageScope, fallbackStorageScope, sharedEnabled])


  // Keep the server-backed indicator map current in memory so publishing one
  // timeframe never drops another timeframe's hydrated slot.
  useEffect(() => {
    if (mode !== 'live') return
    // Race guard: during a TF switch React may render with the new
    // `activeTimeframe` before `indicators` has been swapped to that TF's
    // collection. `committedTfRef` only advances in lockstep with the
    // indicator swap, so a mismatch means `indicators` is still the old
    // TF's — skip the write until they reconcile.
    if (committedTfRef.current !== activeTimeframe) return
    serverIndicatorMapRef.current = {
      ...(serverIndicatorMapRef.current || {}),
      [activeTimeframe]: indicators,
    }
  }, [mode, activeTimeframe, indicators, chartStorageScope])

  useEffect(() => {
    if (sharedEnabled) return
    if (mode !== 'live') return
    if (!serverIndicatorHydrated) return
    if (committedTfRef.current !== activeTimeframe) return
    // CE-04g-UI-mobile-workspace-followup — server publishes require an
    // explicit indicator action this session. Hydration completing (which
    // flips `serverIndicatorHydrated` even on a failed/empty fetch) and
    // the resulting `setIndicators` must not upsert device-local residue
    // or auto-seed defaults as the canonical user-global template.
    if (!userIndicatorActionRef.current) return

    const map = { ...(serverIndicatorMapRef.current || {}) }
    map[activeTimeframe] = indicators
    serverIndicatorMapRef.current = map

    if (serverIndicatorSaveTimerRef.current) {
      clearTimeout(serverIndicatorSaveTimerRef.current)
    }

    let cancelled = false
    serverIndicatorSaveTimerRef.current = setTimeout(async () => {
      const userId = await getAuthenticatedUserId()
      if (!userId || cancelled) return
      await supabase
        .from('indicator_templates')
        .upsert({
          user_id: userId,
          name: serverIndicatorTemplateName(chartStorageScope),
          description: serverIndicatorTemplateDescription(chartStorageScope),
          preset_snapshots: indicatorTemplatePayload(map, chartStorageScope),
          is_active: true,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,name' })
    }, SERVER_INDICATOR_SYNC_DELAY_MS)

    return () => {
      cancelled = true
      if (serverIndicatorSaveTimerRef.current) {
        clearTimeout(serverIndicatorSaveTimerRef.current)
        serverIndicatorSaveTimerRef.current = null
      }
    }
  }, [mode, activeTimeframe, indicators, serverIndicatorHydrated, chartStorageScope, sharedEnabled])

  useEffect(() => {
    chartSettingsRef.current = chartSettings
  }, [chartSettings])

  const persistChartPreferences = useCallback((nextPreferences) => {
    if (!nextPreferences || !chartPreferencesReadyRef.current) return
    const current = sharedCurrent.current
    if (current.shared.status !== 'disabled') {
      void current.shared.store.edit(current.timeframe, { settings: chartSettingsPreferencePayload(nextPreferences.geometrySettings, nextPreferences.settingsByTheme).settings })
      return
    }
    void writeServerChartPreference({
      scope: chartStorageScope,
      preferenceKey: CHART_PREFERENCE_KEYS.settings,
      preferences: chartSettingsPreferencePayload(
        nextPreferences.geometrySettings,
        nextPreferences.settingsByTheme
      ),
    })
  }, [chartStorageScope])

  const editChartPreferences = useCallback((updater) => {
    const current = sharedCurrent.current
    if (current.shared.status !== 'disabled') {
      if (!current.profile) return
      const next = updater(normalizeChartPreferencesPayload(current.profile.settings))
      persistChartPreferences(next)
      return
    }
    setChartPreferences(prev => {
      const next = updater(prev)
      persistChartPreferences(next)
      return next
    })
  }, [persistChartPreferences])

  const updateChartSettings = useCallback((patch) => {
    const current = sharedCurrent.current
    if (current.shared.status !== 'disabled' && !current.profile) return
    editChartPreferences(prev => {
      const next = {
        ...prev,
        settingsByTheme: {
          ...prev.settingsByTheme,
          [visualTheme]: normalizeChartSettings({
            ...(prev.settingsByTheme[visualTheme] || DEFAULT_CHART_SETTINGS_BY_THEME[visualTheme]),
            ...patch,
          }, visualTheme),
        },
      }
      return next
    })
  }, [editChartPreferences, visualTheme])

  const updateChartMargins = useCallback((patch) => {
    const current = sharedCurrent.current
    if (current.shared.status !== 'disabled' && !current.profile) return
    editChartPreferences(prev => {
      const current = normalizeChartGeometrySettings(prev.geometrySettings)
      const currentSlot = chartMarginsForTimeframe(current, activeTimeframe)
      const nextSlot = normalizeChartMarginSlot({
        ...currentSlot,
        ...(patch.topPercent != null && patch.topPercent !== '' ? { top: Number(patch.topPercent) / 100 } : null),
        ...(patch.bottomPercent != null && patch.bottomPercent !== '' ? { bottom: Number(patch.bottomPercent) / 100 } : null),
        ...(patch.rightOffset != null && patch.rightOffset !== '' ? { rightOffset: Number(patch.rightOffset) } : null),
        ...(patch.defaultView != null ? { defaultView: patch.defaultView } : null),
        ...(patch.dynamicSchedule != null ? { dynamicSchedule: patch.dynamicSchedule } : null),
        ...(patch.candlesBeforeOpen != null && patch.candlesBeforeOpen !== '' ? { candlesBeforeOpen: Number(patch.candlesBeforeOpen) } : null),
        ...(patch.visibleBars != null && patch.visibleBars !== '' ? { visibleBars: Number(patch.visibleBars) } : null),
      }, activeTimeframe)
      const next = {
        ...prev,
        geometrySettings: normalizeChartGeometrySettings({
          ...current,
          marginsByTimeframe: {
            ...current.marginsByTimeframe,
            [activeTimeframe]: nextSlot,
          },
        }),
      }
      return next
    })
  }, [activeTimeframe, editChartPreferences])

  const updateChartBehavior = useCallback((patch) => {
    const current = sharedCurrent.current
    if (current.shared.status !== 'disabled' && !current.profile) return
    editChartPreferences(prev => {
      const next = {
        ...prev,
        geometrySettings: normalizeChartGeometrySettings({ ...prev.geometrySettings, ...(typeof patch === 'function' ? patch(prev.geometrySettings) : patch) }),
      }
      return next
    })
  }, [editChartPreferences])

  // ── Live-mode state (CE-04g-UI-a) ───────────────────────────────────
  const [liveStatus, setLiveStatus] = useState('idle')
  const [runtimeFanoutStatus, setRuntimeFanoutStatus] = useState('idle')
  const [browserOffline, setBrowserOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false
  )
  const [hasRenderedLiveCandles, setHasRenderedLiveCandles] = useState(false)
  const autoScale = chartGeometrySettings.autoScale
  const logScale = chartGeometrySettings.logScale
  const barStateRef = useRef({ time: null, open: null, high: -Infinity, low: Infinity, close: null, sessionDateKey: null, clockBoundary: false, hasCorrection: false })
  const extendedPriceLineRef = useRef(null)
  const extendedPriceLineSessionRef = useRef(null)
  const quoteSessionPriceLineRef = useRef(null)
  const quoteSessionPriceLineSeriesRef = useRef(null)
  const externalSessionPriceActiveRef = useRef(false)
  const liveCandlesRef = useRef([])
  const liveBoundaryTimerRef = useRef(null)
  const carsRequestSignatureRef = useRef(null)
  const defaultViewRef = useRef(null)
  const suppressVisibleRangePersistenceRef = useRef(false)
  const suppressVisibleRangePersistenceTimerRef = useRef(null)
  const previousChartTypeRef = useRef(chartType)
  const previousTimeframeRef = useRef(activeTimeframe)
  const previousStyleViewKeyRef = useRef(null)
  const styleViewportRef = useRef(null)
  const visibleRangeBarCountRef = useRef(0)
  // CE-04g-UI-indicators-live-tick — mirror `displayCandles` to a ref so
  // Effect 3 (live pump) can read the latest settled bars without listing
  // `displayCandles` as a dep (which would re-fire it on every settled-bar
  // boundary — Effect 2 already covers that path via setData). Synced on
  // every render by an effect below.
  const displayCandlesRef = useRef([])
  const staticVwapCandlesRef = useMemo(() => ({ current: bars }), [bars])
  const chartExportIdRef = useRef(null)
  if (chartExportIdRef.current == null) {
    chartExportIdRef.current = createChartExportId(symbol)
  }
  const chartExportDebugEnabled = isChartExportDebugEnabled()
  const latestChartExportStateRef = useRef(null)
  const scrollbackInFlightRef = useRef(false)
  const scrollbackVisibleRangeRef = useRef(null)
  const liveDataViewKeyRef = useRef(null)
  const latencySpanRef = useRef(null)
  const pendingLatencyStartRef = useRef(null)

  const isOption = assetType === 'option' || isOCCSymbol(symbol)
  const externalSessionPriceSpec = useMemo(
    () => isOption ? null : buildQuoteSessionPriceLine(sessionPrice, sessionPriceSession),
    [isOption, sessionPrice, sessionPriceSession],
  )
  // Options must not inherit stock pre/post/overnight overlays from either
  // workspace quotes or the fallback stream. Candle prices remain unchanged.
  externalSessionPriceActiveRef.current = isOption || sessionPriceAuthoritative || externalSessionPriceSpec != null

  useEffect(() => {
    const handleOffline = () => setBrowserOffline(true)
    const handleOnline = () => setBrowserOffline(false)
    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)
    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }, [])
  // CE-04g-UI-indicators-live-tick — per-component last-rendered series
  // time. Required for the Lightweight Charts series.update() contract:
  // the new item's time MUST be >= the latest rendered item's time. Shape:
  // { [presetId]: { [componentKey]: number | null } }. Reset to null when
  // Effect 1 creates a component; updated by Effect 2 (on setData) and
  // Effect 3 (on series.update).
  const lastSeriesTimeRef = useRef({})
  // CE-04g-UI-indicators-live-tick — one pending paint is enough: each
  // EventSource tick updates the in-memory forming bar immediately, then the
  // next animation frame renders its latest state. The indicator formulas,
  // parameters, and source bars remain unchanged.
  const liveIndicatorHistoryDirtyRef = useRef(false)
  const livePumpFrameRef = useRef(null)
  const livePumpUsesAnimationFrameRef = useRef(false)
  // CE-04g-UI-indicators-live-tick — Effect 3's only dep that toggles per
  // tick. Bumped from the tick subscriber via the throttle state machine.
  const [livePumpNonce, setLivePumpNonce] = useState(0)
  // CE-04g-UI-indicators-live-tick — bumped by each chart-init effect AFTER
  // `chartRef.current = chart` so Effect 1 (lifecycle, no `displayCandles`
  // dep) re-fires once the chart instance exists. In live mode, the chart
  // is created only after `displayCandles` becomes non-empty, which would
  // not naturally re-trigger Effect 1 without this signal.
  const [chartReadyNonce, setChartReadyNonce] = useState(0)

  const optContract = useMemo(
    () => (isOption && symbol ? parseOCC(symbol) : null),
    [isOption, symbol]
  )

  const fetchDuration = useMemo(() => {
    const tf = IBKR_TF[activeTimeframe] || IBKR_TF['daily']
    if ((activeTimeframe === 'daily' || activeTimeframe === 'weekly') && entryDate) {
      const entryMs = new Date(entryDate).getTime()
      const daysSinceEntry = Math.ceil((Date.now() - entryMs) / (1000 * 60 * 60 * 24))
      const bufferedDays = Math.ceil(daysSinceEntry * 1.2)
      const years = Math.min(5, Math.max(activeTimeframe === 'weekly' ? 5 : 3, Math.ceil(bufferedDays / 365)))
      return `${years} Y`
    }
    return tf.duration
  }, [activeTimeframe, entryDate])
  const chartParityMode = mode === 'live' && marketDataChartParityModeEnabled()

  const latencyKey = `${mode}:${symbol || ''}:${activeTimeframe || ''}:${marketSession || ''}`
  if (mode === 'live') {
    const currentSpan = latencySpanRef.current
    if (!currentSpan || currentSpan.key !== latencyKey) {
      const pending = pendingLatencyStartRef.current
      const pendingMatches = pending?.key === latencyKey
      const reason = pendingMatches
        ? pending.reason
        : (
          currentSpan?.symbol !== symbol
            ? 'symbol-load'
            : currentSpan?.timeframe !== activeTimeframe
              ? 'timeframe-switch'
              : currentSpan?.session !== marketSession
                ? 'session-change'
                : 'chart-load'
        )
      latencySpanRef.current = {
        key: latencyKey,
        symbol,
        timeframe: activeTimeframe,
        session: marketSession,
        reason,
        startedAt: pendingMatches ? pending.startedAt : marketDataLatencyNow(),
        startedWallTs: pendingMatches ? pending.startedWallTs : Date.now(),
        firstPaintAt: null,
        firstLiveUpdateAt: null,
      }
      if (pendingMatches) pendingLatencyStartRef.current = null
    }
  }

  const markPendingLatencySpan = useMemo(() => createMarkPendingLatencySpan(symbol, activeTimeframe, marketSession, mode, pendingLatencyStartRef), [activeTimeframe, marketSession, mode, symbol])

  // Fetch the NYSE row from `trading_calendars` for VWAP windowing.
  // CE-04g-UI-vwap-anchor: WTD post-holiday-Monday anchor.
  // CE-04g-UI-vwap-window: 2-day rolling window's previous-trading-day
  // walker (skips holidays + weekends). Other VWAP windows ignore it.
  const { tradingCalendar } = useTradingCalendar()
  const earningsVwapTiming = indicators.find(ind => ind.kind === 'vwap_earnings')?.params?.anchor_timing || 'earnings-date'
  const earningsVwapVisible = indicators.some(ind => ind.kind === 'vwap_earnings'
    && ind.style?.visible !== false && ind.style?.vwap?.visible !== false)
  const earningsVwapAnchor = useEarningsVwapAnchor(symbol,
    earningsVwapVisible && mode === 'live' && !isOption && assetType === 'equity',
    preferenceUserId || auth?.user?.id, tradingCalendar, earningsVwapTiming)
  const twoEarningsVwapTiming = indicators.find(ind => ind.kind === 'vwap_earnings_2')?.params?.anchor_timing || 'earnings-date'
  const twoEarningsVwapVisible = indicators.some(ind => ind.kind === 'vwap_earnings_2'
    && ind.style?.visible !== false && ind.style?.vwap?.visible !== false)
  const twoEarningsVwapAnchor = useEarningsVwapAnchor(symbol,
    twoEarningsVwapVisible && mode === 'live' && !isOption && assetType === 'equity',
    preferenceUserId || auth?.user?.id, tradingCalendar, twoEarningsVwapTiming, 2)

  // CE-04g-UI-pivots-popover — short window of daily bars so the chart can
  // derive Classic Daily pivots from the prior trading session's OFFICIAL
  // daily OHLC (TV-aligned) instead of an RTH intraday aggregate. Live
  // mode only — static mode already receives pre-built bars from the
  // caller and the chart cannot pick a different source.
  const { dailyBars: dailyPivotBars } =
    useDailyPivotSource(symbol, needsDailyPivotSource(indicators, mode, isOption) && symbol !== 'MES1!', timezone)

  const hasVisibleCars = indicators.some(
    (i) => i.kind === 'cars_v1' && i.style?.visible !== false
  )
  const carsDailyEnabled = mode === 'live' && activeTimeframe === 'daily' && hasVisibleCars
  const {
    primaryBars: carsAdjustedPrimaryBars,
    benchmarkBars: carsBenchmarkBars,
    status: carsAdjustedStatus,
  } = useCarsAdjustedDailySource(symbol, 'SPY', carsDailyEnabled, timezone)

  const scheduleLivePump = useCallback(() => {
    if (livePumpFrameRef.current != null) return
    const flush = () => {
      livePumpFrameRef.current = null
      livePumpUsesAnimationFrameRef.current = false
      setLivePumpNonce(n => n + 1)
    }
    if (typeof requestAnimationFrame === 'function') {
      livePumpUsesAnimationFrameRef.current = true
      livePumpFrameRef.current = requestAnimationFrame(flush)
    } else {
      livePumpFrameRef.current = setTimeout(flush, 0)
    }
  }, [])

  const applyCorrectedLiveBar = useCallback((correctedBar) => {
    const result = applyFormingBarCorrection(barStateRef.current, correctedBar)
    const correctedEpochMs = Number(correctedBar?.eventEpochMs ?? correctedBar?.rawEpochMs)
    const correctedSessionDateKey = Number.isFinite(correctedEpochMs)
      ? getEtSessionDateKey(correctedEpochMs)
      : null
    if (
      correctedBar?.lastPriceOnly
      && !result.matchesCurrent
      && barStateRef.current?.time != null
      && correctedBar.time < barStateRef.current.time
    ) return
    if (result.matchesCurrent) {
      barStateRef.current = correctedSessionDateKey
        ? { ...result.nextBarState, sessionDateKey: correctedSessionDateKey }
        : result.nextBarState
    } else if (
      correctedBar
      && (barStateRef.current?.time == null || correctedBar.time >= barStateRef.current.time)
    ) {
      barStateRef.current = {
        ...barStateRef.current,
        time: correctedBar.time,
        rawEpochMs: correctedBar.rawEpochMs ?? null,
        open: correctedBar.open,
        high: correctedBar.high,
        low: correctedBar.low,
        close: correctedBar.close,
        volume: correctedBar.volume,
        lastPriceRawEpochMs: correctedBar.lastPriceOnly
          ? (correctedBar.eventEpochMs ?? correctedBar.rawEpochMs ?? null)
          : null,
        sessionDateKey: correctedSessionDateKey ?? barStateRef.current?.sessionDateKey ?? null,
        clockBoundary: false,
        hasCorrection: true,
      }
    }
    const merged = result.merged
    if (!merged) return
    const source = liveCandlesRef.current?.length
      ? liveCandlesRef.current
      : displayCandlesRef.current
    const reconciliation = reconcileRuntimeCandle(source, merged)
    if (!reconciliation.accepted) return
    if (candleSeriesRef.current) {
      updateLivePriceBar(
        chartRef.current,
        candleSeriesRef.current,
        stylePriceBar(
          merged,
          previousRenderedBarForUpdate(displayCandlesRef.current, merged),
          chartType,
          chartSettingsRef.current,
        ),
        true,
      )
    }
    updateFixedOhlcBarPrimitive(composeEffectiveBars(displayCandlesRef.current, merged))
    // The runtime owns the active bar until the next history refresh. Keep a
    // client-owned immutable tail so React cannot re-point the live pump at
    // the stale HTTP snapshot after an SSE correction.
    liveCandlesRef.current = reconciliation.bars
    displayCandlesRef.current = reconciliation.bars
    scheduleLivePump()
  }, [chartType, scheduleLivePump])

  const applyClockBoundaryLiveBar = useCallback(() => {
    const boundaryBar = buildLiveBoundaryPlaceholder(barStateRef.current, {
      nowMs: Date.now(),
      timeframe: activeTimeframe,
      timezone,
      getMarketSession,
      getEtSessionDateKey,
      floorToBarPeriod,
    })
    if (!boundaryBar) return false

    const sessionDateKey = getEtSessionDateKey(boundaryBar.rawEpochMs)
    barStateRef.current = {
      ...barStateRef.current,
      time: boundaryBar.time,
      rawEpochMs: boundaryBar.rawEpochMs,
      open: boundaryBar.open,
      high: boundaryBar.high,
      low: boundaryBar.low,
      close: boundaryBar.close,
      volume: boundaryBar.volume,
      sessionDateKey,
      clockBoundary: true,
      hasCorrection: true,
    }

    if (candleSeriesRef.current) {
      updateLivePriceBar(
        chartRef.current,
        candleSeriesRef.current,
        stylePriceBar(
          boundaryBar,
          previousRenderedBarForUpdate(displayCandlesRef.current, boundaryBar),
          chartType,
          chartSettingsRef.current,
        ),
        true,
      )
    }
    updateFixedOhlcBarPrimitive(composeEffectiveBars(displayCandlesRef.current, boundaryBar))
    const source = liveCandlesRef.current?.length
      ? liveCandlesRef.current
      : displayCandlesRef.current
    const reconciliation = reconcileRuntimeCandle(source, boundaryBar)
    if (!reconciliation.accepted) return false
    liveCandlesRef.current = reconciliation.bars
    displayCandlesRef.current = reconciliation.bars
    scheduleLivePump()
    return true
  }, [activeTimeframe, chartType, scheduleLivePump, timezone])

  const sharedDailySource = useWorkspaceDailySource({ symbol, timeframe: activeTimeframe, marketSession, timezone, candleAuthority, enabled: mode === 'live' && !chartParityMode && !isOption && assetType === 'equity' })
  const independentCandleSource = useCandleStore({
    symbol,
    timeframe: activeTimeframe,
    optContract,
    fetchDuration,
    timezone,
    marketSession,
    enabled: !sharedDailySource && mode === 'live' && !!symbol,
    onFormingBarUpdate: chartParityMode ? undefined : applyCorrectedLiveBar,
    parityMode: chartParityMode,
    candleAuthority,
  })
  const {
    candles: rawDisplayCandles,
    status: rawFetchStatus,
    error: fetchError,
    retry: retryFetch,
    requestKey: candleRequestKey,
    candlesKey,
    streamUrl,
    reconcileStream,
    loadMoreBefore,
  } = sharedDailySource || independentCandleSource

  const candleIdentityIsCurrent = !candleRequestKey || !candlesKey || candlesKey === candleRequestKey
  const fetchStatus = rawFetchStatus === 'ready' && !candleIdentityIsCurrent
    ? 'loading'
    : rawFetchStatus
  const displayCandles = fetchStatus === 'ready'
    ? rawDisplayCandles
    : liveCandlesRef.current
  const displayCandlesLength = Array.isArray(displayCandles) ? displayCandles.length : 0
  const rightPriceScaleMinimumWidth = useMemo(
    () => compact ? 0 : stablePriceScaleWidth(mode === 'static' ? bars : displayCandles, CHART_RIGHT_PRICE_SCALE_MIN_WIDTH),
    [compact, mode, bars, displayCandles],
  )
  const liveCandleViewKey = `${symbol || ''}|${activeTimeframe}|${marketSession}|${timezone}`
  const showLiveChartChrome = mode === 'live' && chartBootstrapReady && (liveStatus === 'ready' || hasRenderedLiveCandles)
  const liveChartDataCurrent = mode !== 'live' || fetchStatus === 'ready'
  const liveChartTransitioning = mode === 'live' && fetchStatus === 'loading' && hasRenderedLiveCandles
  const fetchErrorMessage = fetchError?.message || ''
  const liveEmptyErrorLabel = /canonical bars/i.test(fetchErrorMessage)
    ? 'Canonical bars unavailable for this timeframe'
    : fetchError?.code === 'CHART_AUTH' ? 'Sign in again to load chart data'
      : fetchError?.code === 'CHART_EMPTY' ? 'IBKR returned no bars for this timeframe'
        : fetchStatus === 'error' ? 'Chart history could not load. Retry the request.'
          : 'No data available for this timeframe'
  latestChartExportStateRef.current = {
    symbol,
    timeframe: activeTimeframe,
    session: marketSession,
    mode,
    timezone,
    chartType,
    fetchStatus,
    liveStatus,
    runtimeFanoutStatus,
    chartParityMode,
    bars: mode === 'static' ? bars : displayCandles,
  }

  // CE-04g-UI-indicators-live-tick — keep displayCandlesRef in sync with
  // the latest `displayCandles` so Effect 3 (live pump) reads the freshest
  // settled bars without listing `displayCandles` as a dep. No deps array
  // by design — runs after every render before child projection effects.
  useLayoutEffect(() => {
    // Once live mode has a seeded snapshot, the stream owns only its trailing
    // candle. Do not overwrite that corrected in-memory tail with the older
    // HTTP array on each React render.
    displayCandlesRef.current = mode === 'live' && liveCandlesRef.current?.length
      ? liveCandlesRef.current
      : displayCandles
    visibleRangeBarCountRef.current = mode === 'static'
      ? (Array.isArray(bars) ? bars.length : 0)
      : (Array.isArray(displayCandles) ? displayCandles.length : 0)
  })

  useEffect(() => {
    if (!chartExportDebugEnabled) return undefined
    return registerChartExportSnapshot(chartExportIdRef.current, () => {
      const state = latestChartExportStateRef.current || {}
      const liveExportReady = state.mode !== 'live' || state.fetchStatus === 'ready'
      const settledBars = state.mode === 'static'
        ? (Array.isArray(state.bars) ? state.bars : [])
        : (liveExportReady ? (
          Array.isArray(liveCandlesRef.current) && liveCandlesRef.current.length
            ? liveCandlesRef.current
            : displayCandlesRef.current
        ) : [])
      const formingBar = state.mode === 'live' && liveExportReady
        ? synthesizeFormingBar(barStateRef.current, liveCandlesRef.current)
        : null
      const effectiveBars = state.mode === 'live'
        ? composeEffectiveBars(settledBars, formingBar)
        : settledBars
      return buildChartExportSnapshot({
        ...state,
        chartId: chartExportIdRef.current,
        bars: effectiveBars,
        activeBarTime: barStateRef.current?.time ?? null,
      })
    })
  }, [chartExportDebugEnabled])

  useEffect(() => {
    if (mode !== 'live') return
    if (fetchStatus === 'loading') setLiveStatus('loading')
    else if (fetchStatus === 'error') setLiveStatus('error')
    else if (fetchStatus === 'ready') setLiveStatus('ready')
  }, [mode, fetchStatus])

  const recordFirstLiveUpdateAfterPaint = useMemo(() => createRecordFirstLiveUpdateAfterPaint(latencySpanRef, latencyKey), [latencyKey])

  // 1-shot auto-retry on transient fetch failure.
  const retriedRef = useRef(false)
  useEffect(() => {
    if (mode !== 'live') return
    if (fetchStatus === 'ready') { retriedRef.current = false; return }
    if (fetchStatus !== 'error' || retriedRef.current) return
    retriedRef.current = true
    const t = setTimeout(() => retryFetch(), 3000)
    return () => clearTimeout(t)
  }, [mode, fetchStatus, retryFetch])

  const resetLiveViewportForTimeframe = useCallback((tf) => {
    if (mode !== 'live' || !tf) return
    visibleTimeRangesRef.current = clearVisibleRangeMapTimeframe(visibleTimeRangesRef.current, tf)
    visibleLogicalRangesRef.current = clearVisibleRangeMapTimeframe(visibleLogicalRangesRef.current, tf)
    scrollbackVisibleRangeRef.current = null
    liveCandlesRef.current = []
    displayCandlesRef.current = []
    barStateRef.current = { time: null, open: null, high: -Infinity, low: Infinity, close: null, sessionDateKey: null, clockBoundary: false, hasCorrection: false }
    liveDataViewKeyRef.current = null
    defaultViewRef.current = null
    setHasRenderedLiveCandles(false)
    try { candleSeriesRef.current?.setData?.([]) } catch { /* noop */ }
    updateFixedOhlcBarPrimitive([])
    suppressVisibleRangePersistenceRef.current = true
    if (suppressVisibleRangePersistenceTimerRef.current) {
      clearTimeout(suppressVisibleRangePersistenceTimerRef.current)
    }
    suppressVisibleRangePersistenceTimerRef.current = setTimeout(() => {
      suppressVisibleRangePersistenceRef.current = false
      suppressVisibleRangePersistenceTimerRef.current = null
    }, 180)
  }, [mode])

  // CE-04g-UI-chrome-1-fix-3 — the internal TF selector (composedChrome=false)
  // changes the timeframe here. Swap the live indicator collection to the new
  // TF's server-backed slot, mirroring the composedChrome prop-watcher path below.
  // `committedTfRef` advances in lockstep with `setIndicators` so the write
  // effect's race guard stays correct through the React batch.
  const handleTimeframeChange = useMemo(() => createHandleTimeframeChange({
    markPendingLatencySpan, setActiveTimeframe, mode, resetLiveViewportForTimeframe,
    committedTfRef, setIndicators, serverIndicatorMapRef, chartStorageScope, onTimeframeChange,
  }), [onTimeframeChange, mode, chartStorageScope, resetLiveViewportForTimeframe, markPendingLatencySpan])

  // CE-04g-UI-chrome-1 — when a parent-owned top bar drives the timeframe
  // (composedChrome=true), this effect mirrors prop changes into the
  // chassis's internal `activeTimeframe`. Gated on the prop so the
  // internal selector path (composedChrome=false) is untouched.
  // CE-04g-UI-chrome-1-fix-3 — on a real TF change (not the mount run) it
  // also swaps the live indicator collection to the new TF's server-backed slot.
  // `committedTfRef` advances here, in lockstep with `setIndicators`, so the
  // write effect's race guard stays correct through the React batch.
  useEffect(() => {
    if (!composedChrome) return
    setActiveTimeframe(initialTimeframe)
    if (tfWatchFirstRunRef.current) {
      tfWatchFirstRunRef.current = false
      return
    }
    if (mode !== 'live') return
    markPendingLatencySpan('timeframe-switch', { nextTimeframe: initialTimeframe })
    resetLiveViewportForTimeframe(initialTimeframe)
    committedTfRef.current = initialTimeframe
    setIndicators(
      readServerIndicatorsForTimeframe(initialTimeframe, mode, serverIndicatorMapRef.current)
        ?? initialIndicatorsForMode(mode, chartStorageScope)
    )
  }, [composedChrome, initialTimeframe, mode, chartStorageScope, resetLiveViewportForTimeframe, markPendingLatencySpan])

  // CE-04g-UI-chart-smoke-fixes — symbol change preserves user
  // indicators and the per-kind counter (chart-template model).
  // Per Brandon: indicators / settings are user-global; only drawings /
  // annotations (future) are per-symbol. Bumping `resetCount` is kept as
  // a discriminator for downstream consumers; the popover still closes.
  // Per-timeframe server persistence in live mode is unaffected because the
  // indicator state isn't being overwritten.
  useEffect(() => {
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false
      return
    }
    setResetCount((n) => n + 1)
    setPopoverPresetId(null)
    scrollbackVisibleRangeRef.current = null
    if (mode === 'live') {
      liveCandlesRef.current = []
      displayCandlesRef.current = []
      barStateRef.current = { time: null, open: null, high: -Infinity, low: Infinity, close: null, sessionDateKey: null, clockBoundary: false, hasCorrection: false }
      liveDataViewKeyRef.current = null
      setHasRenderedLiveCandles(false)
      try { candleSeriesRef.current?.setData?.([]) } catch { /* noop */ }
      updateFixedOhlcBarPrimitive([])
      if (activeTimeframe !== 'daily' && activeTimeframe !== 'weekly') {
        visibleTimeRangesRef.current = clearVisibleRangeMapTimeframe(visibleTimeRangesRef.current, activeTimeframe)
        visibleLogicalRangesRef.current = clearVisibleRangeMapTimeframe(visibleLogicalRangesRef.current, activeTimeframe)
        defaultViewRef.current = null
        suppressVisibleRangePersistence(180)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol])

  // ── Static-mode handlers ──────────────────────────────────────────────
  // CE-04g-UI-chrome-1 — single internal entry point for appending an
  // indicator. Both the IndicatorAccordion +Add button and the
  // `addIndicator(kind)` imperative handle (consumed by parent-owned
  // top-bar chrome) delegate here. Returns the new presetId, or null when
  // the kind is unsupported or already at its one-instance cap.
  // CE-04g-UI-chrome-2 — duplicate-instance check is now scoped via
  // MULTI_INSTANCE_KINDS so SMA / EMA can stack while the other curated
  // kinds keep the cap.
  const editIndicators = useCallback((updater) => {
    const current = sharedCurrent.current
    if (current.shared.status !== 'disabled') {
      if (current.profile) void current.shared.store.edit(current.timeframe, { indicators: updater(validateIndicators(current.profile.indicators)) })
      return
    }
    setIndicators(updater)
  }, [])
  const handleAdd = useCallback((kind) => {
    if (!DEFAULT_PARAMS_BY_KIND[kind]) return null
    if (kind === 'cars_v1' && activeTimeframe !== 'daily') return null
    if (!MULTI_INSTANCE_KINDS.has(kind) && indicators.some((i) => i.kind === kind)) return null
    const counters = idCountersByKindRef.current
    counters[kind] = (counters[kind] || 0) + 1
    const presetId = `${kind}-${counters[kind]}`
    userIndicatorActionRef.current = true
    editIndicators((prev) => [...prev, makeIndicatorInstance(kind, presetId)])
    return presetId
  }, [activeTimeframe, indicators])

  const handleAddTemplate = useCallback((template) => {
    const kind = template?.kind
    if (!DEFAULT_PARAMS_BY_KIND[kind]) return null
    if (kind === 'cars_v1' && activeTimeframe !== 'daily') return null
    if (!MULTI_INSTANCE_KINDS.has(kind) && indicators.some((i) => i.kind === kind)) return null
    const counters = idCountersByKindRef.current
    counters[kind] = (counters[kind] || 0) + 1
    const presetId = `${kind}-${counters[kind]}`
    const next = createIndicatorFromTemplate(template, presetId)
    if (!next) return null
    userIndicatorActionRef.current = true
    editIndicators((prev) => [...prev, next])
    return presetId
  }, [activeTimeframe, indicators])

  const handleSaveTemplate = useCallback((presetId) => {
    const indicator = indicators.find((ind) => ind.presetId === presetId)
    if (!indicator) return
    const defaultName = `${indicator.kind.toUpperCase()} ${indicator.params?.length ?? ''}`.trim()
    const name = typeof window !== 'undefined'
      ? window.prompt('Template name', defaultName)
      : defaultName
    if (!name || !String(name).trim()) return
    saveIndicatorTemplate(indicator, name, {
      workspace: mode === 'static' ? 'demo-position' : chartStorageScope,
      timeframe: activeTimeframe,
    })
  }, [activeTimeframe, chartStorageScope, indicators, mode, saveIndicatorTemplate])

  // CE-04g-UI-chrome-2 — remove an indicator by presetId. Wired to the
  // accordion's trash icon. Filters by presetId (not kind) so multi-instance
  // SMA / EMA remove individually. Closes the popover if it was open on the
  // removed indicator.
  const handleRemove = useCallback((presetId) => {
    userIndicatorActionRef.current = true
    editIndicators((prev) => prev.filter((i) => i.presetId !== presetId))
    setPopoverPresetId((prev) => (prev === presetId ? null : prev))
  }, [])

  const handleReorder = useCallback((draggedPresetId, targetPresetId, position) => {
    // Legend-order only: existing Lightweight Charts series retain their
    // creation stack order so drag-reorder does not alter visual overlays.
    userIndicatorActionRef.current = true
    editIndicators((prev) => reorderIndicatorList(prev, draggedPresetId, targetPresetId, position))
  }, [])

  const setAllIndicatorsVisible = useCallback((visible) => {
    userIndicatorActionRef.current = true
    editIndicators((prev) => prev.map((ind) => ({
      ...ind,
      style: { ...ind.style, visible },
    })))
  }, [])

  const handleToggleAllVisible = useCallback(() => {
    userIndicatorActionRef.current = true
    editIndicators((prev) => {
      const shouldShow = !prev.some((ind) => ind.style?.visible !== false)
      return prev.map((ind) => ({
        ...ind,
        style: { ...ind.style, visible: shouldShow },
      }))
    })
  }, [])

  const handleToggleVisible = useCallback((presetId) => {
    userIndicatorActionRef.current = true
    editIndicators((prev) => prev.map((ind) => {
      if (ind.presetId !== presetId) return ind
      const wasVisible = ind.style?.visible !== false
      return { ...ind, style: { ...ind.style, visible: !wasVisible } }
    }))
  }, [])

  const handleOpenPopover = useCallback((presetId) => {
    setPopoverPresetId(presetId)
  }, [])

  const handlePopoverChange = useCallback((field, value) => {
    userIndicatorActionRef.current = true
    editIndicators((prev) => prev.map((ind) => {
      if (ind.presetId !== popoverPresetId) return ind
      return setNestedField(ind, field, value)
    }))
  }, [popoverPresetId])

  const handlePopoverRemove = useCallback(() => {
    userIndicatorActionRef.current = true
    editIndicators((prev) => prev.filter((i) => i.presetId !== popoverPresetId))
    setPopoverPresetId(null)
  }, [popoverPresetId])

  const handlePopoverClose = useCallback(() => {
    setPopoverPresetId(null)
  }, [])

  // ── LWC chart lifecycle (static mode only) ────────────────────────────
  const drawingsRef = useRef(null)
  const chartContainerRef = useRef(null)
  const chartRef = useRef(null)
  const crosshairCleanupRef = useRef(null)
  const candleSeriesRef = useRef(null)
  const dynamicNavigationRef = useRef(null)
  const createPriceAlertAtCrosshair = usePriceAlertCrosshair({
    chartRef, seriesRef: candleSeriesRef, containerRef: chartContainerRef,
    ready: chartReadyNonce, enabled: mode === 'live' && assetType === 'equity' && liveChartDataCurrent && crosshairEnabled,
    symbol, timeframe: activeTimeframe, onCreate: onPriceAlertCreate,
  })
  // CE-04g-UI-chrome-1 — imperative handle for parent-owned top-bar chrome.
  // The ChartTopBar's Indicators ▾ dropdown (and CE-05's future
  // PositionWorkspace top bar) calls `chartRef.current.addIndicator(kind)`
  // through this handle. Delegates to `handleAdd` so the accordion's
  // +Add Indicator row and the top-bar dropdown share one source of truth.
  // See chart-component-contract.md §15.
  useImperativeHandle(ref, () => ({
    drawingAction: action => drawingsRef.current?.[action]?.(),
    createPriceAlertAtCrosshair,
    addIndicator(kind) {
      return handleAdd(kind)
    },
    addIndicatorTemplate(template) {
      return handleAddTemplate(template)
    },
    toggleAllIndicatorsVisible() {
      handleToggleAllVisible()
    },
    setSyncedCrosshair(point) {
      applySyncedCrosshair(chartRef.current, candleSeriesRef.current, liveChartDataCurrent ? point : null, activeTimeframeRef.current)
    },
    openSettings(tab = 'symbol') {
      setSettingsActiveTab(tab)
      setSettingsOpen(true)
    },
  }), [handleAdd, handleAddTemplate, handleToggleAllVisible, liveChartDataCurrent, createPriceAlertAtCrosshair])
  useChartNavigationCommands({
    active: keyboardActive, containerRef: chartContainerRef,
    getChart: () => chartRef.current,
    getLatestTime: () => candleSeriesRef.current?.data().at(-1)?.time,
    ready: liveChartDataCurrent,
    onNavigate: () => dynamicNavigationRef.current?.pause(),
    openSettings: () => { setSettingsActiveTab('symbol'); setSettingsOpen(true) },
    toggleExecutionMarkers: () => updateChartBehavior(current => ({ executionMarkersVisible: current.executionMarkersVisible === false })),
  })
  const dashboardRegimeRef = useRef(null)
  useEffect(() => {
    const chart = chartRef.current
    const series = candleSeriesRef.current
    if (!compact || !chart || !series) return
    const primitive = new CarsRegimePrimitive()
    series.attachPrimitive(primitive)
    dashboardRegimeRef.current = primitive
    const publishRange = () => onVisibleTimeRangeChange?.(chart.timeScale().getVisibleRange())
    chart.timeScale().subscribeVisibleTimeRangeChange(publishRange)
    publishRange()
    return () => {
      dashboardRegimeRef.current = null
      // The chart creation effect may have already disposed the chart.
      try { chart.timeScale().unsubscribeVisibleTimeRangeChange(publishRange); series.detachPrimitive(primitive) } catch { /* disposed */ }
    }
  }, [compact, chartReadyNonce, onVisibleTimeRangeChange])
  useEffect(() => {
    dashboardRegimeRef.current?.update({ spans: backgroundRegimeSpans || [], fillStyle: 'rgba(139, 92, 246, 0.26)', startFillStyle:'rgba(139, 92, 246, 0.48)' })
  }, [backgroundRegimeSpans, chartReadyNonce])

  // Dashboard breadth lives on the SAME native time scale as its candles.
  // Pane 1 has its own value scale; dates, zoom, pan and crosshair are shared.
  const breadthPaneRef = useRef(null)
  const hasBreadthIndicator = compact && breadthIndicator != null
  useEffect(() => {
    const chart = chartRef.current
    if (!chart || !hasBreadthIndicator) return
    const series = chart.addHistogramSeries({
      priceScaleId: 'breadth',
      priceFormat: { type: 'price', precision: 0, minMove: 1 }, lastValueVisible: false, priceLineVisible: false,
    }, 1)
    // Signed counts must stay linear even when benchmark prices use log scale.
    series.priceScale().applyOptions({ mode: 0, autoScale: true, scaleMargins: { top: 0.12, bottom: 0.12 } })
    series.createPriceLine({ price: 0, color: '#88888866', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false })
    const positive = new CarsRegimePrimitive()
    const negative = new CarsRegimePrimitive()
    series.attachPrimitive(positive)
    series.attachPrimitive(negative)
    breadthPaneRef.current = { series, positive, negative, previous: [] }
    applyPaneHeights(chart, paneRatioRef.current, measuredHeightRef.current)
    return () => {
      breadthPaneRef.current = null
      try { series.detachPrimitive(positive); series.detachPrimitive(negative); chart.removeSeries(series) } catch { /* chart already disposed */ }
    }
  }, [hasBreadthIndicator, chartReadyNonce])
  useEffect(() => {
    const pane = breadthPaneRef.current
    const chart = chartRef.current
    if (!pane || !chart || !breadthIndicator) return
    const { current } = breadthIndicator
    // Match the benchmark's available candle window. Retain older observations
    // in the collector, without creating candle-free years in this shared chart.
    const first = displayCandles[0]?.time
    const last = displayCandles.at(-1)?.time
    const inCandleWindow = point => first != null && last != null && point.time >= first && point.time <= last
    const data = breadthIndicator.data.filter(inCandleWindow)
    const regimes = breadthIndicator.regimes
    const anchorTime = candleSeriesRef.current?.data().at(-1)?.time
    const previousAnchor = anchorTime == null ? null : chart.timeScale().timeToIndex?.(anchorTime)
    const range = chart.timeScale().getVisibleLogicalRange()
    const hadBreadthData = pane.previous.length > 0
    const mode = updateChartSeries(pane.series, pane.previous, data)
    pane.previous = data
    pane.positive.update({ spans: buildSignalRegimeSpans(regimes,1,{from:first,to:last}), startFillStyle:current?'rgba(48, 163, 113, 0.50)':'rgba(48, 163, 113, 0.24)', fillStyle: current ? 'rgba(48, 163, 113, 0.15)' : 'rgba(48, 163, 113, 0.07)' })
    pane.negative.update({ spans: buildSignalRegimeSpans(regimes,-1,{from:first,to:last}), startFillStyle:current?'rgba(231, 85, 94, 0.44)':'rgba(231, 85, 94, 0.22)', fillStyle: current ? 'rgba(231, 85, 94, 0.14)' : 'rgba(231, 85, 94, 0.07)' })
    // Market selection and refreshed history must not reset the user's dates.
    if (hadBreadthData && mode === 'replace' && range && anchorTime != null) {
      const nextAnchor = chart.timeScale().timeToIndex?.(anchorTime)
      const shift = Number.isFinite(previousAnchor) && Number.isFinite(nextAnchor) ? nextAnchor - previousAnchor : 0
      chart.timeScale().setVisibleLogicalRange({ from: range.from + shift, to: range.to + shift })
    }
  }, [breadthIndicator, chartReadyNonce, displayCandles[0]?.time, displayCandles.at(-1)?.time])

  const fixedOhlcBarPrimitiveRef = useRef(null)
  const overlayPriceLinesRef = useRef([])
  const seriesMarkersRef = useRef(null)
  const executionMarkerPrimitiveRef = useRef(null)
  const quickMeasurePrimitiveRef = useRef(null)
  const [quickMeasureSnapshot, setQuickMeasureSnapshot] = useState(null)
  const executionTooltipTimerRef = useRef(null)
  const pendingExecutionTooltipMarkerIdRef = useRef(null)
  const activeExecutionTooltipMarkerIdRef = useRef(null)

  const handleQuickMeasureChange = useCallback((snapshot) => {
    setQuickMeasureSnapshot(snapshot)
  }, [])

  useEffect(() => {
    quickMeasurePrimitiveRef.current?.clear?.()
  }, [symbol])

  const selectableStopLines = useMemo(() => {
    const lines = []
    const add = (line) => {
      const price = finiteOverlayPrice(line?.price)
      if (price != null) lines.push({ price })
    }
    add(setupLines?.stop)
    if (Array.isArray(setupLines?.trancheStops)) {
      for (const line of setupLines.trancheStops) add(line)
    }
    return lines
  }, [setupLines])

  const selectedExecutionMarkerIds = useMemo(() => {
    const stopPrice = finiteOverlayPrice(selectedStopPrice)
    if (stopPrice == null) return []
    const executionMarkers = (Array.isArray(markers) ? markers : [])
      .filter(markerIsExecutionAnnotation)
      .map(markerAsExecution)
    const stopOrders = selectableStopLines.map((line) => ({ stopPrice: line.price }))
    return (Array.isArray(markers) ? markers : [])
      .filter((marker) => markerIsExecutionAnnotation(marker) && marker.side === 'buy')
      .filter((marker) => executionMatchesStopPrice({
        execution: markerAsExecution(marker),
        stopPrice,
        executions: executionMarkers,
        stopOrders,
        symbol,
      }))
      .map((marker) => String(marker.id))
  }, [markers, selectedStopPrice, selectableStopLines, symbol])

  const suppressVisibleRangePersistence = useCallback((durationMs = 180) => {
    suppressVisibleRangePersistenceRef.current = true
    if (suppressVisibleRangePersistenceTimerRef.current) {
      clearTimeout(suppressVisibleRangePersistenceTimerRef.current)
    }
    suppressVisibleRangePersistenceTimerRef.current = setTimeout(() => {
      suppressVisibleRangePersistenceRef.current = false
      suppressVisibleRangePersistenceTimerRef.current = null
    }, durationMs)
  }, [])

  const blockVisibleRangePersistence = useCallback(() => {
    suppressVisibleRangePersistenceRef.current = true
    scrollbackVisibleRangeRef.current = null
    if (suppressVisibleRangePersistenceTimerRef.current) {
      clearTimeout(suppressVisibleRangePersistenceTimerRef.current)
      suppressVisibleRangePersistenceTimerRef.current = null
    }
  }, [])

  const releaseVisibleRangePersistenceAfter = useCallback((durationMs = LIVE_VIEWPORT_SETTLE_MS) => {
    suppressVisibleRangePersistenceRef.current = true
    if (suppressVisibleRangePersistenceTimerRef.current) {
      clearTimeout(suppressVisibleRangePersistenceTimerRef.current)
    }
    suppressVisibleRangePersistenceTimerRef.current = setTimeout(() => {
      suppressVisibleRangePersistenceRef.current = false
      suppressVisibleRangePersistenceTimerRef.current = null
    }, durationMs)
  }, [])

  useEffect(() => () => {
    if (suppressVisibleRangePersistenceTimerRef.current) {
      clearTimeout(suppressVisibleRangePersistenceTimerRef.current)
      suppressVisibleRangePersistenceTimerRef.current = null
    }
  }, [])

  useLayoutEffect(() => {
    const viewKey = `${mode}|${liveCandleViewKey}`
    const chartTypeChanged = previousChartTypeRef.current !== chartType
    const timeframeChanged = previousTimeframeRef.current !== activeTimeframe
    if (previousStyleViewKeyRef.current !== viewKey) {
      styleViewportRef.current = null
    } else if (chartTypeChanged) {
      // Capture before passive cleanup removes the old native chart. Appearance
      // changes must not replace the operator's pan/zoom with a fresh default.
      const sameData = mode !== 'live' || (liveChartDataCurrent && liveDataViewKeyRef.current === liveCandleViewKey)
      const range = sameData ? readVisibleLogicalRange(chartRef.current) : null
      styleViewportRef.current = range ? { viewKey, range, nonce: chartReadyNonce } : null
    }
    if (mode === 'live' && (chartTypeChanged || timeframeChanged)) blockVisibleRangePersistence()
    previousStyleViewKeyRef.current = viewKey
    previousChartTypeRef.current = chartType
    previousTimeframeRef.current = activeTimeframe
  }, [mode, liveCandleViewKey, liveChartDataCurrent, activeTimeframe, chartType, chartReadyNonce, blockVisibleRangePersistence])
  const clearExecutionTooltipIntent = useCallback((clearActive = true) => {
    pendingExecutionTooltipMarkerIdRef.current = null
    if (executionTooltipTimerRef.current) {
      clearTimeout(executionTooltipTimerRef.current)
      executionTooltipTimerRef.current = null
    }
    if (clearActive) {
      activeExecutionTooltipMarkerIdRef.current = null
      executionMarkerPrimitiveRef.current?.setHoveredMarkerId?.(null)
      setHoveredExecutionAnnotation(null)
    }
  }, [])
  // CE-04c — value type widened from `ISeriesApi<'Line'>` to a
  // per-component map `{ [componentKey]: ISeriesApi<'Line'> | ISeriesApi<'Area'> }`.
  // SMA/EMA hold a single key (`ma`); Bollinger/Donchian hold four
  // (`basis`/`upper`/`lower`/`background`).
  // CE-04c-2 — value type widened again to include
  // `ISeriesApi<'Histogram'>` for Volume. Volume's bucket is
  // `{ bars: ISeriesApi<'Histogram'> }`.
  const seriesByPresetRef = useRef(new Map())
  const volumeReadoutCleanupRef = useRef(null)
  const paneLayoutCleanupRef = useRef(null)

  const [nativePaneLayout, setNativePaneLayout] = useState(null)
  const persistPaneRatio = useCallback((nextRatio) => {
    paneRatioRef.current = nextRatio
    setPaneRatio(nextRatio)
    const current = sharedCurrent.current
    if (current.shared.status !== 'disabled') {
      void current.shared.store.edit(current.timeframe, { paneRatio: nextRatio })
      return
    }
    void writeServerChartPreference({
      scope: chartStorageScope,
      preferenceKey: CHART_PREFERENCE_KEYS.paneRatio,
      preferences: paneRatioPreferencePayload(nextRatio),
    })
  }, [chartStorageScope])

  // CE-04g-UI-chart-smoke-fixes — React-state mirror of the imperative
  // volume-pane lifecycle. Effect 1 sets this after every addPane /
  // removePane decision so the scale-control anchor collapses in lockstep
  // with the ACTUAL pane (not just the user-visibility intent). Reading
  // the paneRegistryRef ref directly from render would race against Effect
  // 1's commit-time mutation.
  const [hasVolumePane, setHasVolumePane] = useState(false)
  // CE-04g-UI-pivots-iseriesprimitive-refactor — one PivotPrimitive per
  // active pivot preset, attached to the candle series via attachPrimitive.
  // Replaces the per-display-key line-series handle registry from v0.24.14.
  // Cleared on chart teardown so primitives bound to a removed series
  // cannot be reused. Lifecycle (attach / detach) owned by Effect 1; payload
  // updates flow through the dedicated pivot reconcile effect (Effect 2.5).
  const pivotPrimitivesByPresetRef = useRef(new Map())
  const carsPrimitivesByPresetRef = useRef(new Map())
  const sessionBackgroundPrimitiveRef = useRef(null)
  // CE-04g-UI-pivots-iseriesprimitive-refactor — set of preset IDs that
  // have received their first finite-level payload update in this
  // primitive's lifetime. Used by Effect 2.5 to gate the once-per-attach
  // diagnostic emit (replaces v0.24.14's per-pass mintedPivotOverlaySeries
  // flag). Cleared on chart teardown alongside the primitives map.
  const pivotDiagnosticSeenRef = useRef(new Set())
  // CE-04g-UI-pivots-iseriesprimitive-refactor — sticky per-preset session
  // anchor. Preserves v0.24.14's guard at TradeChart.jsx:2185-2192: only
  // updates when the computed sessionStartTime is finite, so a transient
  // malformed last bar cannot null a previously-valid anchor. Effect 2.5
  // conditionally writes; Effect 1's lifecycle branch deletes per preset on
  // hide/removal; chart teardown resets the whole map. This is architecture,
  // not a test concern — Diagnosis 5.
  const pivotSegmentStartByPresetRef = useRef(new Map())
  // One-shot warning flag for the nextRthOpenChartTime fallback path
  // (calendar walker exhausts → fall back to last-bar time and emit a
  // single console.warn per chart-instance lifetime). Reset on chart
  // teardown so a rebuild can warn again if the degenerate case persists.
  // Owned by Effect 2.5 (the only remaining caller of nextRthOpenChartTime
  // in React after the iseriesprimitive refactor).
  const nextRthFallbackWarnedRef = useRef(false)
  // CE-04g-UI-pivots-parity-diagnostic — monotonic counter incremented once
  // per pivot mint emit (Effect 1). Surfaced in the snapshot so multiple
  // emits during a single session can be ordered in the console.
  const pivotDebugEmitSeqRef = useRef(0)
  // CE-04c-2 — per-chart pane registry. Maps logical pane names →
  // numeric LWC pane indices. Initialized inside the chart-init useEffect
  // because LWC's panes are chart-instance-scoped (not symbol-scoped):
  // when the chart is recreated, the registry resets; when `symbol`
  // changes within the same chart instance the registry persists (volume
  // pane stays allocated but is dormant until a future Volume add).
  const paneRegistryRef = useRef(null)

  // Pane height tracking: the chassis measures its outermost div with a
  // ResizeObserver and derives both the chart total height and the pane
  // separator position from that measurement. Stored as a ratio so native
  // pane resizes survive viewport changes proportionally (default 0.75
  // price / 0.25 volume).
  const skeletonRef = useRef(null)
  const [measuredHeight, setMeasuredHeight] = useState(480)
  const measuredHeightRef = useRef(480)
  useEffect(() => { measuredHeightRef.current = measuredHeight }, [measuredHeight])
  // Pane ratio is server-backed; default to 0.75 until hydration completes.
  const [paneRatio, setPaneRatio] = useState(0.75)
  const pane0Height = Math.round(measuredHeight * paneRatio)
  // Keep `paneRatio` mirrored on a ref so chart-created observers and
  // callbacks always read the latest server-backed ratio.
  const paneRatioRef = useRef(paneRatio)
  useEffect(() => { paneRatioRef.current = paneRatio }, [paneRatio])

  useEffect(() => {
    let cancelled = false
    setServerChartPreferencesHydrated(false)

    async function hydrateServerChartPreferences() {
      if (sharedEnabled) return
      let [
        settingsPayload,
        panePayload,
        visibleTimePayload,
        visibleLogicalPayload,
      ] = await Promise.all([
        readServerChartPreference({
          scope: chartStorageScope,
          preferenceKey: CHART_PREFERENCE_KEYS.settings,
        }),
        readServerChartPreference({
          scope: chartStorageScope,
          preferenceKey: CHART_PREFERENCE_KEYS.paneRatio,
        }),
        readServerChartPreference({
          scope: chartStorageScope,
          preferenceKey: CHART_PREFERENCE_KEYS.visibleTimeRange,
        }),
        readServerChartPreference({
          scope: chartStorageScope,
          preferenceKey: CHART_PREFERENCE_KEYS.visibleLogicalRange,
        }),
      ])

      if (fallbackStorageScope) {
        if (!settingsPayload) settingsPayload = await readServerChartPreference({
          scope: fallbackStorageScope, preferenceKey: CHART_PREFERENCE_KEYS.settings,
        })
        if (!panePayload) panePayload = await readServerChartPreference({
          scope: fallbackStorageScope, preferenceKey: CHART_PREFERENCE_KEYS.paneRatio,
        })
      }
      if (cancelled) return

      if (settingsPayload?.version === 1 && settingsPayload.settings) {
        setChartPreferences(normalizeChartPreferencesPayload(settingsPayload.settings))
      }

      const serverPaneRatio = Number(panePayload?.value)
      if (Number.isFinite(serverPaneRatio) && serverPaneRatio >= 0.05 && serverPaneRatio <= 0.95) {
        setPaneRatio(serverPaneRatio)
      }

      if (mode !== 'live' && visibleTimePayload?.version === VISIBLE_RANGE_PREFERENCE_VERSION) {
        visibleTimeRangesRef.current = normalizeVisibleTimeRanges(visibleTimePayload.ranges)
      } else if (mode === 'live') {
        visibleTimeRangesRef.current = {}
      }
      if (mode !== 'live' && visibleLogicalPayload?.version === VISIBLE_RANGE_PREFERENCE_VERSION) {
        visibleLogicalRangesRef.current = normalizeVisibleLogicalRanges(visibleLogicalPayload.ranges)
      } else if (mode === 'live') {
        visibleLogicalRangesRef.current = {}
      }
      setServerChartPreferencesHydrated(true)
      if (
        mode !== 'live'
        && (
          visibleTimePayload?.version === VISIBLE_RANGE_PREFERENCE_VERSION
          || visibleLogicalPayload?.version === VISIBLE_RANGE_PREFERENCE_VERSION
        )
      ) {
        setVisibleRangePreferenceNonce((n) => n + 1)
      }
    }

    hydrateServerChartPreferences()

    return () => {
      cancelled = true
    }
  }, [chartStorageScope, mode, fallbackStorageScope, sharedEnabled])

  useEffect(() => {
    if (!sharedEnabled || !sharedProfile) return
    committedTfRef.current = activeTimeframe
    const next = validateIndicators(sharedProfile.indicators)
    userIndicatorActionRef.current = false
    idCountersByKindRef.current = initialCountersWithPersistence(mode, next, chartStorageScope)
    setIndicators(next)
    setChartPreferences(normalizeChartPreferencesPayload(sharedProfile.settings))
    if (Number.isFinite(sharedProfile.paneRatio)) setPaneRatio(sharedProfile.paneRatio)
    setServerIndicatorHydrated(true)
    setServerChartPreferencesHydrated(true)
  }, [sharedEnabled, sharedProfile, activeTimeframe, mode, chartStorageScope])

  useEffect(() => {
    if (visibleRangePreferenceNonce <= 0) return
    if (!serverChartPreferencesHydrated) return
    if (mode === 'live') return
    const chart = chartRef.current
    if (!chart) return
    const totalBars = Array.isArray(bars) ? bars.length : 0
    suppressVisibleRangePersistence(180)
    if (
      !applyStoredVisibleLogicalRange(chart, activeTimeframe, totalBars, visibleLogicalRangesRef.current, activeChartMargins)
    ) {
      applyStoredVisibleTimeRange(chart, activeTimeframe, visibleTimeRangesRef.current)
    }
    releaseVisibleRangePersistenceAfter()
  }, [
    visibleRangePreferenceNonce,
    chartReadyNonce,
    activeTimeframe,
    mode,
    bars,
    displayCandles.length,
    activeChartMargins,
    serverChartPreferencesHydrated,
    suppressVisibleRangePersistence,
    releaseVisibleRangePersistenceAfter,
  ])

  // Track the outermost div's height. The chart canvas + LWC panes scale
  // from the resulting `measuredHeight`. Default 480 covers SSR / pre-mount.
  useEffect(() => {
    const el = skeletonRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        if (!e.contentRect) continue
        const h = Math.floor(e.contentRect.height)
        if (h > 0) setMeasuredHeight(h)
      }
    })
    try { ro.observe(el) } catch { /* happy-dom may not implement RO */ }
    return () => { try { ro.disconnect() } catch { /* noop */ } }
  }, [])

  // CE-04g-UI-chart-smoke-fixes — apply pane heights based on current pane
  // count. Volume pane is created on-demand by Effect 1, so the chart can
  // be single-pane (price-only collapse) or two-pane (price + volume).
  // `paneRatio` is the PRICE-pane share — pane 0 always uses it; pane 1
  // gets the remainder. Single-pane mode gives pane 0 the full height.
  function applyPaneHeights(chart, paneRatio, totalHeight) {
    const panes = chart.panes()
    if (panes.length === 0) return
    if (panes.length === 1) {
      if (typeof panes[0].setHeight === 'function') panes[0].setHeight(totalHeight)
      return
    }
    // Native stretch factors divide the available pane space, excluding the
    // time axis and separator. Pixel heights based on the outer canvas drift.
    if (panes[0].setStretchFactor && panes[1].setStretchFactor) {
      panes[0].setStretchFactor(paneRatio)
      panes[1].setStretchFactor(1 - paneRatio)
      return
    }
    const pane0H = Math.round(totalHeight * paneRatio)
    const pane1H = totalHeight - pane0H
    if (typeof panes[0].setHeight === 'function') panes[0].setHeight(pane0H)
    if (typeof panes[1].setHeight === 'function') panes[1].setHeight(pane1H)
  }

  function attachFixedOhlcBarPrimitive(candleSeries) {
    if (!candleSeries || chartType !== 'bar' || chartSettings.thickOhlcBars === false) return null
    if (fixedOhlcBarPrimitiveRef.current) return fixedOhlcBarPrimitiveRef.current
    const primitive = new FixedOhlcBarPrimitive()
    try {
      candleSeries.attachPrimitive(primitive)
      fixedOhlcBarPrimitiveRef.current = primitive
      return primitive
    } catch {
      fixedOhlcBarPrimitiveRef.current = null
      return null
    }
  }

  function syncSessionBackgroundPrimitive() {
    const candleSeries = candleSeriesRef.current
    if (!candleSeries || marketSession !== 'all') {
      if (candleSeries && sessionBackgroundPrimitiveRef.current) {
        try { candleSeries.detachPrimitive(sessionBackgroundPrimitiveRef.current) } catch { /* noop */ }
      }
      sessionBackgroundPrimitiveRef.current = null
      return null
    }
    if (!sessionBackgroundPrimitiveRef.current) {
      try {
        const primitive = new SessionBackgroundPrimitive()
        candleSeries.attachPrimitive(primitive)
        sessionBackgroundPrimitiveRef.current = primitive
      } catch {
        sessionBackgroundPrimitiveRef.current = null
      }
    }
    return sessionBackgroundPrimitiveRef.current
  }

  function updateFixedOhlcBarPrimitive(nextBars) {
    const primitive = fixedOhlcBarPrimitiveRef.current
    if (!primitive || chartType !== 'bar' || chartSettings.thickOhlcBars === false) return
    primitive.update({
      bars: nextBars,
      color: chartSettingsColor(chartSettings, 'barUpColor', 'barUpOpacity', SERIES_COLORS_WORKSPACE.barUp),
    })
  }

  function detachFixedOhlcBarPrimitive() {
    const candleSeries = candleSeriesRef.current
    const primitive = fixedOhlcBarPrimitiveRef.current
    if (!candleSeries || !primitive) {
      fixedOhlcBarPrimitiveRef.current = null
      return
    }
    try { candleSeries.detachPrimitive(primitive) } catch { /* noop */ }
    fixedOhlcBarPrimitiveRef.current = null
  }

  // When the measured height changes (or the chart instance is rebuilt via
  // chartType / activeTimeframe), push pane heights derived from the current
  // `paneRatio` so the separator stays at the same proportional position.
  // CE-04g-UI-chrome-1-fix item 4 — re-applies after rebuild by listing the
  // same rebuild-triggering inputs as the chart-create effects.
  // CE-04g-UI-chart-smoke-fixes — uses applyPaneHeights so single-pane
  // (Volume hidden) mode lets pane 0 take the full height.
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    try { applyPaneHeights(chart, paneRatio, measuredHeight) } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measuredHeight, paneRatio, chartType, activeTimeframe])

  useEffect(() => {
    const chart = chartRef.current
    const container = chartContainerRef.current
    if (!chart || !container) return undefined
    const stop = observeChartPaneLayout(chart, container, {
      onLayout: (next) => setNativePaneLayout(prev => (
        prev?.height === next.height && prev?.axisWidth === next.axisWidth ? prev : next
      )),
      onRatio: persistPaneRatio,
    })
    paneLayoutCleanupRef.current = stop
    return () => {
      stop()
      if (paneLayoutCleanupRef.current === stop) paneLayoutCleanupRef.current = null
    }
  }, [chartReadyNonce, hasVolumePane, hasBreadthIndicator, persistPaneRatio, measuredHeight, paneRatio])

  useEffect(() => {
    const candleSeries = candleSeriesRef.current
    if (!candleSeries) return
    if (chartType !== 'bar' || chartSettings.thickOhlcBars === false) {
      detachFixedOhlcBarPrimitive()
      return
    }

    const primitive = attachFixedOhlcBarPrimitive(candleSeries)
    if (!primitive) return
    const sourceBars = mode === 'static' ? bars : displayCandles
    if (Array.isArray(sourceBars)) updateFixedOhlcBarPrimitive(sourceBars)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    chartType,
    chartSettings.thickOhlcBars,
    chartSettings.barUpColor,
    chartSettings.barUpOpacity,
    bars,
    displayCandles,
  ])

  useEffect(() => {
    const series = candleSeriesRef.current
    if (!series) return
    try {
      series.applyOptions(chartType === 'line' ? closeLineOptions(chartSettings) : chartType === 'bar'
        ? barSeriesOptions(chartSettings)
        : candleSeriesOptions(chartSettings))
    } catch { /* noop */ }
  }, [chartType, chartSettings])

  // Init chart once when the container mounts in static mode.
  useEffect(() => {
    if (mode !== 'static') return
    const container = chartContainerRef.current
    if (!container) return

    // CE-04c-2 — init pane registry alongside the chart. `'price'` always
    // resolves to 0 (LWC's default pane). New names mint the next free
    // index in registration order.
    paneRegistryRef.current = new Map([['price', 0]])

    // CE-04g-UI-chrome-1 — workspace-theme tokens (chartTokens.js). The
    // CrosshairMode.Normal override is preserved verbatim from CE-04c-2-
    // fix-4 (free-follow mode; price label tracks cursor's actual y-
    // position, no Magnet snap to nearest OHLC).
    const chart = createChart(container, {
      autoSize: false,
      width: container.clientWidth || 800,
      height: container.clientHeight || 480,
      hoveredSeriesOnTop: false,
      // CE-05 pane cleanup — use the native LWC pane separator as the
      // single source of truth. The panes, volume bars, and bottom control
      // anchors all align to LWC's actual pane geometry.
      layout: {
        ...chartCanvasAppearance.layout,
        attributionLogo: false,
        panes: { enableResize: true, separatorColor: BORDER_COLOR_RGBA, separatorHoverColor: BORDER_COLOR_RGBA_HOVER },
      },
      grid: chartCanvasAppearance.grid,
      crosshair: { ...CHART_CROSSHAIR_WORKSPACE, mode: CrosshairMode.Normal },
      rightPriceScale: {
        ...CHART_RIGHT_PRICE_SCALE_WORKSPACE,
        minimumWidth: rightPriceScaleMinimumWidth,
        textColor: chartCanvasAppearance.scaleTextColor,
        scaleMargins: activeChartScaleMargins,
      },
      timeScale: {
        ...CHART_TIME_SCALE_WORKSPACE,
        ...(compact ? { lockVisibleTimeRangeOnResize: true } : {}),
        timeVisible: true,
        rightOffset: activeChartMargins.rightOffset,
      },
    })
    chartRef.current = chart
    // CE-04g-UI-indicators-live-tick — re-fire Effect 1 (lifecycle) once
    // the chart instance is live. Effect 1's deps deliberately exclude
    // `displayCandles`, so without this signal it could miss the initial
    // chart-created render in live mode.
    setChartReadyNonce(n => n + 1)

    const candle = chartType === 'line'
      ? chart.addLineSeries(closeLineOptions(chartSettings))
      : chartType === 'bar'
      ? chart.addBarSeries(barSeriesOptions(chartSettings))
      : chart.addCandlestickSeries(candleSeriesOptions(chartSettings))
    candleSeriesRef.current = candle
    const fixedBarPrimitive = attachFixedOhlcBarPrimitive(candle)
    if (quickMeasureEnabled) {
      try {
        const primitive = new QuickMeasurePrimitive({
          onChange: handleQuickMeasureChange,
          style: quickMeasureAppearance,
        })
        candle.attachPrimitive(primitive)
        quickMeasurePrimitiveRef.current = primitive
      } catch { /* unsupported mock or chart teardown */ }
    }
    if (fixedBarPrimitive && Array.isArray(bars)) {
      updateFixedOhlcBarPrimitive(bars)
    }

    // CE-04g-UI-chart-smoke-fixes — pane 1 (volume) is NOT created here.
    // Effect 1 mints it on-demand when a visible Volume indicator exists
    // and removes it via chart.removePane() when none does (TV-style
    // collapse). The chart starts single-pane; pane 0 takes the full
    // measured height until Effect 1's first run.
    try {
      applyPaneHeights(chart, paneRatioRef.current, measuredHeightRef.current)
    } catch { /* noop — pane API absent in some test mocks */ }

    let lastW = 0, lastH = 0
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        if (!e.contentRect) continue
        const w = Math.floor(e.contentRect.width)
        const h = Math.floor(e.contentRect.height)
        if (w <= 0 || h <= 0) continue
        if (w === lastW && h === lastH) continue
        lastW = w; lastH = h
        // Set pane proportions before painting the new size. ResizeObserver
        // runs before paint; deferring the canvas redraw exposes the old size.
        try { applyPaneHeights(chart, paneRatioRef.current, h) } catch { /* noop */ }
        try { chart.resize(w, h, true) } catch { /* chart torn down */ }
      }
    })
    try { ro.observe(container) } catch { /* happy-dom may not implement RO */ }

    return () => {
      try { ro.disconnect() } catch { /* noop */ }
      // CE-04g-UI-pivots-iseriesprimitive-refactor — detach all primitives
      // before chart.remove() so LWC internals are still live. The primitive's
      // detached() hook drops its internal refs, but explicit detach matches
      // the established unsubscribe-before-remove pattern.
      const cs = candleSeriesRef.current
      const primMap = pivotPrimitivesByPresetRef.current
      if (cs && primMap) {
        for (const primitive of primMap.values()) {
          try { cs.detachPrimitive(primitive) } catch { /* noop */ }
        }
      }
      const carsMap = carsPrimitivesByPresetRef.current
      if (cs && carsMap) {
        for (const primitive of carsMap.values()) {
          try { cs.detachPrimitive(primitive) } catch { /* noop */ }
        }
      }
      if (cs && fixedOhlcBarPrimitiveRef.current) {
        try { cs.detachPrimitive(fixedOhlcBarPrimitiveRef.current) } catch { /* noop */ }
      }
      if (cs && sessionBackgroundPrimitiveRef.current) {
        try { cs.detachPrimitive(sessionBackgroundPrimitiveRef.current) } catch { /* noop */ }
      }
      if (cs && executionMarkerPrimitiveRef.current) {
        try { cs.detachPrimitive(executionMarkerPrimitiveRef.current) } catch { /* noop */ }
      }
      if (cs && quickMeasurePrimitiveRef.current) {
        try { cs.detachPrimitive(quickMeasurePrimitiveRef.current) } catch { /* noop */ }
      }
      clearExecutionTooltipIntent()
      try { seriesMarkersRef.current?.setMarkers?.([]) } catch { /* noop */ }
      try { seriesMarkersRef.current?.detach?.() } catch { /* noop */ }
      for (const line of overlayPriceLinesRef.current) {
        try { cs?.removePriceLine(line) } catch { /* noop */ }
      }
      overlayPriceLinesRef.current = []
      seriesMarkersRef.current = null
      defaultViewRef.current = null
      pivotPrimitivesByPresetRef.current = new Map()
      carsPrimitivesByPresetRef.current = new Map()
      pivotDiagnosticSeenRef.current = new Set()
      pivotSegmentStartByPresetRef.current = new Map()
      nextRthFallbackWarnedRef.current = false
      crosshairCleanupRef.current?.()
      volumeReadoutCleanupRef.current?.()
      volumeReadoutCleanupRef.current = null
      // Chart rebuilds can precede the observer effect's nonce update.
      // Cancel queued geometry reads before disposing native chart internals.
      paneLayoutCleanupRef.current?.()
      paneLayoutCleanupRef.current = null
      try { chart.remove() } catch { /* noop */ }
      chartRef.current = null
      candleSeriesRef.current = null
      fixedOhlcBarPrimitiveRef.current = null
      sessionBackgroundPrimitiveRef.current = null
      executionMarkerPrimitiveRef.current = null
      quickMeasurePrimitiveRef.current = null
      setQuickMeasureSnapshot(null)
      seriesByPresetRef.current = new Map()
      paneRegistryRef.current = null
      // CE-04g-UI-chart-smoke-fixes — collapse the DIY UI state alongside
      // the chart teardown. Effect 1 re-syncs to `true` after the next
      // chart rebuild creates the volume pane on-demand.
      setHasVolumePane(false)
      // CE-04g-UI-indicators-live-tick — reset for symmetry with live mode;
      // the time-monotonicity guard must not see entries from a torn-down
      // chart's series.
      lastSeriesTimeRef.current = {}
      liveIndicatorHistoryDirtyRef.current = false
    }
  }, [mode, chartType, clearExecutionTooltipIntent, quickMeasureEnabled, handleQuickMeasureChange])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    try {
      chart.applyOptions({
        layout: chartCanvasAppearance.layout,
        grid: chartCanvasAppearance.grid,
        rightPriceScale: { textColor: chartCanvasAppearance.scaleTextColor },
      })
    } catch { /* noop — chart may be tearing down */ }
  }, [chartCanvasAppearance])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    chart.priceScale('right').applyOptions({ minimumWidth: rightPriceScaleMinimumWidth })
  }, [chartReadyNonce, rightPriceScaleMinimumWidth])

  useEffect(() => {
    quickMeasurePrimitiveRef.current?.applyOptions?.(quickMeasureAppearance)
  }, [quickMeasureAppearance])

  // Push bars to the candle series when they change.
  useEffect(() => {
    if (mode !== 'static') return
    if (!candleSeriesRef.current || !Array.isArray(bars)) return
    try {
      candleSeriesRef.current.setData(stylePriceBars(bars, chartType, chartSettings))
      updateFixedOhlcBarPrimitive(bars)
      const chart = chartRef.current
      if (
        chart
        && !applyStoredVisibleLogicalRange(chart, activeTimeframe, Array.isArray(bars) ? bars.length : 0, visibleLogicalRangesRef.current, activeChartMargins)
        && !applyStoredVisibleTimeRange(chart, activeTimeframe, visibleTimeRangesRef.current)
      ) {
        chart.timeScale()?.fitContent()
      }
    } catch { /* swallow — bad bar shape is caller's concern */ }
  }, [mode, bars, activeTimeframe, chartType, priceStyleKey, activeChartMargins])

  useEffect(() => {
    const primitive = syncSessionBackgroundPrimitive()
    if (!primitive) return
    const sourceBars = mode === 'static' ? bars : displayCandles
    const spans = marketSession === 'all'
      ? buildSessionBackgroundSpans(sourceBars, getMarketSession)
      : []
    primitive.update({
      spans,
      fillStyle: chartSettingsColor(
        chartSettings,
        'extendedHoursBackgroundColor',
        'extendedHoursBackgroundOpacity',
        '#64748B'
      ),
    })
  }, [mode, bars, displayCandles, marketSession, chartSettings, chartReadyNonce])

  useEffect(() => {
    const series = candleSeriesRef.current
    if (!series) return

    for (const line of overlayPriceLinesRef.current) {
      try { series.removePriceLine(line) } catch { /* noop */ }
    }
    overlayPriceLinesRef.current = []

    if (!liveChartDataCurrent) return

    const specs = buildOverlayPriceLineSpecs({ setupLines, positionLines, visualTheme, chartSettings })
    for (const spec of specs) {
      try {
        overlayPriceLinesRef.current.push(series.createPriceLine(spec))
      } catch { /* noop — chart may be tearing down */ }
    }
    return () => {
      for (const line of overlayPriceLinesRef.current) {
        try { series.removePriceLine(line) } catch { /* noop */ }
      }
      overlayPriceLinesRef.current = []
    }
  }, [setupLines, positionLines, chartReadyNonce, liveChartDataCurrent, visualTheme, chartSettings])

  // The workspace quote is already reference-counted by PositionHeader. Reuse
  // that price and provider session here so every timeframe gets one truthful
  // non-RTH line without opening a second market-data subscription.
  useEffect(() => {
    const series = candleSeriesRef.current
    const previousSeries = quoteSessionPriceLineSeriesRef.current
    if (previousSeries && previousSeries !== series && quoteSessionPriceLineRef.current) {
      try { previousSeries.removePriceLine(quoteSessionPriceLineRef.current) } catch { /* old chart already removed */ }
      quoteSessionPriceLineRef.current = null
      quoteSessionPriceLineSeriesRef.current = null
    }
    if (!series) return

    if (!externalSessionPriceSpec) {
      if (quoteSessionPriceLineRef.current) {
        try { series.removePriceLine(quoteSessionPriceLineRef.current) } catch { /* noop */ }
        quoteSessionPriceLineRef.current = null
        quoteSessionPriceLineSeriesRef.current = null
      }
      return
    }

    if (extendedPriceLineRef.current) {
      try { series.removePriceLine(extendedPriceLineRef.current) } catch { /* noop */ }
      extendedPriceLineRef.current = null
    }

    const visibleSpec = { ...externalSessionPriceSpec, ...sessionPriceVisibility(sessionPriceSession, chartSettings) }
    if (quoteSessionPriceLineRef.current) {
      try { quoteSessionPriceLineRef.current.applyOptions(visibleSpec) } catch { /* noop */ }
      return
    }

    try {
      quoteSessionPriceLineRef.current = series.createPriceLine(visibleSpec)
      quoteSessionPriceLineSeriesRef.current = series
    } catch { /* chart may be tearing down */ }
  }, [externalSessionPriceSpec, chartReadyNonce, activeTimeframe, sessionPriceSession,
    chartSettings.preMarketPriceVisible, chartSettings.postMarketPriceVisible, chartSettings.overnightPriceVisible])

  // Apply preferences to an existing fallback line without restarting the feed.
  useEffect(() => {
    extendedPriceLineRef.current?.applyOptions(sessionPriceVisibility(extendedPriceLineSessionRef.current, chartSettings))
  }, [chartReadyNonce, chartSettings.preMarketPriceVisible, chartSettings.postMarketPriceVisible, chartSettings.overnightPriceVisible])

  useEffect(() => {
    const series = candleSeriesRef.current
    if (!series) return

    const nextMarkers = liveChartDataCurrent ? buildSeriesMarkerSpecs(markers) : []
    try {
      if (!seriesMarkersRef.current) {
        seriesMarkersRef.current = createSeriesMarkers(series, nextMarkers)
      } else {
        seriesMarkersRef.current.setMarkers(nextMarkers)
      }
    } catch {
      seriesMarkersRef.current = null
    }
  }, [markers, chartReadyNonce, liveChartDataCurrent])

  const handleExecutionMarkerMouseMove = useCallback((event) => {
    if (privacyMode) return
    const primitive = executionMarkerPrimitiveRef.current
    const container = chartContainerRef.current
    if (!primitive || !container) return
    const rect = container.getBoundingClientRect()
    const annotation = primitive.hitTest({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    })
    const markerId = annotation?.marker?.id ?? null
    const pendingId = pendingExecutionTooltipMarkerIdRef.current
    const activeId = activeExecutionTooltipMarkerIdRef.current
    if (!markerId) {
      clearExecutionTooltipIntent()
      return
    }
    if (activeId === markerId) return
    if (pendingId === markerId) return

    clearExecutionTooltipIntent()
    pendingExecutionTooltipMarkerIdRef.current = markerId
    executionTooltipTimerRef.current = setTimeout(() => {
      executionTooltipTimerRef.current = null
      if (pendingExecutionTooltipMarkerIdRef.current !== markerId) return
      activeExecutionTooltipMarkerIdRef.current = markerId
      primitive.setHoveredMarkerId(markerId)
      setHoveredExecutionAnnotation(annotation)
    }, EXECUTION_TOOLTIP_HOVER_DELAY_MS)
  }, [privacyMode, clearExecutionTooltipIntent])

  const handleExecutionMarkerMouseLeave = useCallback(() => {
    clearExecutionTooltipIntent()
  }, [clearExecutionTooltipIntent])

  const handleOverlayAxisClick = useCallback((event) => {
    const series = candleSeriesRef.current
    const container = chartContainerRef.current
    if (!series || !container || selectableStopLines.length === 0) return
    const rect = container.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    if (x < rect.width - 88) {
      setSelectedStopPrice(null)
      return
    }

    let closest = null
    let closestDist = Infinity
    for (const line of selectableStopLines) {
      const lineY = series.priceToCoordinate(line.price)
      if (!Number.isFinite(lineY)) continue
      const dist = Math.abs(y - lineY)
      if (dist < 14 && dist < closestDist) {
        closest = line.price
        closestDist = dist
      }
    }
    setSelectedStopPrice((prev) => (closest != null && Math.abs(Number(prev) - closest) < 0.01 ? null : closest))
  }, [selectableStopLines])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    const handler = (range) => {
      if (suppressVisibleRangePersistenceRef.current) return
      if (mode === 'live') return
      const next = nextVisibleTimeRangeMap(visibleTimeRangesRef.current, activeTimeframe, range)
      if (!next) return
      visibleTimeRangesRef.current = next
      void writeServerChartPreference({
        scope: chartStorageScope,
        preferenceKey: CHART_PREFERENCE_KEYS.visibleTimeRange,
        preferences: visibleTimeRangesPreferencePayload(next),
      })
    }
    try { chart.timeScale().subscribeVisibleTimeRangeChange(handler) } catch { return undefined }
    return () => {
      try { chart.timeScale().unsubscribeVisibleTimeRangeChange(handler) } catch { /* noop */ }
    }
  }, [chartReadyNonce, activeTimeframe, mode, liveChartDataCurrent, chartStorageScope])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    const handler = (range) => {
      if (suppressVisibleRangePersistenceRef.current) return
      if (mode !== 'live') {
        const next = nextVisibleLogicalRangeMap(
          visibleLogicalRangesRef.current,
          activeTimeframe,
          range,
          visibleRangeBarCountRef.current
        )
        if (next) {
          visibleLogicalRangesRef.current = next
          void writeServerChartPreference({
            scope: chartStorageScope,
            preferenceKey: CHART_PREFERENCE_KEYS.visibleLogicalRange,
            preferences: visibleLogicalRangesPreferencePayload(next),
          })
        }
      } else if (!liveChartDataCurrent) {
        return
      }
      const currentScrollbackRange = Number.isFinite(range?.from) && Number.isFinite(range?.to)
        ? { from: range.from, to: range.to }
        : null
      const previousScrollbackRange = scrollbackVisibleRangeRef.current
      // Keep sub-bar animation steps accumulated against the last meaningful
      // position so smooth keyboard panning can still request older candles.
      if (currentScrollbackRange && (
        !previousScrollbackRange
        || Math.abs(currentScrollbackRange.from - previousScrollbackRange.from) > 1
      )) {
        scrollbackVisibleRangeRef.current = currentScrollbackRange
      }
      const movedLeft = Boolean(
        previousScrollbackRange
        && currentScrollbackRange
        && currentScrollbackRange.from < previousScrollbackRange.from - 1
      )
      if (
        mode === 'live'
        && liveChartDataCurrent
        && typeof loadMoreBefore === 'function'
        && movedLeft
        && Number.isFinite(range?.from)
        && range.from <= 25
        && !scrollbackInFlightRef.current
      ) {
        scrollbackInFlightRef.current = true
        const preserve = { from: range.from, to: range.to }
        loadMoreBefore()
          .then((added) => {
            if (!added || !chartRef.current) return
            try {
              chartRef.current.timeScale().setVisibleLogicalRange({
                from: preserve.from + added,
                to: preserve.to + added,
              })
            } catch { /* noop */ }
          })
          .finally(() => { scrollbackInFlightRef.current = false })
      }
    }
    try { chart.timeScale().subscribeVisibleLogicalRangeChange(handler) } catch { return undefined }
    return () => {
      try { chart.timeScale().unsubscribeVisibleLogicalRangeChange(handler) } catch { /* noop */ }
    }
  }, [chartReadyNonce, activeTimeframe, mode, liveChartDataCurrent, chartStorageScope, loadMoreBefore])

  // ── Live-mode chart lifecycle (CE-04g-UI-a) ────────────────────────────
  // Owns the chart shell only. Symbol-specific candles and tick streams are
  // reconciled below so switching assets does not blank and rebuild the LWC
  // canvas while the next candle request is in flight.
  useEffect(() => {
    if (mode !== 'live') return
    if (!chartBootstrapReady) return
    const container = chartContainerRef.current
    if (!container) return

    paneRegistryRef.current = new Map([['price', 0]])

    const rOffset = activeChartMargins.rightOffset

    // CE-04g-UI-chrome-1 — workspace-theme tokens (chartTokens.js).
    const chart = createChart(container, {
      autoSize: false,
      width: container.clientWidth || 800,
      height: container.clientHeight || 480,
      hoveredSeriesOnTop: false,
      // CE-05 pane cleanup — use the native LWC pane separator.
      // See the static-mode init for the rationale.
      layout: {
        ...chartCanvasAppearance.layout,
        attributionLogo: false,
        panes: { enableResize: true, separatorColor: BORDER_COLOR_RGBA, separatorHoverColor: BORDER_COLOR_RGBA_HOVER },
      },
      grid: chartCanvasAppearance.grid,
      crosshair: { ...CHART_CROSSHAIR_WORKSPACE, mode: CrosshairMode.Normal },
      rightPriceScale: {
        ...CHART_RIGHT_PRICE_SCALE_WORKSPACE,
        minimumWidth: rightPriceScaleMinimumWidth,
        textColor: chartCanvasAppearance.scaleTextColor,
        scaleMargins: activeChartScaleMargins,
      },
      timeScale: {
        ...CHART_TIME_SCALE_WORKSPACE,
        ...(compact ? { lockVisibleTimeRangeOnResize: true } : {}),
        timeVisible: activeTimeframe !== 'daily' && activeTimeframe !== 'weekly',
        rightOffset: rOffset,
      },
    })
    chartRef.current = chart
    // CE-04g-UI-indicators-live-tick — re-fire Effect 1 (lifecycle) once
    // the chart instance is live. See the static-mode init for rationale.
    setChartReadyNonce(n => n + 1)

    const candle = chartType === 'line'
      ? chart.addLineSeries(closeLineOptions(chartSettings))
      : chartType === 'bar'
      ? chart.addBarSeries(barSeriesOptions(chartSettings))
      : chart.addCandlestickSeries(candleSeriesOptions(chartSettings))
    candleSeriesRef.current = candle
    attachFixedOhlcBarPrimitive(candle)
    if (quickMeasureEnabled) {
      try {
        const primitive = new QuickMeasurePrimitive({
          onChange: handleQuickMeasureChange,
          style: quickMeasureAppearance,
        })
        candle.attachPrimitive(primitive)
        quickMeasurePrimitiveRef.current = primitive
      } catch { /* unsupported mock or chart teardown */ }
    }

    // CE-04g-UI-chart-smoke-fixes — pane 1 is created on-demand by Effect 1;
    // single-pane until a visible Volume indicator exists.
    try {
      applyPaneHeights(chart, paneRatioRef.current, measuredHeightRef.current)
    } catch { /* noop */ }

    const visibleBars = activeChartMargins.visibleBars
    const totalBars = displayCandles.length
    if (liveChartDataCurrent && totalBars > 0) {
      defaultViewRef.current = { totalBars, visibleBars, rOffset, defaultView: activeChartMargins.defaultView, candlesBeforeOpen: activeChartMargins.candlesBeforeOpen }
      applyDefaultVisibleLogicalRange(chart, totalBars, visibleBars, rOffset, mode, compact ? displayCandles.at(-1)?.time : null, compact ? {} : { candles: displayCandles, timeframe: activeTimeframe, ...activeChartMargins })
      liveDataViewKeyRef.current = `${symbol || ''}|${activeTimeframe}|${marketSession}|${timezone}`
      releaseVisibleRangePersistenceAfter()
    } else {
      defaultViewRef.current = null
    }

    let lastW = 0, lastH = 0
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        if (!e.contentRect) continue
        const w = Math.floor(e.contentRect.width)
        const h = Math.floor(e.contentRect.height)
        if (w <= 0 || h <= 0) continue
        if (w === lastW && h === lastH) continue
        lastW = w; lastH = h
        // Set pane proportions before painting the new size. ResizeObserver
        // runs before paint; deferring the canvas redraw exposes the old size.
        try { applyPaneHeights(chart, paneRatioRef.current, h) } catch { /* noop */ }
        try { chart.resize(w, h, true) } catch { /* chart torn down */ }
      }
    })
    try { ro.observe(container) } catch { /* noop */ }

    return () => {
      try { ro.disconnect() } catch { /* noop */ }
      // CE-04g-UI-pivots-iseriesprimitive-refactor — detach all primitives
      // before chart.remove() so LWC internals are still live. Mirrors the
      // static-mode teardown.
      const cs = candleSeriesRef.current
      const primMap = pivotPrimitivesByPresetRef.current
      if (cs && primMap) {
        for (const primitive of primMap.values()) {
          try { cs.detachPrimitive(primitive) } catch { /* noop */ }
        }
      }
      const carsMap = carsPrimitivesByPresetRef.current
      if (cs && carsMap) {
        for (const primitive of carsMap.values()) {
          try { cs.detachPrimitive(primitive) } catch { /* noop */ }
        }
      }
      if (cs && fixedOhlcBarPrimitiveRef.current) {
        try { cs.detachPrimitive(fixedOhlcBarPrimitiveRef.current) } catch { /* noop */ }
      }
      if (cs && executionMarkerPrimitiveRef.current) {
        try { cs.detachPrimitive(executionMarkerPrimitiveRef.current) } catch { /* noop */ }
      }
      if (cs && quickMeasurePrimitiveRef.current) {
        try { cs.detachPrimitive(quickMeasurePrimitiveRef.current) } catch { /* noop */ }
      }
      clearExecutionTooltipIntent()
      try { seriesMarkersRef.current?.setMarkers?.([]) } catch { /* noop */ }
      try { seriesMarkersRef.current?.detach?.() } catch { /* noop */ }
      for (const line of overlayPriceLinesRef.current) {
        try { cs?.removePriceLine(line) } catch { /* noop */ }
      }
      overlayPriceLinesRef.current = []
      seriesMarkersRef.current = null
      pivotPrimitivesByPresetRef.current = new Map()
      carsPrimitivesByPresetRef.current = new Map()
      pivotDiagnosticSeenRef.current = new Set()
      pivotSegmentStartByPresetRef.current = new Map()
      nextRthFallbackWarnedRef.current = false
      crosshairCleanupRef.current?.()
      volumeReadoutCleanupRef.current?.()
      volumeReadoutCleanupRef.current = null
      // Chart rebuilds can precede the observer effect's nonce update.
      // Cancel queued geometry reads before disposing native chart internals.
      paneLayoutCleanupRef.current?.()
      paneLayoutCleanupRef.current = null
      try { chart.remove() } catch { /* noop */ }
      chartRef.current = null
      candleSeriesRef.current = null
      fixedOhlcBarPrimitiveRef.current = null
      executionMarkerPrimitiveRef.current = null
      quickMeasurePrimitiveRef.current = null
      setQuickMeasureSnapshot(null)
      seriesByPresetRef.current = new Map()
      paneRegistryRef.current = null
      // CE-04g-UI-chart-smoke-fixes — parity with the static teardown
      // path: collapse the DIY UI state when the actual chart shell is gone.
      setHasVolumePane(false)
      setHasRenderedLiveCandles(false)
      lastSeriesTimeRef.current = {}
      liveIndicatorHistoryDirtyRef.current = false
    }
  }, [mode, activeTimeframe, chartType, clearExecutionTooltipIntent, chartBootstrapReady, quickMeasureEnabled, handleQuickMeasureChange])

  useEffect(() => {
    if (mode !== 'live') return
    if (!chartBootstrapReady) return
    if (fetchStatus !== 'ready' || !displayCandles.length) return
    const chart = chartRef.current
    const candle = candleSeriesRef.current
    if (!chart || !candle) return

    const liveDataViewKey = liveCandleViewKey
    const previousLiveDataViewKey = liveDataViewKeyRef.current
    const previousTotalBars = defaultViewRef.current?.totalBars
    const replacingSparseHistory = isReplacingSparseLiveHistory(
      previousLiveDataViewKey,
      liveDataViewKey,
      previousTotalBars,
      displayCandles.length,
    )
    const defaultViewAlreadyInitialized =
      previousLiveDataViewKey == null
      && defaultViewRef.current?.totalBars === displayCandles.length
    const preserveCurrentViewport =
      !replacingSparseHistory
      && defaultViewRef.current?.defaultView === activeChartMargins.defaultView
      && defaultViewRef.current?.candlesBeforeOpen === activeChartMargins.candlesBeforeOpen
      && (previousLiveDataViewKey === liveDataViewKey || defaultViewAlreadyInitialized)
    const visibleLogicalRange = preserveCurrentViewport
      ? readVisibleLogicalRange(chart)
      : null

    // Snapshot history is the handoff seed. Copy it so stream updates cannot
    // mutate the fetch-store array and so the active tail survives renders.
    liveCandlesRef.current = displayCandles.map((bar) => ({ ...bar }))
    displayCandlesRef.current = liveCandlesRef.current
    setHasRenderedLiveCandles(true)

    // Defensive reset — covers symbol change, timeframe switch, or refetch.
    barStateRef.current = { time: null, open: null, high: -Infinity, low: Infinity, close: null, sessionDateKey: null, clockBoundary: false, hasCorrection: false }
    if (extendedPriceLineRef.current) {
      try { candle.removePriceLine(extendedPriceLineRef.current) } catch { /* noop */ }
      extendedPriceLineRef.current = null
    }

    let painted = false
    try {
      candle.setData(stylePriceBars(displayCandles, chartType, chartSettings))
      updateFixedOhlcBarPrimitive(displayCandles)
      painted = true
    } catch { /* noop */ }

    if (painted) {
      liveDataViewKeyRef.current = liveDataViewKey
      const span = latencySpanRef.current
      if (span && span.key === latencyKey && span.firstPaintAt == null) {
        const now = marketDataLatencyNow()
        span.firstPaintAt = now
        const durationMs = roundLatencyMs(now - span.startedAt)
        emitMarketDataLatencyEvent({
          kind: span.reason === 'timeframe-switch' ? 'timeframe-repaint' : 'chart-first-paint',
          reason: span.reason,
          symbol: span.symbol,
          timeframe: span.timeframe,
          session: span.session,
          bars: displayCandles.length,
          durationMs,
          symbolToFirstPaintMs: span.reason === 'timeframe-switch' ? null : durationMs,
          timeframeClickToRepaintMs: span.reason === 'timeframe-switch' ? durationMs : null,
          startedWallTs: span.startedWallTs,
        })
      }
    }

    const rOffset = activeChartMargins.rightOffset
    const visibleBars = activeChartMargins.visibleBars
    const totalBars = displayCandles.length
    defaultViewRef.current = { totalBars, visibleBars, rOffset, defaultView: activeChartMargins.defaultView, candlesBeforeOpen: activeChartMargins.candlesBeforeOpen }
    if (preserveCurrentViewport && visibleLogicalRange) {
      restoreVisibleLogicalRange(chart, visibleLogicalRange)
    } else {
      applyDefaultVisibleLogicalRange(chart, totalBars, visibleBars, rOffset, mode, compact ? displayCandles.at(-1)?.time : null, compact ? {} : { candles: displayCandles, timeframe: activeTimeframe, ...activeChartMargins })
      releaseVisibleRangePersistenceAfter()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, displayCandles, fetchStatus, activeTimeframe, chartType, priceStyleKey, activeChartMargins.rightOffset, activeChartMargins.visibleBars, activeChartMargins.defaultView, activeChartMargins.candlesBeforeOpen, releaseVisibleRangePersistenceAfter, chartBootstrapReady, latencyKey, symbol, marketSession, timezone, liveCandleViewKey])

  // Privacy is a display override, never a saved chart preference. Clear before paint.
  useLayoutEffect(() => {
    if (!privacyMode) return
    executionMarkerPrimitiveRef.current?.update?.({ markers: [], bars: [], theme: visualTheme })
    setExecutionAnnotations(prev => prev.length === 0 ? prev : [])
    clearExecutionTooltipIntent()
  }, [privacyMode, visualTheme, clearExecutionTooltipIntent])

  // Project execution markers after the history seed has reached the series.
  // Otherwise a ready-to-ready history refresh can keep the prior bar array.
  useEffect(() => {
    const chart = chartRef.current
    const series = candleSeriesRef.current
    if (privacyMode || !liveChartDataCurrent || !chartGeometrySettings.executionMarkersVisible) {
      executionMarkerPrimitiveRef.current?.update?.({ markers: [], bars: [], theme: visualTheme })
      setExecutionAnnotations((prev) => prev.length === 0 ? prev : [])
      clearExecutionTooltipIntent()
      return
    }
    if (!chart || !series) {
      executionMarkerPrimitiveRef.current?.update?.({ markers: [], bars: [], theme: visualTheme })
      setExecutionAnnotations((prev) => prev.length === 0 ? prev : [])
      clearExecutionTooltipIntent()
      return
    }

    const sourceBars = mode === 'static' ? bars : displayCandlesRef.current
    if (!Array.isArray(sourceBars) || sourceBars.length === 0) {
      executionMarkerPrimitiveRef.current?.update?.({ markers: [], bars: [], theme: visualTheme })
      setExecutionAnnotations((prev) => prev.length === 0 ? prev : [])
      clearExecutionTooltipIntent()
      return
    }

    const cutoff = markerRangeCutoff(markerRange)
    const annotationMarkers = (Array.isArray(markers) ? markers : [])
      .filter(markerIsExecutionAnnotation)
      .filter((marker) => {
        const rawTime = finiteOverlayPrice(marker.bar_time_ref ?? marker.time)
        const executedAt = markerExecutedAt(marker)
        const fillTime = executedAt ? Date.parse(executedAt) / 1000 : NaN
        const rangeTime = Number.isFinite(fillTime) ? fillTime : rawTime
        return rawTime != null && (cutoff === 0 || rangeTime >= cutoff)
      })
    const pendingMarkerId = pendingExecutionTooltipMarkerIdRef.current
    if (
      pendingMarkerId != null
      && !annotationMarkers.some((marker) => String(marker?.id ?? '') === String(pendingMarkerId))
    ) {
      clearExecutionTooltipIntent()
    }
    if (annotationMarkers.length === 0) {
      executionMarkerPrimitiveRef.current?.update?.({ markers: [], bars: sourceBars, theme: visualTheme })
      setExecutionAnnotations((prev) => prev.length === 0 ? prev : [])
      clearExecutionTooltipIntent()
      return
    }

    let primitive = executionMarkerPrimitiveRef.current
    if (!primitive) {
      primitive = new ExecutionMarkerPrimitive()
      try {
        series.attachPrimitive(primitive)
        executionMarkerPrimitiveRef.current = primitive
      } catch {
        executionMarkerPrimitiveRef.current = null
        setExecutionAnnotations((prev) => prev.length === 0 ? prev : [])
        return
      }
    }
    primitive.update({ markers: annotationMarkers, bars: sourceBars, theme: visualTheme, timeframe: activeTimeframe })
    primitive.setSelectedMarkerIds(selectedExecutionMarkerIds)
    const annotations = primitive.annotations()
    const hoveredId = pendingExecutionTooltipMarkerIdRef.current ?? activeExecutionTooltipMarkerIdRef.current
    if (hoveredId != null && !annotations.some(annotation => String(annotation.marker?.id) === String(hoveredId))) {
      clearExecutionTooltipIntent()
    }
    setExecutionAnnotations(annotations)
  }, [markers, markerRange, privacyMode, chartGeometrySettings.executionMarkersVisible, selectedExecutionMarkerIds, chartReadyNonce, mode, bars, activeTimeframe, fetchStatus, liveChartDataCurrent, visualTheme, clearExecutionTooltipIntent, displayCandles, livePumpNonce])

  // ── Per-tick OHLC.update via ibkrStream ──────────────────────────────
  useEffect(() => {
    if (mode !== 'live') return
    if (!chartBootstrapReady) return
    if (fetchStatus !== 'ready' || !displayCandles.length) return
    if (chartParityMode) return

    let cancelled = false
    let unsubStream = null
    const barState = barStateRef.current
    barState.time = null; barState.open = null; barState.high = -Infinity; barState.low = Infinity
    barState.close = null; barState.sessionDateKey = null; barState.clockBoundary = false; barState.hasCorrection = false
    const last = displayCandles[displayCandles.length - 1]
    if (last) {
      barState.time = last.time; barState.open = last.open; barState.high = last.high; barState.low = last.low
      barState.close = last.close
      // CE-04g-UI-vwap-anchor: toCandles now stamps `rawEpochMs` on every
      // candle, so the history seed carries the real-instant epoch too.
      // The reducer learns `sessionDateKey` from the first accepted
      // regular-session tick — including same-bar ticks. See CONTEXT.md
      // CE-04g-UI-live-bar-continuity for the bootstrap contract.
      barState.rawEpochMs = last.rawEpochMs ?? null
    }

    const isOpt = isOCCSymbol(symbol)
    const occ = isOpt ? parseOCC(symbol) : null
    const streamSymbol = occ ? occ.underlying : symbol
    const streamParams = occ
      ? { secType: 'OPT', expiry: occ.expiry, strike: String(occ.strike), right: occ.right }
      : {}
    const useEquityLiveCurrent = !isOpt && EQUITY_STREAM_TIMEFRAMES.has(activeTimeframe)

    // The workspace's official-Python IBKR service owns both the forming
    // candle and outside-hours price stream. This branch deliberately does
    // not subscribe to the legacy bridge or the equity fanout.
    if (candleAuthority === 'ibkr-runtime') {
      if (!streamUrl) return undefined
      const stop = (sharedDailySource?.subscribe || subscribeIbkrChartStream)({
        streamUrl,
        timeframe: activeTimeframe,
        reconcile: reconcileStream,
        onFormingCandle: (rawCandle, payload) => {
          if (cancelled) return
          const corrected = toCandles([rawCandle], timezone)[0]
          if (!corrected) return
          corrected.authoritativeForming = true
          const eventEpochMs = Number(payload?.eventTs)
          if (Number.isFinite(eventEpochMs) && eventEpochMs > 0) {
            corrected.eventEpochMs = eventEpochMs
          }
          const latest = liveCandlesRef.current?.at(-1) || displayCandlesRef.current?.at(-1) || null
          if (latest?.time != null && corrected.time < latest.time) return
          applyCorrectedLiveBar(corrected)
          recordFirstLiveUpdateAfterPaint(payload, corrected)
        },
        onCompletedCandle: (rawCandle, payload) => {
          if (cancelled) return
          const corrected = toCandles([rawCandle], timezone)[0]
          if (!corrected) return
          const eventEpochMs = Number(payload?.eventTs)
          if (Number.isFinite(eventEpochMs) && eventEpochMs > 0) {
            corrected.eventEpochMs = eventEpochMs
          }
          const source = liveCandlesRef.current?.length
            ? liveCandlesRef.current
            : displayCandlesRef.current
          if (!Array.isArray(source)) return
          const reconciliation = reconcileRuntimeCandle(source, corrected, { completed: true })
          if (!reconciliation.accepted) return
          // A just-closed bar can receive its final IBKR TRADES checkpoint
          // after the next live bucket has opened. Replace that exact bar and
          // repaint the bounded in-memory series; `series.update()` only
          // accepts its trailing point and cannot correct older timestamps.
          const next = reconciliation.bars
          liveIndicatorHistoryDirtyRef.current = true
          liveCandlesRef.current = next
          displayCandlesRef.current = next
          const visibleRange = readVisibleLogicalRange(chartRef.current)
          try {
            candleSeriesRef.current?.setData(stylePriceBars(next, chartType, chartSettingsRef.current))
            updateFixedOhlcBarPrimitive(next)
          } catch { /* the next full history paint remains authoritative */ }
          restoreVisibleLogicalRange(chartRef.current, visibleRange, { force: true })
          scheduleLivePump()
        },
        onPrice: (payload) => {
          if (cancelled || !candleSeriesRef.current) return
          if (externalSessionPriceActiveRef.current) return
          const session = String(payload?.session || '').toLowerCase()
          const price = Number(payload?.price)
          if (!Number.isFinite(price) || price <= 0) return
          if (session !== 'pre' && session !== 'post') return
          if (extendedPriceLineRef.current) {
            try { candleSeriesRef.current.removePriceLine(extendedPriceLineRef.current) } catch { /* noop */ }
          }
          extendedPriceLineSessionRef.current = session
          extendedPriceLineRef.current = candleSeriesRef.current.createPriceLine({
            ...sessionPriceVisibility(session, chartSettingsRef.current),
            price, color: '#3B82F6', lineWidth: 1, lineStyle: 2,
            title: session === 'pre' ? 'Pre' : 'Post',
          })
        },
        onState: ({ state }) => { if (!cancelled) setRuntimeFanoutStatus(state || 'idle') },
        onError: () => {},
      })
      return () => {
        cancelled = true
        stop()
        setRuntimeFanoutStatus('idle')
        if (extendedPriceLineRef.current) {
          try { candleSeriesRef.current?.removePriceLine(extendedPriceLineRef.current) } catch { /* noop */ }
          extendedPriceLineRef.current = null
        }
      }
    }

    if (useEquityLiveCurrent) {
      const equityBarSize = equityStreamBarSizeForTimeframe(activeTimeframe)
      let unsubscribeFanout = null
      let unsubscribeExtendedStream = null
      if (equityBarSize) {
        unsubscribeFanout = subscribeEquityStream({
          symbol: streamSymbol,
          timeframe: activeTimeframe,
          timezone,
          session: marketSession,
          onBar: ({ chartBar: corrected, payload }) => {
            if (cancelled || payload?.isFinal) return
            const latest = liveCandlesRef.current?.at(-1) || displayCandlesRef.current?.at(-1) || null
            if (latest?.time != null && corrected.time < latest.time) return
            if (shouldIgnoreClosedSessionLiveBar(corrected, latest, {
              timeframe: activeTimeframe,
              marketSession,
            })) return
            emitMarketDataLatencyDebug({
              symbol: streamSymbol,
              timeframe: activeTimeframe,
              session: marketSession,
              payload,
              corrected,
            })
            applyCorrectedLiveBar(corrected)
            recordFirstLiveUpdateAfterPaint(payload, corrected)
          },
          onLastPrice: ({ chartBar: corrected, payload }) => {
            if (cancelled) return
            const latest = liveCandlesRef.current?.at(-1) || displayCandlesRef.current?.at(-1) || null
            if (latest?.time != null && corrected.time < latest.time) return
            if (shouldIgnoreClosedSessionLiveBar(corrected, latest, {
              timeframe: activeTimeframe,
              marketSession,
            })) return
            emitMarketDataLatencyDebug({
              symbol: streamSymbol,
              timeframe: activeTimeframe,
              session: marketSession,
              payload,
              corrected,
            })
            applyCorrectedLiveBar(corrected)
            recordFirstLiveUpdateAfterPaint(payload, corrected)
          },
          onStatus: ({ state }) => {
            if (!cancelled) setRuntimeFanoutStatus(state)
          },
          onError: () => {},
        })
      }

      if (activeTimeframe === 'daily') {
        ibkrStream.subscribe(streamSymbol, (tick) => {
          if (!candleSeriesRef.current || cancelled) return
          if (externalSessionPriceActiveRef.current) return

          const tickInstant = getTickInstant(tick)
          if (tickInstant === null) return
          if (tick?.field !== 'last' || tick?.source === 'ibkr-snapshot') return
          const price = Number(tick.value)
          if (!Number.isFinite(price) || price <= 0) return

          const session = getMarketSession(tickInstant)
          if (session !== 'pre' && session !== 'post') {
            if (extendedPriceLineRef.current) {
              try { candleSeriesRef.current.removePriceLine(extendedPriceLineRef.current) } catch { /* noop */ }
              extendedPriceLineRef.current = null
            }
            return
          }

          if (extendedPriceLineRef.current) {
            try { candleSeriesRef.current.removePriceLine(extendedPriceLineRef.current) } catch { /* noop */ }
          }
          const lineTitle = session === 'pre' ? 'Pre' : 'Post'
          extendedPriceLineSessionRef.current = session
          extendedPriceLineRef.current = candleSeriesRef.current.createPriceLine({
            ...sessionPriceVisibility(session, chartSettingsRef.current),
            price, color: '#3B82F6', lineWidth: 1, lineStyle: 2, title: lineTitle,
          })
        }, streamParams).then(u => {
          if (cancelled) { if (u) u(); return }
          unsubscribeExtendedStream = u
        }).catch(() => {})
      }

      return () => {
        cancelled = true
        setRuntimeFanoutStatus('idle')
        if (unsubscribeFanout) unsubscribeFanout()
        if (unsubscribeExtendedStream) unsubscribeExtendedStream()
        if (livePumpFrameRef.current != null) {
          if (livePumpUsesAnimationFrameRef.current && typeof cancelAnimationFrame === 'function') {
            cancelAnimationFrame(livePumpFrameRef.current)
          } else {
            clearTimeout(livePumpFrameRef.current)
          }
          livePumpFrameRef.current = null
          livePumpUsesAnimationFrameRef.current = false
        }
        if (extendedPriceLineRef.current) {
          try { candleSeriesRef.current?.removePriceLine(extendedPriceLineRef.current) } catch { /* noop */ }
          extendedPriceLineRef.current = null
        }
      }
    }

    ibkrStream.subscribe(streamSymbol, (tick) => {
      if (!candleSeriesRef.current || cancelled) return
      if (activeTimeframe === 'weekly') return

      const tickInstant = getTickInstant(tick)
      if (tickInstant === null) return
      const previousBarTime = barStateRef.current?.time ?? null
      const result = reduceLiveTick(barStateRef.current, tick, {
        tickInstant,
        getMarketSession,
        getEtSessionDateKey,
        floorToBarPeriod,
        timeframe: activeTimeframe,
        timezone,
      })
      barStateRef.current = result.nextBarState

      if (result.kind === 'update') {
        if (extendedPriceLineRef.current) {
          try { candleSeriesRef.current.removePriceLine(extendedPriceLineRef.current) } catch { /* noop */ }
          extendedPriceLineRef.current = null
        }
        updateLivePriceBar(
          chartRef.current,
          candleSeriesRef.current,
          stylePriceBar(
            result.barUpdate,
            previousRenderedBarForUpdate(displayCandlesRef.current, result.barUpdate),
            chartType,
            chartSettingsRef.current,
          ),
          true,
        )
        updateFixedOhlcBarPrimitive(composeEffectiveBars(
          displayCandlesRef.current,
          result.barUpdate,
        ))
        scheduleLivePump()
      } else if (result.kind === 'extended' && !externalSessionPriceActiveRef.current) {
        if (extendedPriceLineRef.current) {
          try { candleSeriesRef.current.removePriceLine(extendedPriceLineRef.current) } catch { /* noop */ }
        }
        const lineTitle = result.session === 'pre' ? 'Pre' : 'Post'
        extendedPriceLineSessionRef.current = result.session
        extendedPriceLineRef.current = candleSeriesRef.current.createPriceLine({
          ...sessionPriceVisibility(result.session, chartSettingsRef.current),
          price: result.price, color: '#3B82F6', lineWidth: 1, lineStyle: 2, title: lineTitle,
        })
      }
    }, streamParams).then(u => {
      if (cancelled) { if (u) u(); return }
      unsubStream = u
    }).catch(() => {})

    return () => {
      cancelled = true
      if (unsubStream) unsubStream()
      // Drop the pending paint so it cannot update a torn-down chart.
      if (livePumpFrameRef.current != null) {
        if (livePumpUsesAnimationFrameRef.current && typeof cancelAnimationFrame === 'function') {
          cancelAnimationFrame(livePumpFrameRef.current)
        } else {
          clearTimeout(livePumpFrameRef.current)
        }
        livePumpFrameRef.current = null
        livePumpUsesAnimationFrameRef.current = false
      }
      if (extendedPriceLineRef.current) {
        try { candleSeriesRef.current?.removePriceLine(extendedPriceLineRef.current) } catch { /* noop */ }
        extendedPriceLineRef.current = null
      }
    }
  }, [sharedDailySource?.subscribe, mode, chartBootstrapReady, displayCandles, fetchStatus, activeTimeframe, timezone, symbol, chartType, marketSession, chartParityMode, candleAuthority, streamUrl, reconcileStream, applyCorrectedLiveBar, clearExecutionTooltipIntent, scheduleLivePump, recordFirstLiveUpdateAfterPaint])

  useEffect(() => {
    if (mode !== 'live') return undefined
    if (!chartBootstrapReady) return undefined
    if (fetchStatus !== 'ready' || !displayCandlesLength) return undefined
    if (chartParityMode) return undefined
    if (candleAuthority === 'ibkr-runtime') return undefined
    if (!EQUITY_STREAM_TIMEFRAMES.has(activeTimeframe)) return undefined

    const tick = () => { applyClockBoundaryLiveBar() }
    liveBoundaryTimerRef.current = setInterval(tick, LIVE_BOUNDARY_POLL_MS)
    tick()

    return () => {
      if (liveBoundaryTimerRef.current) {
        clearInterval(liveBoundaryTimerRef.current)
        liveBoundaryTimerRef.current = null
      }
    }
  }, [
    mode,
    chartBootstrapReady,
    fetchStatus,
    displayCandlesLength,
    activeTimeframe,
    chartParityMode,
    candleAuthority,
    applyClockBoundaryLiveBar,
  ])

  // ── Scale + reset-view handlers (CE-04g-UI-a) ───────────────────────────
  const applyAutoScale = useCallback((next) => {
    const chart = chartRef.current
    if (!chart) return
    updateChartBehavior({ autoScale: next })
    chart.priceScale('right').applyOptions({ autoScale: next })
  }, [updateChartBehavior])

  const toggleAutoScale = useCallback(() => {
    applyAutoScale(!autoScale)
  }, [applyAutoScale, autoScale])

  const applyLogScale = useCallback((next) => {
    const chart = chartRef.current
    if (!chart) return
    updateChartBehavior({ logScale: next })
    chart.priceScale('right').applyOptions({ mode: next ? 1 : 0 })
  }, [updateChartBehavior])

  useEffect(() => {
    chartRef.current?.priceScale('right').applyOptions({ autoScale, mode: logScale ? 1 : 0 })
  }, [chartReadyNonce, autoScale, logScale])

  const toggleLogScale = useCallback(() => {
    applyLogScale(!logScale)
  }, [applyLogScale, logScale])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    try {
      chart.priceScale('right').applyOptions({
        scaleMargins: activeChartScaleMargins,
      })
    } catch { /* noop */ }
    try {
      // Dynamic geometry owns the right edge. Reapplying the legacy fixed
      // margin during Option+R's autoscale update can overwrite its queued range.
      if (!(mode === 'live' && !compact && activeChartMargins.defaultView === 'dynamic')) {
        const range = mode === 'live' && activeChartMargins.defaultView === 'session-open'
          ? readVisibleLogicalRange(chart) : null
        chart.timeScale().applyOptions?.({ rightOffset: activeChartMargins.rightOffset })
        restoreVisibleLogicalRange(chart, range)
      }
    } catch { /* noop */ }
    if (defaultViewRef.current) {
      defaultViewRef.current = {
        ...defaultViewRef.current,
        rOffset: activeChartMargins.rightOffset,
        visibleBars: activeChartMargins.visibleBars,
      }
    }
  }, [
    chartReadyNonce,
    activeChartScaleMargins,
    activeChartMargins.rightOffset,
    activeChartMargins.visibleBars,
    activeChartMargins.defaultView,
    mode,
    compact,
  ])

  const dynamicCanvas = useDynamicCanvas({
    enabled: mode === 'live' && !compact && DYNAMIC_CANVAS_TIMEFRAMES.includes(activeTimeframe) && activeChartMargins.defaultView === 'dynamic',
    viewKey: liveCandleViewKey, ready: liveChartDataCurrent, chartReadyNonce,
    chartRef, containerRef: chartContainerRef, candlesRef: displayCandlesRef,
    timeframe: activeTimeframe, margins: activeChartMargins,
    beforeApply: suppressVisibleRangePersistence,
  })
  dynamicNavigationRef.current = dynamicCanvas

  const resetView = useCallback(() => {
    const chart = chartRef.current
    if (!chart) return
    const nextTimeRanges = clearVisibleRangeMapTimeframe(visibleTimeRangesRef.current, activeTimeframe)
    const nextLogicalRanges = clearVisibleRangeMapTimeframe(visibleLogicalRangesRef.current, activeTimeframe)
    visibleTimeRangesRef.current = nextTimeRanges
    visibleLogicalRangesRef.current = nextLogicalRanges
    if (mode !== 'live') {
      void writeServerChartPreference({
        scope: chartStorageScope,
        preferenceKey: CHART_PREFERENCE_KEYS.visibleTimeRange,
        preferences: visibleTimeRangesPreferencePayload(nextTimeRanges),
      })
      void writeServerChartPreference({
        scope: chartStorageScope,
        preferenceKey: CHART_PREFERENCE_KEYS.visibleLogicalRange,
        preferences: visibleLogicalRangesPreferencePayload(nextLogicalRanges),
      })
    }
    suppressVisibleRangePersistence(180)
    chart.priceScale('right').applyOptions({ autoScale: true })
    updateChartBehavior({ autoScale: true })
    if (dynamicCanvas.reset()) return
    const dv = defaultViewRef.current
    if (dv) {
      applyDefaultVisibleLogicalRange(chart, mode === 'live' ? displayCandlesRef.current.length : dv.totalBars, dv.visibleBars, dv.rOffset, mode, compact ? candleSeriesRef.current?.data().at(-1)?.time : null, compact ? {} : { candles: displayCandlesRef.current, timeframe: activeTimeframe, ...activeChartMargins })
    } else {
      chart.timeScale().fitContent()
    }
  }, [activeTimeframe, activeChartMargins, chartStorageScope, mode, compact, suppressVisibleRangePersistence, updateChartBehavior, dynamicCanvas.reset])

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!keyboardActive || chartContainerRef.current?.closest('[inert]')) return
      const tagName = event.target?.tagName || ''
      if (event.target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(tagName)) return
      if (!event.altKey || event.metaKey || event.ctrlKey || event.shiftKey) return
      if (String(event.key).toLowerCase() !== 'r' && event.code !== 'KeyR') return
      event.preventDefault()
      resetView()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [resetView, keyboardActive])

  useEffect(() => {
    chartRef.current?.applyOptions({ localization: { timeFormatter: chartTimeLabelFormatter(activeTimeframe) } })
  }, [chartReadyNonce, activeTimeframe])

  useEffect(() => {
    const series = candleSeriesRef.current
    if (!series) return undefined
    const custom = chartSettings.currentPriceLabelCustom === true
      && /^#[0-9a-f]{6}$/i.test(chartSettings.currentPriceLabelColor || '')
    if (!custom) return undefined
    series.applyOptions({ lastValueVisible: false })
    const primitive = new CurrentPriceLabelPrimitive(chartSettings.currentPriceLabelColor)
    series.attachPrimitive(primitive)
    return () => {
      try {
        series.detachPrimitive(primitive)
        series.applyOptions({ lastValueVisible: true })
      } catch (error) {
        if (error.message !== 'Object is disposed') throw error
      }
    }
  }, [chartReadyNonce, chartSettings.currentPriceLabelCustom, chartSettings.currentPriceLabelColor])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart || !crosshairEnabled) return
    const line = (axis, base) => {
      const prefix = `crosshair${axis}`
      const visible = chartSettings.crosshairVisible !== false && chartSettings[`${prefix}Visible`] !== false
      const labelColor = axis === 'Horizontal' ? chartSettings.crosshairPriceLabelColor : chartSettings.dateLabelColor
      return {
        ...base,
        ...(/^#[0-9a-f]{6}$/i.test(labelColor || '') ? { labelBackgroundColor: labelColor } : {}),
        visible, labelVisible: visible,
        color: chartSettingsColor(chartSettings, `${prefix}Color`, `${prefix}Opacity`, '#A2A2A2'),
        width: [1, 2, 3, 4].includes(chartSettings[`${prefix}Width`]) ? chartSettings[`${prefix}Width`] : 1,
        style: [0, 1, 2].includes(chartSettings[`${prefix}Style`]) ? chartSettings[`${prefix}Style`] : 2,
      }
    }
    chart.applyOptions({ crosshair: {
      mode: CrosshairMode.Normal,
      vertLine: line('Vertical', CHART_CROSSHAIR_WORKSPACE.vertLine),
      horzLine: line('Horizontal', CHART_CROSSHAIR_WORKSPACE.horzLine),
    } })
  }, [chartReadyNonce, crosshairEnabled, chartSettings])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart || !crosshairEnabled) return undefined
    const onMove = (event) => {
      // Programmatic crosshairs have no sourceEvent: never feed back.
      if (!event.sourceEvent) return
      const point = event.point
      const series = candleSeriesRef.current
      clearSyncedCrosshairOverlay(series)
      const time = event.time ?? coordinateToChartTime(chart, series, point?.x, activeTimeframeRef.current)
      if (!liveChartDataCurrent || !series || time == null || !point || point.x < 0 || point.y < 0) {
        onCrosshairMove?.(null)
        return
      }
      const price = event.paneIndex === 0 || event.paneIndex == null
        ? series.coordinateToPrice(point.y) : event.seriesData?.get(series)?.close
      onCrosshairMove?.({ time, timeframe: activeTimeframeRef.current, price, projected: event.time == null })
    }
    const onLeave = () => onCrosshairMove?.(null)
    const container = chartContainerRef.current
    chart.subscribeCrosshairMove(onMove)
    container?.addEventListener('pointerleave', onLeave)
    let subscribed = true
    const cleanup = () => {
      if (!subscribed) return
      subscribed = false
      chart.unsubscribeCrosshairMove(onMove)
      container?.removeEventListener('pointerleave', onLeave)
      if (crosshairCleanupRef.current === cleanup) crosshairCleanupRef.current = null
    }
    crosshairCleanupRef.current = cleanup
    return cleanup
  }, [chartReadyNonce, crosshairEnabled, onCrosshairMove, liveChartDataCurrent])

  // Optional composition hook: paired with chart lifetime, never per tick.
  const drawingApi = useMemo(() => chartRef.current && candleSeriesRef.current && (mode !== 'live' || liveChartDataCurrent)
    ? { chart: chartRef.current, series: candleSeriesRef.current } : null, [chartReadyNonce, liveChartDataCurrent, mode])
  useEffect(() => {
    if (!onChartReady || !chartRef.current || !candleSeriesRef.current || (mode === 'live' && !liveChartDataCurrent)) return undefined
    onChartReady({ chart: chartRef.current, series: candleSeriesRef.current })
    return () => onChartReady(null)
  }, [onChartReady, chartReadyNonce, mode, liveChartDataCurrent])

  // CE-04g-UI-indicators-live-tick — the previous monolithic reconcile
  // (create + setData + remove + apply-options) is split into three
  // effects with strict separation of responsibilities, so the live-tick
  // pump (Effect 3) can drive only `series.update()` on the trailing
  // point without ever calling `setData()`. Strict separation:
  //
  //   Effect 1 (lifecycle): SOLE owner of addLineSeries / addAreaSeries /
  //     addHistogramSeries / removeSeries / applyOptions. Deps exclude
  //     bar arrays so it does not re-fire on settled-bar boundaries.
  //   Effect 2 (full-data reconcile): SOLE owner of `series.setData()`.
  //     Only setData on series that already exist in the per-preset
  //     bucket (Effect 1's responsibility to create) and whose component
  //     is visible. Fires on mount, indicator add/remove (after Effect 1
  //     catches up), timeframe switch, settled-bar boundary.
  //   Effect 3 (live pump): SOLE owner of `series.update()`. Reads
  //     `displayCandlesRef` (not the closure-captured `displayCandles`)
  //     so the 200ms-trailing pump picks up the latest settled state
  //     without re-firing Effect 3 every settled bar.
  //
  // Indicator-instance invisibility (`style.visible === false`) tears
  // down ALL component series in Effect 1; per-component invisibility
  // tears down only that component's series.

  // Effect 1 — lifecycle. Creates / removes / styles series. Reads NO
  // bar arrays in its dependency list. `chartReadyNonce` re-fires this
  // effect after the chart instance is created in live mode (where
  // displayCandles becoming non-empty is what triggers chart creation,
  // but is NOT in this dep list by design).
  //
  // CE-04g-UI-chart-smoke-fixes — Effect 1 owns:
  //   - Volume pane on-demand creation/removal (TV-style collapse): pane
  //     1 is added via chart.addPane(false) when a visible Volume
  //     indicator exists, removed via chart.removePane(idx) when none
  //     does. Both calls are idempotency-guarded.
  //   - autoscaleInfoProvider: () => null on every non-candle overlay
  //     line series so SMA/EMA/VWAP/Bollinger/Donchian do not pollute
  //     the price-pane autoscale (Strategy chart precedent).
  //
  // CE-04g-UI-pivots-iseriesprimitive-refactor — Effect 1 also owns
  // PivotPrimitive attach/detach lifecycle (one primitive per visible
  // pivot preset, attached to the candle series via attachPrimitive).
  // Pivot payload computation (levels, dedupe, segment anchors, font
  // size) lives in Effect 2.5, which is the only effect that reads
  // `bars` / `displayCandles`. Adding either to Effect 1's deps would
  // re-run the entire indicator lifecycle on every settled-bar boundary
  // and regress the CE-04g-UI-indicators-live-tick three-effect contract.
  useEffect(() => {
    if (mode !== 'static' && mode !== 'live') return
    if (mode === 'live' && !hasRenderedLiveCandles) return
    const chart = chartRef.current
    if (!chart) return

    // Remove observers before any histogram or pane can be removed/replaced.
    volumeReadoutCleanupRef.current?.()
    volumeReadoutCleanupRef.current = null
    const map = seriesByPresetRef.current
    const stillPresent = new Set()
    if (!lastSeriesTimeRef.current) lastSeriesTimeRef.current = {}

    for (const ind of indicators) {
      // Pivots bypass the generic component pipeline — they render as
      // horizontal price lines on the candle series, not as line series.
      if (ind.kind === 'pivots') continue

      const components = COMPONENTS_BY_KIND[ind.kind]
      if (!components) continue
      stillPresent.add(ind.presetId)
      const instanceVisible = ind.style?.visible !== false
      let bucket = map.get(ind.presetId)
      if (!bucket) {
        bucket = {}
        map.set(ind.presetId, bucket)
      }
      if (!lastSeriesTimeRef.current[ind.presetId]) {
        lastSeriesTimeRef.current[ind.presetId] = {}
      }

      // CE-04g-UI-chart-smoke-fixes — Volume pane on-demand creation /
      // removal. The Volume branch is consolidated here (instead of the
      // generic per-component loop below) because the pane lifecycle is
      // tied to the histogram series lifecycle 1:1.
      if (ind.kind === 'volume') {
        const componentStyle = ind.style?.bars || {}
        const componentVisible = componentStyle.visible !== false
        const wantVisible = instanceVisible && componentVisible

        if (!wantVisible) {
          if (bucket.bars) {
            try { chart.removeSeries(bucket.bars) } catch { /* noop */ }
            delete bucket.bars
            lastSeriesTimeRef.current[ind.presetId].bars = null
          }
          continue
        }

        if (!bucket.bars) {
          // Mint pane 1 on demand (idempotent — reuse existing index if a
          // prior visible-Volume indicator already created it).
          if (!paneRegistryRef.current.has('volume')) {
            try {
              const pane = chart.addPane(false)
              const paneIdx = (pane && typeof pane.paneIndex === 'function')
                ? pane.paneIndex()
                : (chart.panes().length - 1)
              paneRegistryRef.current.set('volume', paneIdx)
            } catch { /* noop — pane API absent in some test mocks */ }
          }
          const paneIndex = paneRegistryRef.current.get('volume') ?? 1
          const series = chart.addHistogramSeries({
            priceScaleId: 'volume',
            priceFormat: { type: 'volume' },
            priceLineVisible: false,
            lastValueVisible: false,
          }, paneIndex)
          bucket.bars = series
          lastSeriesTimeRef.current[ind.presetId].bars = null
        } else {
          try {
            bucket.bars.applyOptions({
              lastValueVisible: false,
            })
          } catch { /* noop */ }
        }
        continue
      }

      if (!instanceVisible) {
        // Tear down every component series; keep the bucket entry so we
        // can rebuild quickly when the eye toggles back on.
        const candleSeries = candleSeriesRef.current
        for (const componentKey of Object.keys(bucket)) {
          removeChartComponent(chart, candleSeries, bucket[componentKey])
          delete bucket[componentKey]
          lastSeriesTimeRef.current[ind.presetId][componentKey] = null
        }
        continue
      }

      const paneIndex = resolvePane(paneRegistryRef, DISPLAY_PANE_BY_KIND[ind.kind])

      for (const component of components) {
        const sectionStyle = indicatorSectionStyle(ind, component.key, visualTheme)
        const componentVisible = sectionStyle.visible !== false
        const existing = bucket[component.key]

        if (!componentVisible) {
          if (existing) {
            removeChartComponent(chart, candleSeriesRef.current, existing)
            delete bucket[component.key]
            lastSeriesTimeRef.current[ind.presetId][component.key] = null
          }
          continue
        }

        const fallbackColor =
          DEFAULT_STYLE_BY_KIND[ind.kind]?.[component.key]?.color ?? '#2962FF'
        const baseColor = sectionStyle.color || fallbackColor
        const opacityPct = sectionStyle.opacity ?? 100
        const color = applyOpacityToColor(baseColor, opacityPct)

        if (component.shape === 'fill') {
          if (!existing) {
            const primitive = new BandFillPrimitive()
            try { candleSeriesRef.current?.attachPrimitive?.(primitive) } catch { /* noop */ }
            primitive.update({ points: [], fillStyle: color })
            bucket[component.key] = primitive
            lastSeriesTimeRef.current[ind.presetId][component.key] = null
          } else {
            try { existing.update({ fillStyle: color }) } catch { /* noop */ }
          }
          continue
        }

        const lineWidth = Math.min(4, Math.max(1, sectionStyle.thickness ?? 2))
        const lineStyleEnum = indicatorLineStyle(ind.kind, sectionStyle.line_style)
        if (!existing) {
          const series = chart.addLineSeries({
            color, lineStyle: lineStyleEnum, lineWidth,
            priceLineVisible: false,
            lastValueVisible: ind.style?.values_in_status_line !== false,
            crosshairMarkerVisible: false,
            // CE-04g-UI-chart-smoke-fixes — overlay line must not pollute
            // the price-pane autoscale. Toggling an SMA(390) onto the
            // chart used to compress candles to fit the SMA's range.
            autoscaleInfoProvider: () => null,
          }, paneIndex)
          bucket[component.key] = series
          lastSeriesTimeRef.current[ind.presetId][component.key] = null
        } else {
          try {
            existing.applyOptions({
              color, lineStyle: lineStyleEnum, lineWidth,
              lastValueVisible: ind.style?.values_in_status_line !== false,
            })
          } catch { /* noop */ }
        }
      }
    }

    // Remove buckets for indicators that no longer exist.
    for (const [presetId, bucket] of map.entries()) {
      if (stillPresent.has(presetId)) continue
      const candleSeries = candleSeriesRef.current
      for (const componentKey of Object.keys(bucket)) {
        removeChartComponent(chart, candleSeries, bucket[componentKey])
      }
      map.delete(presetId)
      delete lastSeriesTimeRef.current[presetId]
    }

    // ── Pivots (PivotPrimitive lifecycle — attach/detach only) ──
    // CE-04g-UI-pivots-iseriesprimitive-refactor — one PivotPrimitive per
    // visible pivot preset. Payload updates fire from Effect 2.5; this
    // block is lifecycle-only (no bar reads, no payload computation).
    const candleSeries = candleSeriesRef.current
    if (candleSeries) {
      const primitiveMap = pivotPrimitivesByPresetRef.current
      const pivotsStillPresent = new Set()

      for (const ind of indicators) {
        if (ind.kind !== 'pivots') continue
        pivotsStillPresent.add(ind.presetId)
        const instanceVisible = ind.style?.visible !== false
        const existing = primitiveMap.get(ind.presetId)

        if (!existing && instanceVisible) {
          const primitive = new PivotPrimitive()
          try {
            candleSeries.attachPrimitive(primitive)
            primitiveMap.set(ind.presetId, primitive)
          } catch { /* mock series may not implement attachPrimitive */ }
        } else if (existing && !instanceVisible) {
          try { candleSeries.detachPrimitive(existing) } catch { /* noop */ }
          primitiveMap.delete(ind.presetId)
          // Drop the diagnostic-seen flag so a re-show fires a fresh emit.
          pivotDiagnosticSeenRef.current.delete(ind.presetId)
          // Drop sticky anchor so a re-show starts from a fresh session
          // anchor computation.
          pivotSegmentStartByPresetRef.current.delete(ind.presetId)
        }
      }

      // Detach primitives whose preset was removed entirely from indicators.
      for (const presetId of Array.from(primitiveMap.keys())) {
        if (pivotsStillPresent.has(presetId)) continue
        const primitive = primitiveMap.get(presetId)
        try { candleSeries.detachPrimitive(primitive) } catch { /* noop */ }
        primitiveMap.delete(presetId)
        pivotDiagnosticSeenRef.current.delete(presetId)
        pivotSegmentStartByPresetRef.current.delete(presetId)
      }

      const carsMap = carsPrimitivesByPresetRef.current
      const carsStillPresent = new Set()

      for (const ind of indicators) {
        if (ind.kind !== 'cars_v1') continue
        carsStillPresent.add(ind.presetId)
        const instanceVisible = ind.style?.visible !== false
        const wantVisible = instanceVisible && activeTimeframe === 'daily'
        const existing = carsMap.get(ind.presetId)

        if (!existing && wantVisible) {
          const primitive = new CarsRegimePrimitive()
          try {
            candleSeries.attachPrimitive(primitive)
            carsMap.set(ind.presetId, primitive)
          } catch { /* mock series may not implement attachPrimitive */ }
        } else if (existing && !wantVisible) {
          try { candleSeries.detachPrimitive(existing) } catch { /* noop */ }
          carsMap.delete(ind.presetId)
        }
      }

      for (const presetId of Array.from(carsMap.keys())) {
        if (carsStillPresent.has(presetId)) continue
        const primitive = carsMap.get(presetId)
        try { candleSeries.detachPrimitive(primitive) } catch { /* noop */ }
        carsMap.delete(presetId)
      }
    }

    // CE-04g-UI-chart-smoke-fixes — TV-style Volume pane collapse.
    // After indicator reconcile, if no visible Volume indicator remains,
    // tear down pane 1 entirely (chart becomes price-only). Idempotent —
    // does nothing if 'volume' is no longer registered.
    const anyVisibleVolume = indicators.some(
      (i) => i.kind === 'volume'
        && i.style?.visible !== false
        && (i.style?.bars?.visible !== false)
    )
    if (!anyVisibleVolume && paneRegistryRef.current?.has('volume')) {
      const idx = paneRegistryRef.current.get('volume')
      try { chart.removePane(idx) } catch { /* noop */ }
      paneRegistryRef.current.delete('volume')
    }
    applyPricePaneVisualOrder({
      candleSeries: candleSeriesRef.current,
      seriesByPreset: map,
      indicators,
      visualTheme,
    })
    // Re-apply pane heights — single-pane when collapsed, two-pane when
    // Volume is live. Reads the latest paneRatio + measuredHeight refs
    // so the rehydrated drag position survives across collapse cycles.
    try {
      applyPaneHeights(chart, paneRatioRef.current, measuredHeightRef.current)
    } catch { /* noop */ }
    // CE-04g-UI-chart-smoke-fixes — mirror imperative pane state into
    // React state so the lower-right controls stay in lockstep with the
    // actual LWC pane (not just the indicator-visibility intent). Set
    // unconditionally so a failed pane creation surfaces as
    // `hasVolumePane === false`.
    const paneLive = !!paneRegistryRef.current?.has('volume')
    setHasVolumePane((prev) => (prev === paneLive ? prev : paneLive))
    const volume = indicators.find(ind => ind.kind === 'volume' && ind.style?.visible !== false
      && ind.style?.bars?.visible !== false)
    const volumeSeries = volume && map.get(volume.presetId)?.bars
    if (paneLive && volumeSeries && chartSettings.volumeReadoutVisible !== false) {
      volumeReadoutCleanupRef.current = attachVolumeReadout({
        chart, series: volumeSeries, container: chartContainerRef.current,
        paneIndex: paneRegistryRef.current.get('volume'),
      })
    }
  }, [mode, indicators, chartType, activeTimeframe, chartReadyNonce, visualTheme, hasRenderedLiveCandles, chartSettings.volumeReadoutVisible])

  // Label changes are presentation-only: do not rebuild series, rewrite data,
  // or disturb the viewport and each indicator's saved style.
  useEffect(() => {
    for (const ind of indicators) {
      if (!ind.kind.startsWith('vwap_')) continue
      const series = seriesByPresetRef.current.get(ind.presetId)?.vwap
      series?.applyOptions({
        lastValueVisible: chartSettings.vwapLabelsVisible !== false && ind.style?.values_in_status_line !== false,
      })
    }
  }, [mode, indicators, chartType, activeTimeframe, chartReadyNonce, visualTheme, hasRenderedLiveCandles, chartSettings.vwapLabelsVisible])

  // Effect 2 — full-data reconcile via series.setData(). Same compute
  // branches as before (VWAP-window dispatch from
  // CE-04g-UI-vwap-window). Does NOT create / remove series; only
  // setData on existing visible series. Effect 1 owns lifecycle.
  useEffect(() => {
    if (mode !== 'static' && mode !== 'live') return
    const chart = chartRef.current
    // Indicator mutations can rerun this effect without changing the HTTP
    // snapshot captured by React. In live mode the keyed in-memory tail is
    // canonical; a key mismatch means the next view is still loading, so
    // reconcile empty data instead of replaying the prior view's candles.
    const effectiveBars = mode === 'static'
      ? bars
      : (liveDataViewKeyRef.current === liveCandleViewKey
          ? (liveCandlesRef.current?.length ? liveCandlesRef.current : displayCandlesRef.current)
          : [])
    if (
      !chart
      || !Array.isArray(effectiveBars)
      || (mode === 'static' && effectiveBars.length === 0)
    ) return

    const map = seriesByPresetRef.current
    if (!lastSeriesTimeRef.current) lastSeriesTimeRef.current = {}
    const visibleLogicalRange = mode === 'live' ? readVisibleLogicalRange(chart) : null
    let touchedLiveSeries = false

    for (const ind of indicators) {

      const compute = COMPUTE_BY_KIND[ind.kind]
      const components = COMPONENTS_BY_KIND[ind.kind]
      if (!compute || !components) continue
      if (ind.style?.visible === false) continue
      const bucket = map.get(ind.presetId)
      if (!bucket) continue

      // Per-kind compute dispatch. MUST stay in sync with Effect 3's
      // identical dispatch — keep both branches updated together when a
      // new indicator kind lands.
      let raw
      if (ind.kind === 'keltner') {
        raw = compute(
          effectiveBars,
          ind.params.length,
          ind.params.multiplier,
          ind.params.source,
          ind.params.use_exponential_ma,
          ind.params.bands_style,
          ind.params.atr_length
        )
      } else if (ind.kind === 'bollinger') {
        raw = compute(effectiveBars, ind.params.length, ind.params.std_mult, ind.params.source, ind.params.offset)
      } else if (ind.kind === 'donchian') {
        raw = compute(effectiveBars, ind.params.length, ind.params.offset)
      } else if (ind.kind === 'volume') {
        const volMode = ind.params.color_based_on_previous_close ? 'vs_prev_close' : 'vs_open'
        const barsStyle = indicatorSectionStyle(ind, 'bars', visualTheme)
        const growingColor = barsStyle?.growing?.color
          || DEFAULT_STYLE_BY_KIND.volume.bars.growing.color
        const growingOpacity = barsStyle?.growing?.opacity
          ?? DEFAULT_STYLE_BY_KIND.volume.bars.growing.opacity
        const fallingColor = barsStyle?.falling?.color
          || DEFAULT_STYLE_BY_KIND.volume.bars.falling.color
        const fallingOpacity = barsStyle?.falling?.opacity
          ?? DEFAULT_STYLE_BY_KIND.volume.bars.falling.opacity
        raw = compute(
          effectiveBars,
          volMode,
          applyOpacityToColor(growingColor, growingOpacity),
          applyOpacityToColor(fallingColor, fallingOpacity)
        )
      } else if (ind.kind === 'vwap_1d') {
        // CE-04g-UI-vwap-window: window filter ahead of cumulative VWAP.
        raw = computeWindowVWAP(filterBarsTo1DayWindow(effectiveBars))
      } else if (ind.kind === 'vwap_2d') {
        raw = computeWindowVWAP(filterBarsTo2DayWindow(effectiveBars, tradingCalendar))
      } else if (ind.kind === 'vwap_wtd') {
        raw = computeWindowVWAP(filterBarsToWtdWindow(effectiveBars, tradingCalendar))
      } else if (ind.kind === 'vwap_mtd') {
        raw = computeWindowVWAP(filterBarsToMtdWindow(effectiveBars))
      } else if (ind.kind === 'vwap_ytd') {
        raw = computeWindowVWAP(filterBarsToYtdWindow(effectiveBars))
      } else if (ind.kind === 'vwap_earnings' || ind.kind === 'vwap_earnings_2') {
        raw = computeEarningsVwap(effectiveBars, ind.kind === 'vwap_earnings_2' ? twoEarningsVwapAnchor : earningsVwapAnchor, activeTimeframe).data
      } else {
        raw = compute(effectiveBars, ind.params.length, ind.params.source, ind.params.offset)
      }

      if (!lastSeriesTimeRef.current[ind.presetId]) {
        lastSeriesTimeRef.current[ind.presetId] = {}
      }

      for (const component of components) {
        const sectionStyle = indicatorSectionStyle(ind, component.key, visualTheme)
        if (sectionStyle.visible === false) continue
        const series = bucket[component.key]
        if (!series) continue  // Effect 1 hasn't caught up — next render

        const data = projectComponentData(component, raw)
        try {
          if (mode === 'live') touchedLiveSeries = true
          if (component.shape === 'fill') {
            const sectionStyle = indicatorSectionStyle(ind, component.key, visualTheme)
            const fallbackColor =
              DEFAULT_STYLE_BY_KIND[ind.kind]?.[component.key]?.color ?? '#2962FF'
            const baseColor = sectionStyle.color || fallbackColor
            const opacityPct = sectionStyle.opacity ?? 100
            series.update({ points: data, fillStyle: applyOpacityToColor(baseColor, opacityPct) })
          } else {
            series.setData(data)
          }
          if (Array.isArray(data) && data.length > 0) {
            lastSeriesTimeRef.current[ind.presetId][component.key] = data[data.length - 1].time
          } else {
            // setData([]) fully replaces series data; clear the monotonic
            // baseline so a later non-empty Effect 3 update is not falsely
            // gated by a stale "last rendered" time from the prior data.
            lastSeriesTimeRef.current[ind.presetId][component.key] = null
          }
        } catch { /* swallow — bad shape is caller's concern */ }
      }
    }
    if (touchedLiveSeries) restoreVisibleLogicalRange(chart, visibleLogicalRange)
    // CE-04g-UI-chart-smoke-fixes-hotfix-rehydrate — `chartReadyNonce`
    // bumps after every chart-create reassigns `chartRef.current`. Effect 1
    // (lifecycle) already consumes it to recreate series on the fresh chart
    // instance. Effect 2 must ALSO consume it so the full-data reconcile
    // pass that lands setData on the new series fires immediately after
    // Effect 1, without requiring the user to toggle an indicator. Live-mode
    // The stable live shell can create indicator series one render after
    // the first candle set is applied. `hasRenderedLiveCandles` makes this
    // full-data pass run after those series exist, so historical indicator
    // data is not replaced by only the forming/current bar.
  }, [mode, bars, displayCandles, indicators, chartType, activeTimeframe, tradingCalendar, chartReadyNonce, visualTheme, hasRenderedLiveCandles, liveCandleViewKey, earningsVwapAnchor, twoEarningsVwapAnchor])

  // Effect 2.5 (CE-04g-UI-pivots-iseriesprimitive-refactor) — pivot
  // primitive payload reconcile. Fires whenever bars / displayCandles /
  // indicators / tradingCalendar / dailyPivotBars / timezone /
  // activeTimeframe / chartReadyNonce / chartType / symbol changes. Owns:
  //   - late-bars (Effect 1 attached before bars existed; Effect 2.5 sends
  //     the first finite payload when bars arrive)
  //   - same-session settled-bar drift (replaces Effect 2's deleted pivot
  //     branch)
  //   - ET-session rollover (sessionStartTime recomputes; segmentEndTime
  //     advances to the new next-RTH-open)
  //   - chart rebuild on chartType swap (candle <-> bar) so the freshly-
  //     attached primitive receives its first payload
  //   - style changes (popover edits to color / opacity / thickness /
  //     line_style / font_size / per-component visible / per-component color)
  //   - dedupe changes (axisVisibility recomputes via pickVisibleAxisLabels)
  useEffect(() => {
    if (mode !== 'static' && mode !== 'live') return
    const chart = chartRef.current
    const candleSeries = candleSeriesRef.current
    if (!chart || !candleSeries) return
    const effectiveBars = mode === 'static' ? bars : displayCandles
    if (!Array.isArray(effectiveBars)) return

    const primitiveMap = pivotPrimitivesByPresetRef.current

    for (const ind of indicators) {
      if (ind.kind !== 'pivots') continue
      if (ind.style?.visible === false) continue
      const primitive = primitiveMap.get(ind.presetId)
      if (!primitive) continue  // Effect 1 hasn't attached yet, or detached

      // Per-preset diagnostic gate — fires once per preset attach lifetime.
      let mintedThisPreset = false

      // ── Compute the update payload (mirrors v0.24.14's Effect 1 pivot
      // reconcile logic verbatim — same helpers, same fallback semantics,
      // same field shapes). ──

      let todayEtKey = null
      let latest = {}
      let sessionStartTime = null
      if (effectiveBars.length > 0) {
        const lastBar = effectiveBars[effectiveBars.length - 1]
        todayEtKey =
          (typeof lastBar?.rawEpochMs === 'number'
            && Number.isFinite(lastBar.rawEpochMs))
            ? getEtSessionDateKey(lastBar.rawEpochMs)
            : null
        try {
          // Daily-source override — pivots-popover behavior unchanged.
          const selectedPivotBarSize = ind.params?.bar_size || '1 day'
          const priorDaily = selectedPivotBarSize === '1 day'
            ? pickPriorTradingDayDailyBar(dailyPivotBars, todayEtKey, tradingCalendar)
            : null
          const pivotInputBars = priorDaily
            ? [
                {
                  time: Number.isFinite(lastBar.time) ? lastBar.time - 86400 : 0,
                  open: priorDaily.high,
                  high: priorDaily.high,
                  low: priorDaily.low,
                  close: priorDaily.close,
                  volume: 0,
                },
                lastBar,
              ]
            : effectiveBars
          const pivotInputBarSize = priorDaily ? '1 day' : selectedPivotBarSize
          let levels = computePivotLevels(pivotInputBars, pivotInputBarSize)
          levels = filterPivotsToTodayEtSession(levels, pivotInputBars)
          latest = extractLatestPivotLevels(levels)
        } catch { latest = {} }
        sessionStartTime = findCurrentSessionStartTime(effectiveBars)
      }

      // Sticky session anchor (Diagnosis 5). Only update when finite —
      // transient bad-bar passes preserve the prior anchor.
      if (sessionStartTime != null && Number.isFinite(sessionStartTime)) {
        pivotSegmentStartByPresetRef.current.set(ind.presetId, sessionStartTime)
      }
      const segmentStartTime =
        pivotSegmentStartByPresetRef.current.get(ind.presetId) ?? sessionStartTime

      // Segment endpoint with one-shot console.warn fallback for the
      // degenerate calendar case.
      let segmentEndTime = null
      const latestBarTime = effectiveBars.length > 0
        ? (effectiveBars[effectiveBars.length - 1]?.time ?? null)
        : null
      if (todayEtKey) {
        segmentEndTime = nextRthOpenChartTime(todayEtKey, tradingCalendar, timezone)
        if (segmentEndTime == null) {
          if (!nextRthFallbackWarnedRef.current) {
            nextRthFallbackWarnedRef.current = true
            try {
              console.warn(
                'pivots: nextRthOpenChartTime returned null; falling back to latest bar time'
              )
            } catch { /* noop */ }
          }
          segmentEndTime = latestBarTime
        }
      } else {
        segmentEndTime = latestBarTime
      }

      let currentRthCloseTime = todayEtKey
        ? rthCloseChartTime(todayEtKey, timezone)
        : null
      if (currentRthCloseTime == null) currentRthCloseTime = latestBarTime

      // Dedupe pass — unchanged semantics.
      const lastBarClose = effectiveBars[effectiveBars.length - 1]?.close
      const currentClose = Number.isFinite(lastBarClose) ? lastBarClose : null
      const priceScaleMinMove = resolveAxisMinMove(candleSeries)
      const axisVisibility = pickVisibleAxisLabels(latest, currentClose, priceScaleMinMove)

      // Build the per-level payload, honoring per-component visibility.
      // Missing entries = component hidden via Style tab.
      // visibleLevelCount tracks components configured visible (regardless
      // of finite price this pass); anyFiniteLevel tracks whether any
      // visible component produced a finite price. Used by the Diagnosis 6
      // guard below.
      const levelsPayload = {}
      let visibleLevelCount = 0
      let anyFiniteLevel = false
      for (const dk of PIVOT_DISPLAY_KEYS) {
        const computeKey = PIVOT_KEY_TO_COMPUTE[dk]
        const sectionStyle = indicatorSectionStyle(ind, computeKey, visualTheme)
        const componentVisible = sectionStyle.visible !== false
        if (!componentVisible) continue
        visibleLevelCount += 1
        const price = latest[computeKey]
        if (!Number.isFinite(price)) continue
        const fallbackColor =
          DEFAULT_STYLE_BY_KIND.pivots?.[computeKey]?.color ?? '#FF9800'
        const sectionStyleColor = sectionStyle.color || fallbackColor
        const opacityPct = sectionStyle.opacity ?? 100
        const color = applyOpacityToColor(sectionStyleColor, opacityPct)
        const lineWidth = sectionStyle.thickness ?? 1
        const lineStyleEnum = LINE_STYLE_MAP[sectionStyle.line_style] ?? LineStyle.Solid
        const axisLabelVisible = axisVisibility[computeKey] !== false
        levelsPayload[dk] = {
          price, color, lineStyleEnum, lineWidth, axisLabelVisible,
        }
        anyFiniteLevel = true
      }

      // Diagnosis 6 guard: a transient pass with zero finite levels but
      // some component-visible levels must NOT blank the primitive. Skip
      // the update entirely and let the prior payload survive. An
      // intentionally empty payload (every component hidden,
      // visibleLevelCount === 0) is still allowed through.
      if (!anyFiniteLevel && visibleLevelCount > 0) {
        continue
      }

      const resolvedFontSize = Number.isFinite(ind.style?.font_size)
        ? ind.style.font_size
        : 11

      primitive.update({
        levels: levelsPayload,
        segmentStartTime,
        segmentEndTime,
        currentRthCloseTime,
        effectiveBars,
        fontSize: resolvedFontSize,
        priceScaleMinMove,
      })

      // Diagnostic emit — fires once per preset, on first finite-payload
      // update after attach. Per-preset gate via pivotDiagnosticSeenRef.
      if (anyFiniteLevel && !pivotDiagnosticSeenRef.current.has(ind.presetId)) {
        mintedThisPreset = true
        pivotDiagnosticSeenRef.current.add(ind.presetId)
      }
      if (mintedThisPreset) {
        try {
          const effectiveBarsArr = Array.isArray(effectiveBars) ? effectiveBars : []
          const lastBarDbg = effectiveBarsArr[effectiveBarsArr.length - 1] || null
          const todayEtKeyDbg =
            (lastBarDbg && typeof lastBarDbg.rawEpochMs === 'number'
              && Number.isFinite(lastBarDbg.rawEpochMs))
              ? getEtSessionDateKey(lastBarDbg.rawEpochMs)
              : null
          const priorTradingDayKeyDbg = todayEtKeyDbg
            ? getPreviousEtTradingDayKey(todayEtKeyDbg, tradingCalendar)
            : null
          const selectedPivotBarSizeDbg = ind.params?.bar_size || '1 day'
          const rawDailyBarCandidate = selectedPivotBarSizeDbg === '1 day'
            ? pickPriorTradingDayDailyBarCandidate(
                dailyPivotBars, todayEtKeyDbg, tradingCalendar
              )
            : null
          const normalizedPriorDailyOhlc = selectedPivotBarSizeDbg === '1 day'
            ? pickPriorTradingDayDailyBar(
                dailyPivotBars, todayEtKeyDbg, tradingCalendar
              )
            : null
          const pivotSourcePath =
            selectedPivotBarSizeDbg !== '1 day'
              ? 'intraday-aggregate'
              : (normalizedPriorDailyOhlc ? 'daily-bar' : 'fallback-blocked')

          // Both paths recomputed independent of which one was selected
          // for the mint — surfaces what the other path would have
          // produced.
          let computedPivots_dailyPath = null
          if (normalizedPriorDailyOhlc && lastBarDbg) {
            const dailyInput = [
              {
                time: Number.isFinite(lastBarDbg.time) ? lastBarDbg.time - 86400 : 0,
                open: normalizedPriorDailyOhlc.high,
                high: normalizedPriorDailyOhlc.high,
                low: normalizedPriorDailyOhlc.low,
                close: normalizedPriorDailyOhlc.close,
                volume: 0,
              },
              lastBarDbg,
            ]
            try {
              computedPivots_dailyPath = extractLatestPivotLevels(
                computePivotLevels(dailyInput, '1 day')
              )
            } catch { computedPivots_dailyPath = null }
          }
          let computedPivots_intradayPath = null
          try {
            computedPivots_intradayPath = extractLatestPivotLevels(
              computePivotLevels(effectiveBarsArr, selectedPivotBarSizeDbg)
            )
          } catch { computedPivots_intradayPath = null }

          const impliedPriorOhlc_dailyPath =
            backCalcPriorOhlcFromPivots(computedPivots_dailyPath)
          const impliedPriorOhlc_intradayPath =
            backCalcPriorOhlcFromPivots(computedPivots_intradayPath)

          // Current-session timing — feeds the renderer follow-up slice.
          let currentSessionFirstCandleTime = null
          if (todayEtKeyDbg) {
            for (const b of effectiveBarsArr) {
              const ms = (typeof b?.rawEpochMs === 'number'
                && Number.isFinite(b.rawEpochMs)) ? b.rawEpochMs : null
              const key = ms == null ? null : getEtSessionDateKey(ms)
              if (key === todayEtKeyDbg) {
                currentSessionFirstCandleTime = ms
                break
              }
            }
          }
          const currentLatestCandleTime =
            (lastBarDbg && typeof lastBarDbg.rawEpochMs === 'number'
              && Number.isFinite(lastBarDbg.rawEpochMs))
              ? lastBarDbg.rawEpochMs
              : null

          pivotDebugEmitSeqRef.current += 1
          const snapshot = {
            symbol,
            activeBarSize: selectedPivotBarSizeDbg,
            emitSeq: pivotDebugEmitSeqRef.current,
            todayEtKey: todayEtKeyDbg,
            priorTradingDayKey: priorTradingDayKeyDbg,
            pivotSourcePath,
            dailyBarCount: Array.isArray(dailyPivotBars) ? dailyPivotBars.length : 0,
            rawDailyBarCandidate,
            normalizedPriorDailyOhlc,
            computedPivots_dailyPath,
            computedPivots_intradayPath,
            impliedPriorOhlc_dailyPath,
            impliedPriorOhlc_intradayPath,
            currentSessionFirstCandleTime,
            currentLatestCandleTime,
            targetSegmentStartTime: currentSessionFirstCandleTime,
            targetSegmentEndTime: currentLatestCandleTime,
            tvReferenceForVIAV_20260526: {
              priorSessionOhlc: { high: 50.46, low: 48.27, close: 49.51 },
              expectedPivots: {
                pivot: 49.41, r1: 50.56, s1: 48.37,
                r2: 51.60, s2: 47.22, r3: 52.79, s3: 46.18,
              },
            },
            chartMode: mode,
          }
          emitPivotDebug(snapshot)
        } catch { /* diagnostic must never break the reconcile pass */ }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, bars, displayCandles, indicators, tradingCalendar, dailyPivotBars, timezone, activeTimeframe, chartReadyNonce, chartType, symbol, visualTheme, hasRenderedLiveCandles])

  useEffect(() => {
    if (mode !== 'live') return
    if (!chartBootstrapReady) return
    const carsIndicators = indicators.filter((i) => i.kind === 'cars_v1')
    const carsMap = carsPrimitivesByPresetRef.current
    const clearCars = () => {
      carsRequestSignatureRef.current = null
      for (const ind of carsIndicators) {
        const primitive = carsMap.get(ind.presetId)
        if (primitive) primitive.update({ spans: [], fillStyle: 'rgba(41, 98, 255, 0)' })
      }
    }

    if (!carsIndicators.length) {
      carsRequestSignatureRef.current = null
      return
    }
    if (activeTimeframe !== 'daily') {
      clearCars()
      return
    }
    const visibleCars = carsIndicators.filter((i) => i.style?.visible !== false)
    if (!visibleCars.length) {
      clearCars()
      return
    }
    if (!Array.isArray(displayCandles) || displayCandles.length === 0) {
      clearCars()
      return
    }
    if (carsAdjustedStatus === 'error') {
      clearCars()
      return
    }
    if (
      carsAdjustedStatus !== 'ready'
      || !Array.isArray(carsAdjustedPrimaryBars)
      || !Array.isArray(carsBenchmarkBars)
    ) {
      clearCars()
      return
    }

    const { alignedPrimaryBars, alignedChartBars } =
      alignCarsPrimaryBarsToChartBars(displayCandles, carsAdjustedPrimaryBars)
    const payload = buildCarsIndicatorPayload(alignedPrimaryBars, carsBenchmarkBars)
    if (!payload.bars.length || !payload.benchmark_bars_by_symbol.SPY.length) {
      clearCars()
      return
    }
    const requestSignature = JSON.stringify({
      symbol,
      payload,
      visibleCars: visibleCars.map((ind) => ({
        presetId: ind.presetId,
        background: indicatorSectionStyle(ind, 'background', visualTheme),
      })),
    })
    if (carsRequestSignatureRef.current === requestSignature) return
    carsRequestSignatureRef.current = requestSignature

    const controller = new AbortController()
    let cancelled = false
    let completed = false

    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (cancelled) return
        if (!session?.access_token) throw new Error('CA|RS engine proxy: missing Supabase session')
        const response = await fetch('/api/engine/indicators', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        })
        const result = await response.json().catch(() => null)
        if (cancelled) return
        if (!response.ok) throw new Error(`CA|RS engine proxy ${response.status}`)
        const regime = result?.indicators?.cars_v1_regime
        if (!Array.isArray(regime)) {
          clearCars()
          return
        }
        const spans = buildCarsRegimeSpans(regime, alignedChartBars)
        for (const ind of visibleCars) {
          let primitive = carsMap.get(ind.presetId)
          if (!primitive && candleSeriesRef.current) {
            primitive = new CarsRegimePrimitive()
            try {
              candleSeriesRef.current.attachPrimitive(primitive)
              carsMap.set(ind.presetId, primitive)
            } catch {
              primitive = null
            }
          }
          if (!primitive) continue
          const tint = indicatorSectionStyle(ind, 'background', visualTheme)
          const baseColor = tint.color || DEFAULT_STYLE_BY_KIND.cars_v1.background.color
          const opacity = tint.opacity ?? DEFAULT_STYLE_BY_KIND.cars_v1.background.opacity
          primitive.update({
            spans,
            fillStyle: applyOpacityToColor(baseColor, opacity),
          })
        }
        completed = true
      } catch (err) {
        if (cancelled || err?.name === 'AbortError') return
        carsRequestSignatureRef.current = null
        clearCars()
      }
    })()

    return () => {
      cancelled = true
      if (!completed && carsRequestSignatureRef.current === requestSignature) {
        carsRequestSignatureRef.current = null
      }
      controller.abort()
    }
  }, [
    mode,
    activeTimeframe,
    indicators,
    displayCandles,
    carsAdjustedPrimaryBars,
    carsBenchmarkBars,
    carsAdjustedStatus,
    symbol,
    visualTheme,
    chartReadyNonce,
    chartBootstrapReady,
  ])

  // Effect 3 — live-tick data pump. NEW in CE-04g-UI-indicators-live-tick.
  // Fires only in live mode, only on `livePumpNonce` bumps from the next
  // animation-frame pump. Reads `displayCandlesRef.current` (mirror) so each
  // paint sees the latest settled bars without forcing Effect 3 to re-fire on every settled-bar
  // boundary. Updates the unpainted tail; historical corrections require a
  // full indicator repaint because later values can depend on that bar. Pivots
  // are skipped: their values derive from yesterday's HLC and don't
  // change intrabar.
  useEffect(() => {
    if (mode !== 'live') return
    const chart = chartRef.current
    if (!chart) return
    const display = displayCandlesRef.current
    if (!Array.isArray(display) || display.length === 0) return

    const formingBar = synthesizeFormingBar(barStateRef.current, liveCandlesRef.current)
    if (formingBar == null) return
    const effectiveBars = composeEffectiveBars(display, formingBar)
    const repaintHistory = liveIndicatorHistoryDirtyRef.current
    liveIndicatorHistoryDirtyRef.current = false

    const map = seriesByPresetRef.current
    if (!lastSeriesTimeRef.current) lastSeriesTimeRef.current = {}
    const visibleLogicalRange = readVisibleLogicalRange(chart)
    let touchedLiveSeries = false

    for (const ind of indicators) {
      // CE-04g-UI-pivots-overlay-renderer — pivots use prior-session HLC.
      // The forming bar does not change today's pivot levels; bounded-
      // segment overlays advance their right edge through Effect 2's
      // settled-bar reconcile on each new bar, not on each tick.
      if (ind.kind === 'pivots') continue
      const compute = COMPUTE_BY_KIND[ind.kind]
      const components = COMPONENTS_BY_KIND[ind.kind]
      if (!compute || !components) continue
      if (ind.style?.visible === false) continue
      const bucket = map.get(ind.presetId)
      if (!bucket) continue

      // MUST stay in sync with Effect 2 VWAP-window dispatch above —
      // CE-04g-UI-indicators-live-tick. Calling COMPUTE_BY_KIND directly
      // for vwap_* would bypass the window filter and regress
      // CE-04g-UI-vwap-window.
      let raw
      if (ind.kind === 'keltner') {
        raw = compute(
          effectiveBars,
          ind.params.length,
          ind.params.multiplier,
          ind.params.source,
          ind.params.use_exponential_ma,
          ind.params.bands_style,
          ind.params.atr_length
        )
      } else if (ind.kind === 'bollinger') {
        raw = compute(effectiveBars, ind.params.length, ind.params.std_mult, ind.params.source, ind.params.offset)
      } else if (ind.kind === 'donchian') {
        raw = compute(effectiveBars, ind.params.length, ind.params.offset)
      } else if (ind.kind === 'volume') {
        const volMode = ind.params.color_based_on_previous_close ? 'vs_prev_close' : 'vs_open'
        const barsStyle = indicatorSectionStyle(ind, 'bars', visualTheme)
        const growingColor = barsStyle?.growing?.color
          || DEFAULT_STYLE_BY_KIND.volume.bars.growing.color
        const growingOpacity = barsStyle?.growing?.opacity
          ?? DEFAULT_STYLE_BY_KIND.volume.bars.growing.opacity
        const fallingColor = barsStyle?.falling?.color
          || DEFAULT_STYLE_BY_KIND.volume.bars.falling.color
        const fallingOpacity = barsStyle?.falling?.opacity
          ?? DEFAULT_STYLE_BY_KIND.volume.bars.falling.opacity
        raw = compute(
          effectiveBars,
          volMode,
          applyOpacityToColor(growingColor, growingOpacity),
          applyOpacityToColor(fallingColor, fallingOpacity)
        )
      } else if (ind.kind === 'vwap_1d') {
        raw = computeWindowVWAP(filterBarsTo1DayWindow(effectiveBars))
      } else if (ind.kind === 'vwap_2d') {
        raw = computeWindowVWAP(filterBarsTo2DayWindow(effectiveBars, tradingCalendar))
      } else if (ind.kind === 'vwap_wtd') {
        raw = computeWindowVWAP(filterBarsToWtdWindow(effectiveBars, tradingCalendar))
      } else if (ind.kind === 'vwap_mtd') {
        raw = computeWindowVWAP(filterBarsToMtdWindow(effectiveBars))
      } else if (ind.kind === 'vwap_ytd') {
        raw = computeWindowVWAP(filterBarsToYtdWindow(effectiveBars))
      } else if (ind.kind === 'vwap_earnings' || ind.kind === 'vwap_earnings_2') {
        raw = computeEarningsVwap(effectiveBars, ind.kind === 'vwap_earnings_2' ? twoEarningsVwapAnchor : earningsVwapAnchor, activeTimeframe).data
      } else {
        raw = compute(effectiveBars, ind.params.length, ind.params.source, ind.params.offset)
      }

      if (!lastSeriesTimeRef.current[ind.presetId]) {
        lastSeriesTimeRef.current[ind.presetId] = {}
      }

      for (const component of components) {
        const sectionStyle = indicatorSectionStyle(ind, component.key, visualTheme)
        if (sectionStyle.visible === false) continue
        const series = bucket[component.key]
        if (!series) continue

        const data = projectComponentData(component, raw)
        if (!Array.isArray(data) || data.length === 0) {
          if ((ind.kind === 'vwap_earnings' || ind.kind === 'vwap_earnings_2') && lastSeriesTimeRef.current[ind.presetId]?.[component.key] != null) {
            series.setData([])
            lastSeriesTimeRef.current[ind.presetId][component.key] = null
            touchedLiveSeries = true
          }
          continue
        }
        const lastPoint = data[data.length - 1]
        const lastSeriesTime = lastSeriesTimeRef.current[ind.presetId]?.[component.key]
        // Lightweight Charts contract: series.update(item) requires
        // item.time >= the latest rendered item's time (without the
        // out-of-scope `historicalUpdate=true` mode). Offset-shifted
        // indicators can produce a trailing point with time < last
        // rendered; skip rather than throw. The next Effect 2 full
        // reconcile will resync.
        if (lastSeriesTime != null && lastPoint.time < lastSeriesTime) continue
        try {
          touchedLiveSeries = true
          if (component.shape === 'fill') {
            const sectionStyle = indicatorSectionStyle(ind, component.key, visualTheme)
            const fallbackColor =
              DEFAULT_STYLE_BY_KIND[ind.kind]?.[component.key]?.color ?? '#2962FF'
            const baseColor = sectionStyle.color || fallbackColor
            const opacityPct = sectionStyle.opacity ?? 100
            series.update({ points: data, fillStyle: applyOpacityToColor(baseColor, opacityPct) })
          } else if (repaintHistory) {
            series.setData(data)
          } else if (lastSeriesTime == null) {
            series.update(lastPoint)
          } else {
            // rAF can pause while SSE continues. Repaint the previously
            // forming point, then append EVERY accumulated bucket in order.
            // Updating only lastPoint leaves permanent holes in volume/SMA.
            for (const point of data) {
              if (point.time >= lastSeriesTime) series.update(point)
            }
          }
          lastSeriesTimeRef.current[ind.presetId][component.key] = lastPoint.time
        } catch { /* noop — mirrors existing setData pattern */ }
      }
    }
    if (touchedLiveSeries) restoreVisibleLogicalRange(chart, visibleLogicalRange, { force: true })
  }, [mode, indicators, activeTimeframe, tradingCalendar, livePumpNonce, visualTheme, earningsVwapAnchor, twoEarningsVwapAnchor])

  // Restore after candle/indicator hydration and scale options, including the
  // chart-ready render. Setting a range on the empty replacement chart is not
  // sufficient: native data/layout initialization can change its bar spacing.
  useEffect(() => {
    const pending = styleViewportRef.current
    if (!pending || pending.viewKey !== `${mode}|${liveCandleViewKey}`) return
    if (mode === 'live' && !hasRenderedLiveCandles) return
    if (restoreVisibleLogicalRange(chartRef.current, pending.range, { force: true })
      && chartReadyNonce !== pending.nonce) {
      styleViewportRef.current = null
      releaseVisibleRangePersistenceAfter()
    }
  }, [mode, liveCandleViewKey, chartType, chartReadyNonce, hasRenderedLiveCandles, releaseVisibleRangePersistenceAfter])

  // Find the indicator for the open popover.
  const popoverIndicator = popoverPresetId
    ? indicators.find((i) => i.presetId === popoverPresetId)
    : null

  // CE-04g-UI-chart-smoke-fixes — `hasVolumePane` is React state above,
  // synced from Effect 1's actual pane-lifecycle outcome. Reading it as
  // state (instead of deriving from indicators + paneRegistryRef) avoids
  // a render/effect ordering race where the ref mutation by Effect 1
  // would be invisible to the render that triggered it.

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div
      ref={skeletonRef}
      data-testid="trade-chart-skeleton"
      data-mode={mode}
      data-shared-timeframe={sharedEnabled ? activeTimeframe : undefined}
      data-shared-revision={sharedProfile?.revision}
      data-symbol={symbol}
      data-readonly={String(effectiveReadOnly)}
      data-linked-to-position-id={linkedToPositionId ?? ''}
      data-symbol-search-enabled={String(symbolSearchEnabled)}
      data-cursor-time={cursorTime != null ? String(cursorTime) : ''}
      data-marker-count={String(markers.length)}
      data-execution-annotation-count={String(executionAnnotations.length)}
      data-avwap-anchor-count={String(avwapAnchors.length)}
      data-engine-signal-count={String(engineSignals.length)}
      data-bars-count={mode === 'static' ? (bars != null ? String(bars.length) : '') : String(displayCandles.length)}
      data-has-setup-lines={String(setupLines != null)}
      data-has-position-lines={String(positionLines != null)}
      data-reset-count={String(resetCount)}
      data-on-cursor-change={String(typeof onCursorChange === 'function')}
      data-on-link-change={String(typeof onLinkChange === 'function')}
      data-on-symbol-change={String(typeof onSymbolChange === 'function')}
      data-on-avwap-anchor-save={String(typeof onAvwapAnchorSave === 'function')}
      data-chart-export-enabled={String(chartExportDebugEnabled)}
      data-quick-measure-enabled={String(quickMeasureEnabled)}
      className="relative w-full"
      style={{ height: '100%' }}
    >
      {/* Timeframe selector — live mode only. Suppressed when a parent
          renders its own top bar (CE-04g-UI-chrome-1, composedChrome). */}
      {showLiveChartChrome && !composedChrome && (
        <div className="absolute top-0 right-2 z-20 flex items-center gap-0.5 py-1">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf.key}
              onClick={() => handleTimeframeChange(tf.key)}
              className={`font-mono text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                activeTimeframe === tf.key
                  ? 'bg-canvas2 border border-border text-ink'
                  : 'border border-transparent text-inkMuted hover:text-ink'
              }`}
            >
              {tf.label}
            </button>
          ))}
        </div>
      )}

      {/* Loading state — live mode. */}
      {mode === 'live' && (!chartBootstrapReady || liveStatus === 'loading') && !hasRenderedLiveCandles && (
        <div className="absolute inset-0 flex items-center justify-center z-10" style={{ background: 'rgb(var(--color-canvas))' }}>
          <div className="font-mono text-xs text-inkMuted animate-pulse">Loading chart...</div>
        </div>
      )}
      {liveChartTransitioning && (
        <div className="absolute inset-0 z-20 pointer-events-none flex items-start justify-start bg-canvas/45 backdrop-blur-[1px]">
          <div className="mt-3 ml-3 rounded border border-border bg-canvas/90 px-2 py-1 shadow-sm">
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-inkMuted animate-pulse">Loading {symbol}</div>
          </div>
        </div>
      )}

      {/* Error state — live mode. */}
      {mode === 'live' && liveStatus === 'error' && !hasRenderedLiveCandles && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 gap-2" style={{ background: 'rgb(var(--color-canvas))' }}>
          <div className="font-mono text-xs text-inkMuted">{liveEmptyErrorLabel}</div>
          <button
            onClick={() => retryFetch()}
            className="font-mono text-[10px] text-inkMuted hover:text-ink px-2 py-0.5 rounded border border-border hover:border-inkMuted transition-colors"
          >
            Retry
          </button>
        </div>
      )}
      {mode === 'live' && liveStatus === 'error' && hasRenderedLiveCandles && (
        <div className="absolute top-3 left-3 z-20 rounded border border-border bg-canvas/90 px-2 py-1 shadow-sm">
          <div className="font-mono text-xs text-inkMuted">{liveEmptyErrorLabel}</div>
          <button
            onClick={() => retryFetch()}
            className="font-mono text-[10px] uppercase tracking-[0.14em] text-inkMuted hover:text-ink transition-colors"
          >
            Retry chart
          </button>
        </div>
      )}
      {mode === 'live' && hasRenderedLiveCandles && browserOffline && (
        <div
          className="absolute top-3 z-20 text-red-400"
          style={{ right: (nativePaneLayout?.axisWidth ?? CHART_RIGHT_PRICE_SCALE_MIN_WIDTH) + 8 }}
          role="status"
          aria-label="Live data unavailable"
          title="Live data unavailable"
        >
          <WifiOff size={13} strokeWidth={1.75} aria-hidden="true" />
        </div>
      )}
      {mode === 'live' && hasRenderedLiveCandles && !browserOffline && ['degraded', 'disconnected'].includes(runtimeFanoutStatus) && (
        <div className="absolute top-3 right-3 z-20 rounded border border-border bg-canvas/90 px-2 py-1 shadow-sm">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-inkMuted">
            {runtimeFanoutStatus === 'degraded' ? 'Live data degraded' : 'Live data disconnected'}
          </div>
        </div>
      )}

      {/* Size directly from layout: measuredHeight state arrives one frame later
          and would otherwise paint the new width with the previous height. */}
      <div
        data-testid="trade-chart-canvas-wrapper"
        className="relative w-full"
        style={{ height: '100%' }}
        onMouseMove={handleExecutionMarkerMouseMove}
        onMouseLeave={handleExecutionMarkerMouseLeave}
        onClick={handleOverlayAxisClick}
      >
        <div
          data-testid="trade-chart-static-canvas"
          ref={chartContainerRef}
          style={{ height: '100%', cursor: crosshairEnabled && chartSettings.crosshairVisible !== false ? 'crosshair' : undefined }}
          className="w-full"
        />
        <FutureTimeLabel candlesRef={mode === 'static' ? staticVwapCandlesRef : displayCandlesRef} chart={chartRef.current} series={mode !== 'live' || liveChartDataCurrent ? candleSeriesRef.current : null} container={chartContainerRef.current} timeframe={activeTimeframe} enabled={crosshairEnabled && chartSettings.crosshairVisible !== false && chartSettings.crosshairVerticalVisible !== false} />
        {onDrawingToolChange && preferenceUserId && <ChartDrawings key={JSON.stringify([preferenceUserId,symbol,marketSession])}
          ref={drawingsRef} drawingView="price" userId={preferenceUserId} storageKey={JSON.stringify([symbol,marketSession])}
          timeframe={activeTimeframe} theme={visualTheme} api={drawingApi} tool={drawingTool} active={keyboardActive}
          keyboardActive={keyboardActive} onActivate={() => {}} onToolChange={onDrawingToolChange} />}
        {mode === 'live' && !compact && !isOption && assetType === 'equity' && chartGeometrySettings.earningsVisible !== false && (
          <EarningsLayer key={`earnings:${symbol}`} symbol={symbol} chart={chartRef.current}
            series={liveChartDataCurrent ? candleSeriesRef.current : null}
            timeframe={activeTimeframe} timezone={timezone} tradingCalendar={tradingCalendar}
            paneHeight={nativePaneLayout?.height ?? (hasVolumePane ? pane0Height : measuredHeight)} />
        )}
        {mode === 'live' && assetType === 'equity' && <PriceAlertLines chart={chartRef.current} container={chartContainerRef.current} settings={chartSettings} series={liveChartDataCurrent ? candleSeriesRef.current : null} symbol={symbol} visible={priceAlertsVisible} />}
        {quickMeasureEnabled && (
          <div
            className="sr-only"
            role="status"
            aria-live="polite"
            data-testid="trade-chart-quick-measure-status"
          >
            {quickMeasureSnapshot
              ? `${quickMeasureSnapshot.stats.headline}; ${quickMeasureSnapshot.stats.detail}`
              : 'Hold Shift and click two chart points to measure price and time'}
          </div>
        )}
        {!privacyMode && hoveredExecutionAnnotation && (
          <div
            data-testid="trade-chart-execution-annotation-layer"
            className="absolute inset-0 pointer-events-none"
            style={{ zIndex: 8, overflow: 'visible' }}
          >
            {(() => {
              const ann = hoveredExecutionAnnotation
              const cluster = ann._cluster || [ann]
              const primary = ann.marker
              const sideLabel = markerAnnotationSideLabel(primary)
              const consolidatedCount = markerConsolidatedCount(primary)
              const isConsolidated = consolidatedCount > 1
              const children = displayMarkerChildren(primary)
              const totalQty = markerTotalQty(primary)
              const tooltipX = Math.min(
                ann.iconX + 14,
                (chartContainerRef.current?.clientWidth || 900) - 290
              )
              const tooltipY = Math.max(ann.iconY - 12, 8)
              const badgeClass = primary.side === 'buy'
                ? 'text-[#4aaa6a] border-[#4aaa6a]/40 bg-[#4aaa6a]/10'
                : 'text-[#d45454] border-[#d45454]/40 bg-[#d45454]/10'
              const markerTimeLabel = formatMarkerTimeLabel(primary, timezone)
              const accountNumber = markerAccountText(primary)

              return (
                <div
                  data-testid="trade-chart-execution-tooltip"
                  className="absolute pointer-events-none font-mono animate-execution-tooltip-in"
                  style={{ left: tooltipX, top: tooltipY, zIndex: 20 }}
                >
                  <div className="bg-canvas2 border border-border rounded-lg px-3 py-2 shadow-lg backdrop-blur-sm min-w-[220px] max-w-[290px]">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold border ${badgeClass}`}>
                        {sideLabel}
                      </span>
                      {(isConsolidated || cluster.length > 1) && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-canvas border border-border text-inkMuted">
                          {'\u00d7'}{cluster.length > 1 ? cluster.length : consolidatedCount}
                        </span>
                      )}
                      <span className="text-[9px] text-inkMuted truncate">{markerTimeLabel}</span>
                      {(isConsolidated || cluster.length > 1) && (
                        <span className="text-[9px] text-inkMuted ml-auto tabular-nums">
                          <PrivateValue>{Math.round(Number(cluster.length > 1
                            ? cluster.reduce((sum, item) => sum + markerTotalQty(item.marker), 0)
                            : totalQty
                          ))} shares</PrivateValue>
                        </span>
                      )}
                    </div>
                    {cluster.length > 1 ? (
                      <div className="space-y-1.5 mt-1">
                        {cluster.map((item, itemIndex) => {
                          const itemMarker = item.marker
                          const itemSideLabel = markerAnnotationSideLabel(itemMarker)
                          const itemBadgeClass = itemMarker.side === 'buy'
                            ? 'text-[#4aaa6a] border-[#4aaa6a]/40 bg-[#4aaa6a]/10'
                            : 'text-[#d45454] border-[#d45454]/40 bg-[#d45454]/10'
                          const itemChildren = displayMarkerChildren(itemMarker)
                          return (
                            <div key={itemMarker.id || itemIndex} className={itemIndex > 0 ? 'pt-1.5 border-t border-border/30' : ''}>
                              <div className="flex items-center gap-1.5">
                                <span className={`text-[8px] px-1 py-0.5 rounded font-semibold border ${itemBadgeClass}`}>
                                  {itemSideLabel}
                                </span>
                                <span className="text-[9px] text-ink">
                                  <PrivateValue>{Math.round(markerTotalQty(itemMarker))} shares</PrivateValue>
                                </span>
                              </div>
                              <div className="mt-0.5 pl-1 space-y-0.5">
                                {itemChildren.map((child, childIndex) => (
                                  <div key={`${itemMarker.id || itemIndex}:${childIndex}`} className="flex items-center justify-between gap-3">
                                    <AccountName accountNumber={String(child.account_number || '')} className="text-[8px] text-inkMuted" />
                                    <span className="text-[8px] text-inkMuted tabular-nums">
                                      <PrivateValue>{Math.round(Number(child.quantity) || 0)} @ {formatMarkerPrice(child.price)}</PrivateValue>
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <>
                        <div className="text-[10px] text-ink mb-1">
                          <PrivateValue>{markerAggregateText(primary)}</PrivateValue>
                        </div>
                        {isConsolidated && (
                          <div className="mt-1.5 pt-1.5 border-t border-border/50 space-y-0.5">
                            {children.map((child, childIndex) => (
                              <div key={childIndex} className="flex items-center justify-between gap-3">
                                <AccountName accountNumber={String(child.account_number || '')} className="text-[9px] text-inkMuted" />
                                <span className="text-[9px] text-inkMuted tabular-nums">
                                  <PrivateValue>{Math.round(Number(child.quantity) || 0)} @ {formatMarkerPrice(child.price)}</PrivateValue>
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {!isConsolidated && accountNumber && (
                          <div className="flex items-center justify-between gap-3 mt-0.5">
                            <AccountName accountNumber={String(accountNumber)} className="text-[9px] text-inkMuted" />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )
            })()}

            <style>{`
              @keyframes executionTooltipFadeIn {
                from { opacity: 0; transform: translateY(3px) scale(0.985); }
                to { opacity: 1; transform: translateY(0) scale(1); }
              }
              .animate-execution-tooltip-in {
                animation: executionTooltipFadeIn 160ms cubic-bezier(0.16, 1, 0.3, 1) both;
              }
            `}</style>

          </div>
        )}
        {retainedVwapEnabled && <AnchoredVwapLayer
          candleSeries={mode !== 'live' || liveChartDataCurrent ? candleSeriesRef.current : null}
          drawingTool={drawingTool} drawingActive={keyboardActive} onDrawingToolChange={onDrawingToolChange}
          key={symbol}
          sharedAppearances={sharedAnchorAppearances}
          onSharedAppearanceChange={sharedEnabled ? (id, patch, options) => shared.store.editAnchoredVwap(activeTimeframe, id, patch, options) : undefined}
          currentPrice={sessionPriceAuthoritative ? sessionPrice : (sessionPrice ?? undefined)}
          alertIconPosition={chartSettings.avwapAlertIconPosition}
          labelsVisible={chartSettings.vwapLabelsVisible !== false}
          toolbarTarget={retainedVwapToolbarTarget}
          chart={chartRef.current}
          container={chartContainerRef.current}
          candlesRef={mode === 'static' ? staticVwapCandlesRef : displayCandlesRef}
          timeframe={activeTimeframe}
          timezone={timezone}
          symbol={symbol}
          pricePaneHeight={hasVolumePane ? pane0Height : measuredHeight}
          supported={!isOption && assetType === 'equity'}
        />}
        {/* CE-04g-UI-chrome-2 — IndicatorAccordion floats inside the canvas
            wrapper at top-left (TradingView indicator/settings pill). It
            does not consume layout space; pointer events outside its
            bounding box pass through to the LWC canvas. */}
        {!compact && chartSettings.indicatorDropdownVisible !== false && (mode === 'static' || showLiveChartChrome) && (
          <IndicatorAccordion
            indicators={indicators}
            activeTimeframe={activeTimeframe}
            indicatorTemplates={indicatorTemplates}
            onAdd={handleAdd}
            onAddTemplate={handleAddTemplate}
            onToggleVisible={handleToggleVisible}
            onOpenPopover={handleOpenPopover}
            onRemove={handleRemove}
            onReorder={handleReorder}
            onSetAllVisible={setAllIndicatorsVisible}
            onSaveTemplate={handleSaveTemplate}
          />
        )}
        {/* CE-05 pane cleanup — the visible price/volume separator is LWC's
            native pane divider. Keeping it native makes volume bars, pane
            height, and lower-right controls agree on the same boundary. */}
        {/* The native pane boundary and axis width own this anchor on every viewport. */}
        {!compact && chartSettings.scaleControlsVisible !== false && (showLiveChartChrome || mode === 'static') && (
          <>
          <div
            data-testid="trade-chart-scale-controls"
            className="absolute [right:var(--scale-controls-right)] [top:var(--scale-controls-top)] -translate-y-full z-20 lg:z-10 flex items-center gap-1 lg:gap-2 rounded-lg lg:rounded-none border lg:border-0 border-border bg-canvas/90 lg:bg-transparent p-1 lg:p-0 shadow-sm lg:shadow-none backdrop-blur-sm lg:backdrop-blur-0"
            style={{
              '--scale-controls-top': `${(nativePaneLayout?.height ?? (hasVolumePane ? pane0Height : measuredHeight)) - 6}px`,
              '--scale-controls-right': `${(nativePaneLayout?.axisWidth ?? rightPriceScaleMinimumWidth) + 6}px`,
            }}
          >
            {/* Mobile uses a compact lower-right pill cluster; lg+ keeps the
                original price-pane anchor and 20px desktop footprint. */}
            <button
              onClick={toggleAutoScale}
              title="Auto scale"
              data-testid="trade-chart-auto-scale-button"
              className={`font-mono text-[10px] h-9 min-w-9 px-2 lg:w-5 lg:h-5 lg:min-w-0 lg:px-0 flex items-center justify-center rounded transition-colors ${
                autoScale ? 'bg-canvas2 border border-border text-ink' : 'border border-transparent text-inkMuted hover:text-ink'
              }`}
            >A</button>
            <button
              onClick={toggleLogScale}
              title="Log scale"
              data-testid="trade-chart-log-scale-button"
              className={`font-mono text-[10px] h-9 min-w-9 px-2 lg:w-5 lg:h-5 lg:min-w-0 lg:px-0 flex items-center justify-center rounded transition-colors ${
                logScale ? 'bg-canvas2 border border-border text-ink' : 'border border-transparent text-inkMuted hover:text-ink'
              }`}
            >L</button>
            <button
              onClick={resetView}
              data-testid="trade-chart-reset-view-button"
              title="Reset view (Option+R)"
              className="h-9 min-w-9 px-2 lg:w-5 lg:h-5 lg:min-w-0 lg:px-0 flex items-center justify-center rounded bg-canvas2 border border-border text-ink hover:text-ink hover:bg-canvas2 transition-colors"
            >
              <RotateCcw size={13} strokeWidth={1.9} />
            </button>
          </div>
          </>
        )}
      </div>

      {/* Popover — both modes. */}
      {popoverIndicator && (
        <IndicatorPopover
          kind={popoverIndicator.kind}
          params={popoverIndicator.params}
          style={popoverIndicator.style}
          theme={visualTheme}
          onChange={handlePopoverChange}
          onRemove={handlePopoverRemove}
          onClose={handlePopoverClose}
        />
      )}
      <ChartSettingsModal
        open={settingsOpen}
        chartType={chartType}
        activeTab={settingsActiveTab}
        onActiveTabChange={setSettingsActiveTab}
        settings={chartSettings}
        onSettingsChange={updateChartSettings}
        crosshairEnabled={crosshairEnabled}
        quickMeasureEnabled={quickMeasureEnabled}
        retainedVwapEnabled={retainedVwapEnabled}
        autoScale={autoScale}
        logScale={logScale}
        onAutoScaleChange={applyAutoScale}
        onLogScaleChange={applyLogScale}
        earningsVisible={chartGeometrySettings.earningsVisible !== false}
        onEarningsVisibleChange={earningsVisible => updateChartBehavior({ earningsVisible })}
        executionMarkersVisible={chartGeometrySettings.executionMarkersVisible}
        onExecutionMarkersVisibleChange={executionMarkersVisible => updateChartBehavior({ executionMarkersVisible })}
        markerRange={markerRange}
        onMarkerRangeChange={(markerRange) => updateChartBehavior({ markerRange })}
        activeTimeframe={activeTimeframe}
        activeTimeframeLabel={CHART_MARGIN_TIMEFRAME_LABELS[activeTimeframe] || activeTimeframe}
        sessionOpenViewEnabled={mode === 'live' && !compact}
        margins={activeChartMargins}
        onMarginsChange={updateChartMargins}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  )
}

// PropTypes — dev-mode validation floor. Names match contract §2 verbatim.

const ChartMarkerShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  time: PropTypes.number.isRequired,
  bar_time_ref: PropTypes.number,
  type: PropTypes.oneOf([
    'entry', 'stop', 'target', 'tranche-stop',
    'execution', 'partial-fill', 'stop-hit', 'target-hit',
    'engine-signal', 'alert', 'note',
    'regime_change_bullish', 'regime_change_bearish',
    'actionable_swing',
  ]).isRequired,
  state: PropTypes.oneOf(['rest', 'hover', 'active']).isRequired,
  intensity: PropTypes.number,
  side: PropTypes.oneOf(['buy', 'sell', 'neutral']),
  iconKey: PropTypes.oneOf(['arrow-up', 'arrow-down', 'shield', 'message', 'flag']),
  label: PropTypes.string,
  tooltip: PropTypes.shape({
    headline: PropTypes.string.isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({
      left: PropTypes.string.isRequired,
      right: PropTypes.string,
    })).isRequired,
  }),
  onClick: PropTypes.func,
  onHover: PropTypes.func,
  metadata: PropTypes.shape({
    tranche_id: PropTypes.string,
    account: PropTypes.string,
    qty: PropTypes.number,
    engine_strategy_id: PropTypes.string,
    confluence_score: PropTypes.number,
    action: PropTypes.string,
    swing_label: PropTypes.oneOf(['SH', 'SL', 'HH', 'HL', 'LH', 'LL']),
    available_actions: PropTypes.arrayOf(
      PropTypes.oneOf(['anchor_avwap', 'set_stop_below', 'set_stop_above'])
    ),
    original_bar_index: PropTypes.number,
  }),
})

const AvwapAnchorShape = PropTypes.shape({
  id: PropTypes.string,
  source: PropTypes.oneOf(['user_pinned', 'engine_emitted']).isRequired,
  barIndex: PropTypes.number.isRequired,
  anchorTime: PropTypes.number.isRequired,
  anchorPrice: PropTypes.oneOf(['high', 'low', 'open', 'close']).isRequired,
  rejectionCount: PropTypes.number,
  rejectionBarIndices: PropTypes.arrayOf(PropTypes.number),
  lastRejectionBarIndex: PropTypes.number,
  name: PropTypes.string,
  notes: PropTypes.string,
})

const SetupLinesShape = PropTypes.shape({
  entry: PropTypes.shape({ price: PropTypes.number.isRequired, visible: PropTypes.bool.isRequired }),
  stop: PropTypes.shape({ price: PropTypes.number.isRequired, visible: PropTypes.bool.isRequired }),
  target: PropTypes.shape({ price: PropTypes.number.isRequired, visible: PropTypes.bool.isRequired }),
  trancheStops: PropTypes.arrayOf(PropTypes.shape({
    trancheId: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    label: PropTypes.string,
  })),
  rrBoxOnHover: PropTypes.bool,
})

const PositionLinesShape = PropTypes.shape({
  avgCost: PropTypes.shape({ price: PropTypes.number.isRequired }),
})

const EngineSignalShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  strategyId: PropTypes.string.isRequired,
  barTime: PropTypes.number.isRequired,
  price: PropTypes.number.isRequired,
  confluenceScore: PropTypes.number.isRequired,
  ruleEvaluations: PropTypes.arrayOf(PropTypes.shape({
    ruleId: PropTypes.string.isRequired,
    passed: PropTypes.bool.isRequired,
    contribution: PropTypes.number.isRequired,
  })).isRequired,
})

// Conditional-required `bars` validator — frozen warning substring.
function barsRequiredInStaticMode(props, propName, componentName) {
  if (props.mode === 'static' && (props[propName] == null || !Array.isArray(props[propName]))) {
    return new Error(
      `Invalid prop \`${propName}\` supplied to \`${componentName}\`: ` +
      `bars is required when mode="static".`
    )
  }
  if (props[propName] != null && !Array.isArray(props[propName])) {
    return new Error(
      `Invalid prop \`${propName}\` supplied to \`${componentName}\`: ` +
      `expected array of Candle, got ${typeof props[propName]}.`
    )
  }
  return null
}

const TRADE_CHART_PROP_TYPES = {
  symbol: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(['live', 'thumbnail', 'static']).isRequired,
  markers: PropTypes.arrayOf(ChartMarkerShape),
  avwapAnchors: PropTypes.arrayOf(AvwapAnchorShape),
  setupLines: SetupLinesShape,
  engineSignals: PropTypes.arrayOf(EngineSignalShape),
  positionLines: PositionLinesShape,
  bars: barsRequiredInStaticMode,
  cursorTime: PropTypes.number,
  onCursorChange: PropTypes.func,
  readOnly: PropTypes.bool,
  linkedToPositionId: PropTypes.string,
  onLinkChange: PropTypes.func,
  onSymbolChange: PropTypes.func,
  symbolSearchEnabled: PropTypes.bool,
  onAvwapAnchorSave: PropTypes.func,
  // CE-04g-UI-a — live-mode props.
  timeframe: PropTypes.oneOf(['1min', '2min', '5min', '15min', '30min', '65min', 'daily', 'weekly']),
  timezone: PropTypes.string,
  entryDate: PropTypes.string,
  assetType: PropTypes.oneOf(['equity', 'option']),
  onTimeframeChange: PropTypes.func,
  // CE-04g-UI-chrome-1 — top-bar composition surface.
  chartType: PropTypes.oneOf(['candlestick', 'bar', 'line']),
  marketSession: PropTypes.oneOf(['regular', 'all']),
  theme: PropTypes.oneOf(['workspace']),
  composedChrome: PropTypes.bool,
  compact: PropTypes.bool,
  backgroundRegimeSpans: PropTypes.array,
  breadthIndicator: PropTypes.shape({ data: PropTypes.array.isRequired, regimes: PropTypes.array.isRequired, current: PropTypes.bool }),
  onVisibleTimeRangeChange: PropTypes.func,
  markerRangeDefault: PropTypes.oneOf(['30d', '6m', '1y', 'all']),
  storageScope: PropTypes.string,
  fallbackStorageScope: PropTypes.string,
  keyboardActive: PropTypes.bool,
  drawingTool: PropTypes.string,
  onDrawingToolChange: PropTypes.func,
  crosshairEnabled: PropTypes.bool,
  onCrosshairMove: PropTypes.func,
  onChartReady: PropTypes.func,
  onPriceAlertCreate: PropTypes.func,
  priceAlertsVisible: PropTypes.bool,
  candleAuthority: PropTypes.oneOf(['default', 'ibkr-runtime']),
  sessionPrice: PropTypes.number,
  sessionPriceAuthoritative: PropTypes.bool,
  sessionPriceSession: PropTypes.oneOf(['overnight', 'pre', 'post', 'regular', 'closed']),
  quickMeasureEnabled: PropTypes.bool,
  retainedVwapEnabled: PropTypes.bool,
  retainedVwapToolbarTarget: PropTypes.object,
}

const TradeChartForward = forwardRef(TradeChart)
TradeChartForward.propTypes = TRADE_CHART_PROP_TYPES
TradeChartForward.displayName = 'TradeChart'

export default TradeChartForward
